/**
 * Theme-native Expivi glue — SDK (V2) edition.
 *
 * Same job as expivi-theme/assets/expivi-viewer.js (V1): render the configurator
 * on a product page, price it for the acting B2B company, and add it to the cart
 * with the `_unit_price` line property that the expivi-dynamic-price Cart
 * Transform applies, plus `_unit_discount` — the wholesale reduction the
 * expivi-wholesale-discount function subtracts as a real Shopify discount so it
 * is visible on the cart, checkout and order details rather than baked into a
 * silently cheaper price. No app backend on the read path.
 *
 * No configured-render thumbnail here, deliberately: V1 uploads Expivi's
 * saveConfiguration() thumbnail to /apps/hub/renders and puts the returned url on
 * the cart line as `_preview_url`, but the V2 SDK returns no thumbnail at all, so
 * there is nothing to upload and nothing to mirror. Cart lines on this theme show
 * the variant photo.
 *
 * What changed vs V1, and why the file is SHORTER despite doing more:
 *
 *   - The legacy `ExpiviComponent({...})` viewer is gone. We boot
 *     `ProductConfigurator` from @expivi/sdk and hold the instance ourselves.
 *   - Prices no longer come from scraping Expivi's own DOM with a
 *     MutationObserver. `usePriceBreakdown()` is a real subscription that yields
 *     `{total, currency, groups}` with itemised amounts — so the whole
 *     read-the-price-text-and-guard-against-our-own-writes dance is deleted.
 *   - Add-on group amounts are read off those price items instead of being
 *     summed from `attribute_values[].price` in a catalogue bootstrap. There is
 *     no bootstrap in V2, so parseAttributeValuePrices/parseMaterialSkus/
 *     configuredSkuKeys/computeGroupPrices are all gone.
 *   - The Master SKU comes from `useBomBreakdown()` — the server computes it, we
 *     no longer assemble it from short codes.
 *   - Colour gating is a server-side value-category filter
 *     (`setCategoryFilter`), not a CSS rule hiding swatch images.
 *   - Rush hiding is a skipped attribute in our own render loop, not a
 *     MutationObserver fighting Vuetify's inline styles.
 *
 * We render the option UI ourselves (see renderOptions) rather than mounting
 * Expivi's composer. The reason is WEIGHT, not impossibility — two earlier
 * notes here were wrong and both have been measured since:
 *
 *   - "an 8 MB stylesheet": wrong by ~30x. It is one 278 KB sheet
 *     (@expivi/sdk/theme/style.css), unchanged between 1.5.0 and 1.6.0.
 *   - "Shopify's flat assets/ cannot serve the hashed chunks": wrong. Stencil
 *     resolves chunks as plain relative ES imports, and a flat assets/ is
 *     exactly what that needs. Verified on this store: the 1.5.0 ui-kit's 66
 *     files upload as-is, ui-kit.esm.js boots, and xpv-button/-list/-tiles/
 *     -dropdown-selector/-toggle-buttons register and hydrate.
 *
 * What it actually costs: ~5.7 MB of Stencil JS across four bundles (ui-kit,
 * ui-composer, form-renderer, offer-inquiry) plus that 278 KB sheet, which
 * stops being optional the moment you use the components — they render into
 * LIGHT DOM, so without it they are unstyled. See docs/expivi-v2-migration.md.
 *
 * Our loop covers the four Configurable* attribute kinds this catalogue uses.
 * ponytail: Web2Print* and BundleProduct attributes are skipped with a console
 * warning — add them here (or switch to the composer, see README) if a product
 * ever needs print personalisation.
 *
 * The wholesale price maths below is UNCHANGED from V1 and is mirrored by
 * VerifyOrderPricing server-side. The two must agree or orders are held for
 * price review, so those four functions are copied verbatim, not rewritten.
 *
 * Verified 2026-08-11 against the live V2 API (https://expivi.net/api/v2) via
 * dev-harness.html: product offer 01kt6pegg3t1k9knwtprevhqpq renders 23
 * attributes, prices, and produces a cart payload. Not yet run inside a Shopify
 * theme. See docs/expivi-v2-migration.md for what the catalogue still lacks —
 * notably prices on 20 of 23 attributes.
 */
(function () {
    'use strict';

    /**
     * Store logic lives in expivi-hoodsly.js, not here: pricing, colour gating and
     * the cart render are the merchant's rules, while this file is written against
     * one @expivi/sdk and gets rewritten when that moves. The snippet renders that
     * script first (both are `defer`, so order holds); node requires it directly.
     *
     * Hard failure on purpose. Without it every price would silently compute as
     * `undefined` and the add to cart would post a line with no _unit_price - a
     * retail charge on a wholesale order, which is worse than not loading.
     */
    var H = (typeof window !== 'undefined' && window.ExpiviHoodsly)
        || (typeof require === 'function' ? require('./expivi-hoodsly.js') : null);
    if (!H) {
        throw new Error('[expivi] expivi-hoodsly.js must load before expivi-viewer.js');
    }
    var discountedUnitPrice = H.discountedUnitPrice;
    var discountedWithAddons = H.discountedWithAddons;
    var unitPriceProperties = H.unitPriceProperties;
    var cappedWholesalePercent = H.cappedWholesalePercent;
    var excludedSum = H.excludedSum;
    var breakdownAmounts = H.breakdownAmounts;
    var rawPriceItems = H.rawPriceItems;
    var valuePrice = H.valuePrice;
    var fetchWholesaleDiscount = H.fetchWholesaleDiscount;
    var allowedColorIdentifiers = H.allowedColorIdentifiers;
    var correctedSelection = H.correctedSelection;
    var staleSnapshotValueCid = H.staleSnapshotValueCid;
    var shouldApplySnapshot = H.shouldApplySnapshot;
    var isAllowedColor = H.isAllowedColor;
    var canonicalScreenshot = H.canonicalScreenshot;
    var uploadPreview = H.uploadPreview;
    var attachPreview = H.attachPreview;
    var settleWithin = H.settleWithin;
    var actingCompanyId = H.actingCompanyId;
    // Applies the admin's ignored Expivi ids (data-ignored-sku-codes, from the
    // hub.ignored_sku_codes shop metafield) to every SKU-segment collection.
    var configuredSkuCodes = function (attributes) {
        var ignored = [];
        try {
            var el = document.querySelector('[data-ignored-sku-codes]');
            ignored = JSON.parse((el && el.getAttribute('data-ignored-sku-codes')) || '[]');
        } catch (e) {
            // Bad/missing list: nothing extra is ignored.
        }
        // Each segment's `price` is what the shopper sees beside that option.
        return H.configuredSkuCodes(attributes, ignored, optionPrice);
    };
    var buildDebugSku = H.buildDebugSku;

    // ------------------------------------------------------------------
    // Pure price maths — VERBATIM from V1. Mirrored by VerifyOrderPricing;
    // do not "improve" one side without the other.
    // ------------------------------------------------------------------

    // ------------------------------------------------------------------
    // V2 replacements for the V1 bootstrap parsers
    // ------------------------------------------------------------------

    /**
     * Discount-excluded add-on amounts keyed by group label: {label -> amount},
     * summed from the SDK price breakdown instead of from catalogue value prices.
     *
     * `breakdown.groups` is keyed by priceableType (`base_price` | `attributes` |
     * `script_engine` | `bom`) and each group holds items with `{identifier,
     * name, amount}`. An item belongs to the FIRST merchant group whose
     * `attributes` list names it — matched on the item's `name` OR `identifier`,
     * case-insensitive — so it is never double-counted, and groups come back in
     * merchant order with zero amounts dropped.
     *
     * `base_price` is skipped: that item IS the hood, never an add-on.
     *
     * Behaviour change from V1 worth knowing: V1 also matched a value's PARENT
     * step name, so a group listing `['Ventilation']` caught every child. V2
     * catalogues have no `step` parents (see docs/expivi-v2-migration.md), and a
     * price item names only the attribute itself — so each group must enumerate
     * the child attribute names. The layout tree (`useStoreLayouts`) is the new
     * home of that hierarchy; wire it in here if merchants want group-by-step back.
     *
     * This is strictly better-behaved than the V1 sum: the amounts come from the
     * same response as the total, so a rule-priced (non-additive) add-on can no
     * longer make the client sum and the server total diverge.
     */
    function groupPricesFromBreakdown(breakdown, groups) {
        if (!breakdown || !groups || !groups.length) {
            return {};
        }
        var groupSets = groups.map(function (g) {
            var set = {};
            (g.attributes || []).forEach(function (n) {
                var key = String(n || '').trim().toLowerCase();
                if (key) {
                    set[key] = true;
                }
            });
            return { label: String(g.label || ''), set: set };
        });

        var totals = {};
        var priceGroups = breakdown.groups || {};
        var typed = [];
        Object.keys(priceGroups).forEach(function (type) {
            ((priceGroups[type] && priceGroups[type].items) || []).forEach(function (item) {
                typed.push({ type: type, item: item });
            });
        });
        // Empty means the SDK dropped every item on a priceable_type it does not
        // know (see rawPriceItems in expivi-hoodsly.js). The response still has
        // them, and without them every add-on rides inside the discount.
        if (!typed.length) {
            typed = rawPriceItems(breakdown.productCid).map(function (item) {
                return { type: String((item && item.priceable_type) || ''), item: item };
            });
        }

        typed.forEach(function (entry) {
            if (entry.type === 'base_price') {
                return;
            }
            var item = entry.item;
            var name = String((item && item.name) || '').trim().toLowerCase();
            var identifier = String((item && item.identifier) || '').trim().toLowerCase();
            var matched = null;
            for (var i = 0; i < groupSets.length; i++) {
                if ((name && groupSets[i].set[name]) || (identifier && groupSets[i].set[identifier])) {
                    matched = groupSets[i].label; // first-match-wins
                    break;
                }
            }
            if (matched === null) {
                return;
            }
            var amount = Number(item && item.amount);
            if (isFinite(amount)) {
                totals[matched] = (totals[matched] || 0) + amount;
            }
        });

        var ordered = {};
        groupSets.forEach(function (gs) {
            if ((totals[gs.label] || 0) > 0) {
                ordered[gs.label] = totals[gs.label];
            }
        });
        return ordered;
    }

    /**
     * What rush costs ON THIS BREAKDOWN, or 0 when it is not charged there.
     *
     * Read out of the same document the total comes from, deliberately. The
     * obvious alternative — the selected VALUE's own price_adjustment — is a
     * second source of truth for money, and it undercharges: addToCart captures
     * `price` before ensureRushSelected may tick rush, so the value says 350
     * while the captured total never included it, and subtracting anyway billed
     * $1,320 for a $1,670 hood. Deducting only what the breakdown actually
     * charges makes that whole class of staleness harmless — no fee present, no
     * deduction, and the un-rushed total is already the right figure for a buyer
     * who gets rush free.
     *
     * A rushed configuration whose breakdown charges nothing for rush is the one
     * case worth shouting about: it means the pricing rule was renamed out from
     * under RUSH_NAMES. Failing toward charging is the safe direction — visible
     * to the buyer, and the verifier agrees with the charge — where the opposite
     * is silent lost revenue.
     */
    function rushFee(views, breakdown) {
        var fee = groupPricesFromBreakdown(breakdown, [{ label: 'Rush', attributes: RUSH_NAMES }]).Rush || 0;
        if (!fee && rushSelected(views) && breakdown && !rushFee.warned) {
            rushFee.warned = true;
            // eslint-disable-next-line no-console
            console.warn('[expivi] rush is selected but the price breakdown charges nothing for '
                + 'it, so there is nothing to take off for an auto-rush buyer. The pricing rule '
                + 'has probably been renamed — RUSH_NAMES expects one of: ' + RUSH_NAMES.join(', '));
        }
        return fee;
    }

    /**
     * Total and add-on group amounts to charge for, with rush taken off when the
     * shopper's company (or the shopper) is entitled to it free.
     *
     * Auto-rush TICKS the rush checkbox — the configuration, BOM and cart line
     * must all carry rush — so unlike V1, where the option was simply never
     * selected, the fee IS in the configured total and has to be removed here.
     * Everyone else pays for the box they ticked, so this is a pass-through.
     *
     * `freeRush` is the amount to remove, from rushFee — passed in rather than
     * looked up here, so the one place that decides what rush costs is the one
     * place to fix when a catalogue prices it differently. When a merchant group
     * covers rush, the amount comes out of that row as well:
     * subtracting it from the total alone leaves excludedSum adding it straight
     * back, which overcharges the buyer by the rush fee times their wholesale %.
     */
    function chargeableBreakdown(breakdown, groups, freeRush) {
        var total = Number(breakdown && breakdown.total) || 0;
        var groupPrices = groupPricesFromBreakdown(breakdown, groups);
        var free = Number(freeRush) || 0;
        if (free <= 0) {
            return { total: total, groups: groupPrices };
        }

        var rushKeys = {};
        RUSH_NAMES.forEach(function (n) {
            rushKeys[n.toLowerCase()] = true;
        });
        (groups || []).some(function (g) {
            var named = (g.attributes || []).some(function (n) {
                return rushKeys[String(n || '').trim().toLowerCase()] === true;
            });
            if (!named || groupPrices[g.label] == null) {
                return named;
            }
            var left = (Number(groupPrices[g.label]) || 0) - free;
            if (left > 0) {
                groupPrices[g.label] = left;
            } else {
                delete groupPrices[g.label];
            }
            return true; // first-match-wins, same as groupPricesFromBreakdown
        });

        return { total: total - free, groups: groupPrices };
    }

    /**
     * The Master SKU (bill of materials) from the SDK BOM breakdown: each part's
     * `sku`, joined with '-'. Replaces V1's `articles[].price_sku` join and the
     * whole short-code assembly (parseMaterialSkus + configuredSkuKeys) — the
     * server computes the parts now, so there is nothing left to reconstruct.
     *
     * ponytail: top-level parts only. `part.components[]` carry their own skus;
     * include them here if the hub ever needs sub-assembly codes.
     */
    function masterSkuFromBom(bom) {
        var parts = (bom && bom.parts) || [];
        var skus = parts
            .map(function (p) {
                return String((p && p.sku) || '').trim();
            })
            .filter(Boolean);
        return skus.join('-');
    }

    // Rush is found by the VALUE's sku, never by the attribute's display name:
    // the live catalogue calls the attribute "Rush Manufacturing Options" while
    // V1 called it "Rush Manufacturing", and that one word silently switched
    // auto-rush off for every V2 order. The sku is what the BOM and the price
    // breakdown carry too. RUSH_NAMES is only for matching PRICE items, which
    // report a name or an identifier depending on how the fee is configured.
    var RUSH_IDENTIFIER = 'RSH';
    // Price items are named after PRICING RULES, not attributes — this
    // catalogue's rush line is {name: "Rushed Fee", identifier:
    // "blush-macaw-g-3946"} — so "Rushed Fee" is the name a merchant group would
    // carry, and the generated slug is unmatchable. Only used as a fallback now;
    // rushFee prefers the value's own price.
    var RUSH_NAMES = ['RSH', 'Rushed Fee', 'Rush Manufacturing', 'Rush Manufacturing Options', 'Rush Manufacturing Pricing'];
    // The cart contract is V1's, and line-item.liquid + the hub both still read
    // it: this exact key holding this exact value is what marks a rushed line.
    var RUSH_PROPERTY_KEY = 'Rush Manufacturing';
    var RUSH_PROPERTY = 'Rush My Order';

    /**
     * The rush option among an attribute's values, or null when this is not the
     * rush attribute. Presence of the RSH value IS the identification.
     */
    function rushValue(values) {
        var found = null;
        (values || []).some(function (v) {
            if (String((v && v.identifier) || '').trim().toUpperCase() === RUSH_IDENTIFIER) {
                found = v;
                return true;
            }
            return false;
        });
        return found;
    }

    /**
     * The value to set for an auto-rush buyer, or null when there is nothing to
     * do — not the rush attribute, or rush already selected.
     *
     * Rush is a two-value attribute ("Standard Lead Time" / "Rush My Lead Time"),
     * NOT an on/off flag, and the rush option is not at a fixed index — it is
     * values[1] here and values[0] on other two-value attributes in the same
     * catalogue. Picking by position selected "Standard Lead Time", the opposite
     * of what an entitled buyer is owed, so it is picked by sku.
     */
    function autoRushValue(view, values) {
        var rush = rushValue(values);
        if (!rush) {
            return null;
        }
        var current = ((view && view.configuredValue) || [])[0];
        if (current && current.cid === rush.cid) {
            return null;
        }
        return rush.cid || null;
    }

    /**
     * Is the rush option selected on this configuration? Read off the CONFIGURED
     * values, which carry the same `identifier` sku as the selectable ones.
     */
    function rushSelectedAttribute(attributes) {
        var found = null;
        (attributes || []).some(function (a) {
            var current = ((a && a.configuredValue) || [])[0];
            if (current && String(current.identifier || '').trim().toUpperCase() === RUSH_IDENTIFIER) {
                found = a;
                return true;
            }
            return false;
        });
        return found;
    }

    function rushSelected(attributes) {
        return !!rushSelectedAttribute(attributes);
    }

    /**
     * Last-resort guarantee that an entitled buyer's line carries rush. Returns
     * whether it does by the time this returns.
     *
     * build() ticks rush as soon as the attribute's values reach the store, and
     * measured on the live product that lands in the same frame as the first
     * paint — but nothing in the SDK ORDERS those two, and the add-to-cart
     * button is usable from that paint. A shopper who clicks first would ship a
     * line with rush silently absent: no fee, but no rush job either, and the
     * missing rush job is the half nobody can fix after the order lands.
     *
     * setValue dispatches into the store synchronously, so calling this
     * immediately before serialize() is enough, and the price genuinely does not
     * need to catch up — rushFee reads the fee out of the breakdown itself, so a
     * breakdown that predates this tick charges no rush, deducts no rush, and
     * leaves the un-rushed total that is already correct for these buyers.
     *
     * Deliberately does NOT skip a rule-disabled (not hidden) rush attribute —
     * only view.hidden stops this. Disabling is a UI-only concept in the SDK
     * (attribute.controller.js's own setDisabled: "No hook: disabling is a UI
     * concern only" — setValue is never blocked by it), and this guarantee is
     * explicitly meant to bypass the normal click path for an entitled buyer,
     * not defer to whatever froze the control for an unrelated reason.
     */
    function ensureRushSelected(sdk, configurator, attributes) {
        var views = (attributes && attributes.data) || [];
        if (rushSelected(views)) {
            return true;
        }
        var done = false;
        views.forEach(function (view) {
            if (done || !view || view.hidden) {
                return;
            }
            var ctrl = sdk.useAttribute(configurator, view.cid);
            var rushOn = autoRushValue(view, ctrl && ctrl.values);
            if (rushOn) {
                ctrl.markAsDirty();
                ctrl.setValue(rushOn);
                done = true;
            }
        });
        return done;
    }

    /**
     * Named, human-readable line-item properties from the live attribute store,
     * so the selected options (Size, Color, Ventilation, ...) show on the cart
     * and the order — the manufacturing detail. One entry per attribute with a
     * selection. Keys have no leading "_", so Shopify displays them.
     *
     * Prefers a configured value's `description` (the readable label) over its
     * `identifier`, which V2 tokenizes (`LooseNotInstalled`, `30Wx30H`).
     * Multi-select attributes join with ', '.
     */
    function configurationProperties(attributes) {
        var props = {};
        (attributes || []).forEach(function (a) {
            // A rule-hidden attribute is not part of the order, whatever stale
            // value it still holds.
            if (a && a.hidden) {
                return;
            }
            var name = String((a && a.name) || '').trim();
            // Same check the ConfigurableString render branch makes (see its own
            // comment): a free-text field's configuredValue can still just be the
            // catalogue's own settings.default_value, seeded there by the SDK's
            // createConfiguredValueFromSettings rather than typed by the shopper -
            // seen live used for instructional placeholder copy ("Color Code
            // Here") that has no business reaching an order as the answer.
            //
            // ConfigurableString ONLY, deliberately: a text field's default is
            // routinely catalogue-authored example/instructional copy, but a
            // NUMBER's default is a real default the merchant wants shoppers to
            // be able to accept as-is and have it ship - same settings.default_value
            // mechanism, opposite intent. Scoping this to text keeps a number
            // default pre-filled, editable AND saved, matching what it already
            // looks like on screen (the ConfigurableNumber branch never applied
            // this placeholder treatment, so saving it unconditionally keeps the
            // order line matching what the shopper saw).
            var defaultStrValue = (a && a.rtti === 'ConfigurableString' && a.settings && a.settings.default_value != null)
                ? String(a.settings.default_value) : null;
            var values = ((a && a.configuredValue) || [])
                .map(function (v) {
                    if (!v) {
                        return '';
                    }
                    // A CONFIGURED value has a different shape from a selectable
                    // one: no `config`, and `value` carries the label ("Raw
                    // (Unfinished)") while `description` is "". Reading
                    // description-then-identifier therefore put "ARAW" and "3030"
                    // on the cart line. `value` also holds what the shopper typed
                    // into a free-input attribute, so it leads for both.
                    var scalar = v.value != null && typeof v.value !== 'object' ? String(v.value) : '';
                    // An untouched text input serialises as the STRING "null",
                    // which would otherwise reach the cart as the word null.
                    if (scalar === 'null' || scalar === 'undefined') {
                        scalar = '';
                    }
                    // Still just the catalogue's default, never actually edited.
                    if (defaultStrValue != null && scalar === defaultStrValue) {
                        scalar = '';
                    }
                    var label = (v.config && v.config.value) || scalar || v.description || v.identifier;
                    return String(label == null ? '' : label).trim();
                })
                .filter(Boolean);
            if (name && values.length) {
                props[name] = values.join(', ');
            }
        });
        return props;
    }

    // ------------------------------------------------------------------
    // Browser-only below here
    // ------------------------------------------------------------------

    // A console no page script has touched. Something on this storefront blanks
    // console.log (and evidently console.warn) on window.console; a hidden
    // iframe has its own, untouched console, and Chrome/Firefox/Safari all show
    // an iframe's output in the same DevTools console. Shared across both debug
    // files through window.__hoodslyDebugConsole so only one iframe is made.
    function pristineConsole() {
      if (typeof window === 'undefined') return null;
      if (window.__hoodslyDebugConsole) return window.__hoodslyDebugConsole;
      var c = window.console || null;
      try {
        var frame = document.createElement('iframe');
        frame.style.display = 'none';
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('data-hoodsly-debug-console', '');
        (document.body || document.documentElement).appendChild(frame);
        if (frame.contentWindow && frame.contentWindow.console) c = frame.contentWindow.console;
      } catch (e) { /* fall back to the page console */ }
      window.__hoodslyDebugConsole = c;
      return c;
    }

    // Latest debug state, readable by typing HoodslySkuDebug in the console even
    // if console output itself is blocked.
    function debugState(patch) {
      if (typeof window === 'undefined') return;
      var s = window.HoodslySkuDebug || (window.HoodslySkuDebug = {});
      for (var k in patch) {
        if (Object.prototype.hasOwnProperty.call(patch, k)) s[k] = patch[k];
      }
    }

    // Idempotent: a page that already provides the SDK (the dev harness loads it
    // from a file picker) is left alone rather than fetching a second copy.
    function ensureSdk(src) {
        if (window.ProductConfiguratorLib) {
            return Promise.resolve();
        }
        return loadScript(src);
    }

    function loadScript(src) {
        return new Promise(function (resolve, reject) {
            var script = document.createElement('script');
            script.src = src;
            script.async = false;
            script.onload = resolve;
            script.onerror = function () {
                reject(new Error('Failed to load ' + src));
            };
            document.head.appendChild(script);
        });
    }

    // ------------------------------------------------------------------
    // 3D viewer
    // ------------------------------------------------------------------
    //
    // Expivi's own bridge is `<xpv-3d-viewer>` from ui-composer, which wraps
    // ~1200 lines in dist/libs/ui-composer/collection/bridge/viewer/. Mounting
    // the component would mean uploading the whole Stencil ui-kit + ui-composer
    // chunk set into Shopify's flat assets/ — the same reason we render the
    // options ourselves. Two of those files carry everything a plain
    // (non-bundle) product needs, and they are ported below: create the scene
    // (ProductViewer.initViewer), then push every configured value into it
    // (ProductViewerMappingAdapter).
    //
    // Deliberately not ported — every one of them is BundleProductOffer-only:
    // sub-product sync, drag/drop placement, snap-compatibility hooks, selection
    // outlines, placement indicators, the download-design toolbar. Also skipped:
    // ProductViewerSerializationAdapter, which calls the viewer's
    // saveConfiguration() and pushes a base64 screenshot into serialize() — that
    // would end up inside the `_expivi_configuration` cart property.
    //
    // ponytail: single product only. If a bundle product ever ships on this
    // theme, vendor their bridge rather than growing this.

    // Their CDN, not the theme's assets/: viewer.js is 2.5 MB and side-loads
    // vendor.babylonjs.js (4.4 MB) and six more chunks by relative path.
    // Gated catalogs this small auto-load every page instead of showing Load More.
    var AUTO_PAGE_MAX_COLORS = 10;
    // The viewer derives its API host from the host it was loaded from, so the
    // generic assets.expivi.net sends its catalogue call to expivi.net (401 for
    // a tenant token, empty dark scene). Load it from assets.<tenant host>.
    function viewerSrc(apiUrl) {
        return 'https://assets.' + new URL(apiUrl).host + '/viewer/latest/viewer.js';
    }

    function ensureViewer(src) {
        if (window.expivi) {
            return Promise.resolve();
        }
        return loadScript(src);
    }

    // Port of ProductViewerMappingAdapter. Duck-typed rather than extending
    // sdk.MappingAdapter: MappingEngine only ever calls isInitialized/init/
    // execute/destroy on an adapter, and this file is ES5.
    function viewerMappingAdapter(expivi) {
        var initialized = false;
        var applied = false;
        var sceneUuid = null;
        var attributeOfValue = {};  // 3D value id -> 3D attribute id
        var pending = false;        // scene wasn't ready: resend the full state next time

        function generateMaps() {
            return expivi.resolveProductNodes().then(function (nodes) {
                // ponytail: nodes[0] only. Their version maps one node per
                // productId, which matters only when a bundle has put more than
                // one product in the scene.
                var node = nodes && nodes[0];
                if (!node) {
                    return;
                }
                sceneUuid = node.uuid;
                return expivi.getProductAttributes(node.uuid).then(function (attrs) {
                    attributeOfValue = {};
                    (attrs || []).forEach(function (attr) {
                        (attr.attribute_values || []).forEach(function (value) {
                            attributeOfValue[value.id] = attr.id;
                        });
                    });
                });
            });
        }

        // Every viewer call is awaited in turn: the viewer applies them against
        // one scene graph and interleaving material + attribute writes reorders
        // what wins.
        function chained(items, step) {
            return (items || []).reduce(function (chain, item) {
                return chain.then(function () {
                    return step(item);
                });
            }, Promise.resolve());
        }

        function map3dAttribute(attribute) {
            // Every configured value, not just the first: a multi-select can
            // drive several 3D attributes at once. Last write wins on a clash.
            return chained(attribute.values, function (value) {
                return chained(value.visualization_value_mappings, function (mapping) {
                    var valueId = Number(mapping.target_id);
                    var attributeId = attributeOfValue[valueId];
                    if (!attributeId) {
                        return null;
                    }
                    return expivi.setProductAttribute(attributeId, valueId, sceneUuid);
                });
            });
        }

        function mapMaterialGroups(attribute) {
            // First value only: the material groups are declared on the
            // attribute, so every value would write over the same groups.
            var value = (attribute.values || [])[0];
            var materialId = value && value.material_mapping ? Number(value.material_mapping.target_id) : 0;
            if (!materialId || !isFinite(materialId)) {
                return Promise.resolve();
            }
            return chained(attribute.visualization_mappings, function (mapping) {
                var groupId = Number(mapping.target_id);
                if (!isFinite(groupId)) {
                    return null;
                }
                return expivi.applyActiveMaterial(groupId, materialId, sceneUuid);
            });
        }

        function mapNumberInputs(attribute) {
            if (attribute.value_type !== 'attribute_value_number') {
                return Promise.resolve();
            }
            var value = (attribute.values || [])[0];
            var raw = value ? value.value : null;
            // "null" as a string is what an unset number attribute serialises to.
            if (raw === null || raw === undefined || raw === 'null') {
                return Promise.resolve();
            }
            var number = Number(raw);
            if (isNaN(number)) {
                return Promise.resolve();
            }
            // Operand 0 always: single-operand targets are all V2 emits today.
            return chained(attribute.visualization_mappings, function (mapping) {
                return expivi.setProductAttribute(Number(mapping.target_id), { 0: { value: number } }, sceneUuid);
            });
        }

        function execute(data) {
            // Diffs after the first successful pass; a full state whenever the
            // scene wasn't there to receive the last one.
            var state = (!applied || pending) ? data.state : data.stateDiff;
            pending = false;
            if (!sceneUuid) {
                pending = true;
                return Promise.resolve();
            }
            // Their loop walks products then that product's attributes. With one
            // product in the scene every attribute belongs to it.
            return chained(state.attributes, function (attribute) {
                return map3dAttribute(attribute)
                    .then(function () {
                        return mapMaterialGroups(attribute);
                    })
                    .then(function () {
                        return mapNumberInputs(attribute);
                    });
            }).then(function () {
                applied = true;
            });
        }

        return {
            isInitialized: function () {
                return initialized;
            },
            init: function () {
                initialized = true;
                return generateMaps();
            },
            execute: execute,
            destroy: function () {
                initialized = false;
            },
        };
    }

    function mount3dViewer(configurator, container, token, mainProduct, apiUrl) {
        var VIEWER_SRC = viewerSrc(apiUrl);
        var mappers = mainProduct && mainProduct.visualizationMappers;
        var visualizationId = mappers && mappers[0] && mappers[0].catalogueId;
        if (!visualizationId) {
            // Not an error: a product offer with no visualization mapper has no
            // 3D scene attached in Expivi yet.
            // eslint-disable-next-line no-console
            console.warn('[expivi] this product offer has no visualization mapper, so there is no 3D scene to load.');
            return Promise.resolve();
        }
        return ensureViewer(VIEWER_SRC).then(function () {
            var expivi = window.expivi;
            if (!expivi || !expivi._create) {
                throw new Error('window.expivi is missing after loading ' + VIEWER_SRC);
            }
            var mapped = false;
            expivi._create(token, visualizationId, container, {
                show360Indicator: true,
                stabilizeOffsetsOnRenderUpdates: true,
            });
            expivi._events.onReady.subscribe(function () {
                // Their handshake: the viewer waits to be told someone is listening.
                expivi._dispatch('client_ready');
            });
            expivi._events.onChange.subscribe(function (event) {
                if (!event || event.name !== 'fully_loaded') {
                    return;
                }

                // Some catalogue models load with the camera already tight on
                // (or clipping past) the geometry — SDK-side, not a theme bug.
                // fitCamera() is the SDK's own auto-frame call, so prefer it
                // over guessing a fixed distance. Re-fit on every reload (a
                // size/trim change can swap the whole asset) rather than once,
                // using the fact that this event repeats for exactly that —
                // see the once-guard below for the part that must NOT repeat.
                //
                // __hoodslyReady is the ONLY thing canonicalScreenshot() (in
                // expivi-hoodsly.js) trusts before shooting. resolveProductNodes()
                // alone was not enough: it already returns nodes for a scene that
                // has nothing drawn in it yet (a dark box) or geometry still
                // mid-stream (a glitched, part-loaded capture) — both "succeeded"
                // as far as getScreenshot() could tell. fully_loaded + a settled
                // fitCamera is the real signal that a shot will look right. Set
                // after fitCamera either way: a failed fit leaves the framing
                // off, not the model unrendered, so the scene is still shootable.
                expivi.fitCamera().then(function () {
                    expivi.__hoodslyReady = true;
                }, function () {
                    expivi.__hoodslyReady = true;
                });

                // Registered on fully_loaded, not on ready: the adapter's first
                // act is to read the scene graph, which does not exist until the
                // model is in. The event can repeat, so register once.
                if (mapped) {
                    return;
                }
                mapped = true;
                configurator.registerPlugin({ mappingAdapters: [viewerMappingAdapter(expivi)] });
                configurator.getSystem('mapping').update();
            });
        });
    }

    // ------------------------------------------------------------------
    // 2D visualization
    // ------------------------------------------------------------------
    //
    // A 2D product has no 3D scene (no visualizationMappers); Expivi composites
    // image layers instead. useVisualization2d re-evaluates on every settled
    // rule run and hands back [{layerIndex, url}] already sorted, so all we do
    // is stack them. The cart screenshot is the same layers drawn to a canvas.
    function is2dOnly(mainProduct) {
        var mappers = mainProduct && mainProduct.visualizationMappers;
        return !!(mainProduct && mainProduct.publishedVisualization2dId
            && !(mappers && mappers[0] && mappers[0].catalogueId));
    }

    // The theme's gallery chrome (rotate hint, reset-view button, "3D" tag) is
    // static markup that only makes sense for a 3D scene, and Liquid cannot know
    // which kind the offer is. Flag it here; hs-product-details.liquid reacts.
    function markViewerKind(container, mainProduct) {
        var wrap = container.closest('.hs-gallery-wrap');
        if (!wrap) {
            return;
        }
        var is2d = is2dOnly(mainProduct);
        wrap.setAttribute('data-expivi-viz', is2d ? '2d' : '3d');
        if (is2d) {
            var tag = wrap.querySelector('.hs-gallery-thumb__tag');
            var thumb = wrap.querySelector('[data-gallery-3d]');
            if (tag) { tag.textContent = 'Preview'; }
            if (thumb) { thumb.setAttribute('aria-label', 'Product preview'); }
        }
    }

    function mount2dViewer(sdk, configurator, container, mainProduct) {
        if (!is2dOnly(mainProduct)) {
            return;
        }
        var ctrl = sdk.useVisualization2d(configurator);
        container.style.position = 'relative';
        var token = 0;
        var shot = null;
        var paint = function (layers) {
            if (!layers || !layers.length) {
                return;
            }
            var mine = ++token;
            // Build off-DOM and swap once every layer has loaded, so a re-render
            // never shows a half-composited frame.
            var imgs = layers.map(function (layer) {
                var img = new Image();
                img.alt = '';
                // Set before src: lets the cart screenshot draw these onto a canvas.
                img.crossOrigin = 'anonymous';
                img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;';
                return new Promise(function (resolve) {
                    img.onload = img.onerror = function () { resolve(img); };
                    img.src = layer.url;
                });
            });
            Promise.all(imgs).then(function (loaded) {
                if (mine !== token) {
                    return; // a newer evaluation superseded this one
                }
                container.replaceChildren.apply(container, loaded);
                shot = loaded;
            });
        };
        // Read by canonicalScreenshot() (expivi-hoodsly.js) when there is no 3D viewer.
        window.expivi2dScreenshot = function () {
            var ok = (shot || []).filter(function (i) { return i.naturalWidth; });
            if (!ok.length) {
                return null;
            }
            var size = 800;
            var cv = document.createElement('canvas');
            cv.width = cv.height = size;
            var ctx = cv.getContext('2d');
            ok.forEach(function (i) {
                var s = Math.min(size / i.naturalWidth, size / i.naturalHeight);
                var w = i.naturalWidth * s;
                var h = i.naturalHeight * s;
                ctx.drawImage(i, (size - w) / 2, (size - h) / 2, w, h);
            });
            // JPEG has no alpha, so flatten onto a background (PNG blew the
            // render endpoint's 400 KB cap: 356-638 KB measured live). The hub
            // then knocks out that flat colour, so it must differ from the hood:
            // a white hood on white vanished entirely. Dark bg for light hoods,
            // white for dark ones.
            var px = ctx.getImageData(0, 0, size, size).data;
            var sum = 0;
            var n = 0;
            for (var p = 0; p < px.length; p += 4 * 7) {
                if (px[p + 3] > 200) {
                    sum += (px[p] + px[p + 1] + px[p + 2]) / 3;
                    n++;
                }
            }
            var out = document.createElement('canvas');
            out.width = out.height = size;
            var octx = out.getContext('2d');
            // Some colours (White) ship an opaque layer with the white backdrop
            // baked in, so no fill can separate hood from background. The hub
            // skips its knockout when the four corners disagree: nudge one.
            var baked = px[3] > 200;
            octx.fillStyle = !baked && n && sum / n > 150 ? '#303030' : '#fff';
            octx.fillRect(0, 0, size, size);
            octx.drawImage(cv, 0, 0);
            if (baked) {
                octx.fillStyle = '#d0d0d0';
                octx.fillRect(0, 0, 4, 4);
            }
            return out.toDataURL('image/jpeg', 0.85);
        };
        ctrl.subscribe(paint);
        ctrl.subscribeBusy(function (busy) {
            container.classList.toggle('is-loading', busy);
        });
        ctrl.subscribeError(function (message) {
            if (message) {
                // eslint-disable-next-line no-console
                console.error('[expivi] 2D visualization failed:', message);
            }
        });
        paint(ctrl.data);
    }

    /**
     * Add-to-cart while the configurator is still loading. Held rather than
     * hidden: a button that appears late shifts the layout under a shopper who
     * is already reaching for it, and `disabled` is what actually stops a click
     * — the class is only the spinner.
     *
     * The sold-out button carries no [data-expivi-add-to-cart], so it is never
     * matched here and stays disabled as the template left it.
     */
    function setAddToCartLoading(root, loading) {
        var button = root && root.querySelector('[data-expivi-add-to-cart]');
        if (!button) {
            return;
        }
        // First paint arriving while a late snapshot restore is pending must
        // drop the spinner but leave the button off (setAddToCartRestoring).
        button.disabled = loading || !!root.dataset.expiviRestoring;
        button.classList.toggle('expivi-viewer__add--loading', loading);
    }

    /**
     * Add-to-cart while trackPriceFreshness has it disabled for a stale
     * price. Deliberately NOT setAddToCartLoading() above: that class hides
     * the label behind a spinner (color: transparent), which reads as a
     * skeleton with nothing to say why the button won't respond — this state
     * has a specific, useful reason, so it says it. The label is captured
     * once (on the first call this session) and restored verbatim, so it
     * still reads "Add to cart" and not the loading text after settling.
     */
    function setAddToCartBusy(root, busy) {
        var button = root && root.querySelector('[data-expivi-add-to-cart]');
        if (!button) {
            return;
        }
        // The saved configuration is still being restored: price freshness
        // must not re-enable the button underneath that.
        if (!busy && root.dataset.expiviRestoring) {
            return;
        }
        button.disabled = busy;
        button.classList.toggle('expivi-viewer__add--busy', busy);
        if (busy) {
            if (button.dataset.expiviLabel === undefined) {
                button.dataset.expiviLabel = button.textContent;
            }
            button.textContent = 'Updating price…';
        } else if (button.dataset.expiviLabel !== undefined) {
            button.textContent = button.dataset.expiviLabel;
        }
    }

    /**
     * The load gave up waiting and built the panel with DEFAULTS while the
     * saved configuration is still on its way. Selling now would sell the
     * default hood, not the product the shopper clicked on, so the button
     * says why it is off. Released when the restore resolves or the hard cap
     * passes, so a broken snapshot cannot block purchase for good.
     */
    function setAddToCartRestoring(root, on) {
        var button = root && root.querySelector('[data-expivi-add-to-cart]');
        if (!root) {
            return;
        }
        // Releasing when nothing was held must touch nothing: the normal
        // path calls this before first paint, while the loading hold is on.
        if (!on && !root.dataset.expiviRestoring) {
            return;
        }
        if (on) {
            root.dataset.expiviRestoring = '1';
        } else {
            delete root.dataset.expiviRestoring;
        }
        if (!button) {
            return;
        }
        button.disabled = on || button.classList.contains('expivi-viewer__add--loading');
        if (on) {
            if (button.dataset.expiviLabel === undefined) {
                button.dataset.expiviLabel = button.textContent;
            }
            button.textContent = 'Restoring your saved configuration…';
        } else if (button.dataset.expiviLabel !== undefined) {
            button.textContent = button.dataset.expiviLabel;
        }
    }

    /**
     * Stop the panel presenting as loading. A successful build empties the
     * container and takes the skeleton with it, but the error path never gets
     * that far — so remove it explicitly, or it shimmers under the error message
     * forever.
     */
    function clearOptionsSkeleton(root) {
        var container = root && root.querySelector('[data-expivi-option-container]');
        if (!container) {
            return;
        }
        container.removeAttribute('aria-busy');
        var skeleton = container.querySelector('.expivi-skeleton');
        if (skeleton) {
            skeleton.remove();
        }
    }

    function showError(roots, message) {
        // An error is the end of LOADING, not a green light: the shimmer goes,
        // the button stays disabled. Re-enabling it here handed the shopper a
        // dead control — boot()'s failure paths return before start(), which is
        // the only place the click handler is ever attached, so the button
        // looked ready and did nothing at all.
        //
        // addToCart()'s own failure path is unaffected: its `finally` re-enables
        // the button after this runs, which is right — that panel loaded, and
        // the shopper should be able to try again.
        roots.forEach(function (root) {
            var button = root && root.querySelector('[data-expivi-add-to-cart]');
            if (button) {
                button.disabled = true;
                button.classList.remove('expivi-viewer__add--loading');
            }
            clearOptionsSkeleton(root);
        });

        roots.forEach(function (root) {
            if (!root) {
                return;
            }
            var el = root.querySelector('[data-expivi-error]');
            if (el) {
                el.textContent = message;
                el.hidden = false;
            }
        });
    }

    function cartRoot() {
        return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    // Currency formatter from the configurator's data-currency/data-locale. The
    // SDK also reports a currency on the breakdown; the theme's cart currency
    // wins because that is what Shopify will actually charge.
    function moneyFormatter(root) {
        var currency = (root.dataset.currency || 'USD').toUpperCase();
        var locale = root.dataset.locale || 'en-US';
        try {
            return new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
        } catch (e) {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
        }
    }

    /**
     * The context every Expivi request is made in. PINNED, not derived from the
     * storefront's localization.
     *
     * The team is provisioned for en-US only, and anything else is a hard 422:
     * "The locale en-ca is not supported by this team." A Canadian visitor made
     * `localization.country.iso_code` "ca", which turned the bare "en" Shopify
     * reports into "en-ca" and took the whole configurator down for them.
     *
     * Widen this only when Expivi provision the extra locales; the failure is a
     * blank configurator, not a fallback.
     *
     * All three must stay non-empty. SDK 1.5.0 stopped defaulting them and now
     * fills any gap from GET /view/team instead — and this team's defaults are
     * country us, locale en-us, currency EUR. Blanking `currency` would not
     * error; it would quietly price the whole configurator in euros.
     */
    var EXPIVI_CONTEXT = { locale: 'en-US', country: 'US', currency: 'USD' };

    // Build via DOM, not innerHTML: labels come from merchant metafields and
    // Expivi attribute names — textContent keeps stray HTML inert.
    function breakdownRow(label, value) {
        var row = document.createElement('div');
        row.className = 'expivi-viewer__breakdown-row';
        var l = document.createElement('span');
        l.textContent = label;
        var v = document.createElement('span');
        v.textContent = value;
        row.appendChild(l);
        row.appendChild(v);
        return row;
    }

    /**
     * The human label for an attribute value.
     *
     * `config.value` first: that is the API's `valuable.value` as the SDK's view
     * model exposes it, and it is the ONLY field carrying a label on this
     * catalogue - `description` is null on every value, so falling back to the
     * identifier offered "3030" for a size and "AHOOBLA00001" for a colour.
     */
    /**
     * Short name for the stepper and the Next button (branded controls):
     * "Size Options" -> "Size", "Select Wood Species" -> "Wood Species",
     * "Color & Finish" stays. Falls back to the full name if stripping would
     * leave nothing.
     */
    /**
     * Branded controls: the catalogue has no thumbnails, so wood species
     * would render as plain text cards. A list value named after one of our
     * woods gets the theme's own close-up photo instead (assets/wood-*-close.png),
     * which turns the Wood step into the design's material cards. Only for
     * `list` controls on a field whose name mentions wood - colour tiles named "American Walnut" etc. are stains,
     * not woods, and are never touched.
     */
    var WOOD_PHOTOS = [
        [/white\s*oak/i, 'wood-whiteoak-close.png'],
        [/walnut/i, 'wood-walnut-close.png'],
        [/maple/i, 'wood-maple-close.png'],
    ];
    var assetBase = null;
    function woodPhoto(v) {
        var name = valueLabel(v);
        var file = null;
        for (var i = 0; i < WOOD_PHOTOS.length; i++) {
            if (WOOD_PHOTOS[i][0].test(name)) {
                file = WOOD_PHOTOS[i][1];
                break;
            }
        }
        if (!file) {
            return '';
        }
        if (assetBase === null) {
            // The SDK's own asset_url (data-sdk-url) gives the theme's asset folder.
            var sdk = document.querySelector('[data-sdk-url]');
            var url = sdk ? sdk.getAttribute('data-sdk-url') || '' : '';
            assetBase = url ? url.split('?')[0].replace(/[^/]*$/, '') : '';
        }
        return assetBase ? assetBase + file : '';
    }

    function shortStepName(name) {
        var full = String(name || '').trim();
        var s = full.replace(/^(select|choose)\s+(your\s+)?/i, '')
            .replace(/\s+(options?|selection)$/i, '')
            .trim();
        return s || full;
    }

    function valueLabel(v) {
        var label = v && v.config && v.config.value;
        // `value` is in the chain for Static* : their text arrives there, not in
        // config, so reading description alone rendered them blank.
        return String(label || (v && (v.value || v.description || v.identifier)) || '')
            .trim() || '(unnamed)';
    }

    /**
     * The option label with what that choice costs appended:
     *   30" Width x 36" Height (+$1,800.00)
     *
     * A native <option> renders text and nothing else — no element, no styling —
     * so the price goes inline rather than in a right-aligned column the way
     * Expivi's own composer lays it out. Same information, no custom dropdown to
     * build and keep accessible.
     *
     * A zero adjustment prints nothing. Most attributes here are colours and
     * finishes priced at 0, and "(+$0.00)" on all 27 of them is noise that
     * buries the handful that do cost something.
     *
     * RETAIL, deliberately un-discounted: a wholesale % applies to the hood but
     * NOT to discount-excluded add-ons (ADR-0006), so a per-option "discounted"
     * figure would be wrong for exactly the add-ons a buyer most wants to price.
     * The discount is shown once, on the breakdown, where it can be correct.
     */
    /**
     * What one value adds to the price, in the currency the catalogue publishes.
     *
     * `value.priceAdjustment` is the SDK's own field and is what
     * `XpvListAttributeComponentHelpers.optionPriceAdjustment` reads. It was NOT
     * always there: expivi-hoodsly.js patches XMLHttpRequest to scrape
     * `price_adjustment` out of the raw attribute-values response, on the note
     * that "mapAttributeValueGet ... price_adjustment is not among the fields it
     * copies". Measured on 1.7, that is no longer true — all 236 values on this
     * product carry `priceAdjustment`, 129 of them non-zero.
     *
     * So the store is the source now and the scrape is the fallback, rather than
     * the other way round. The scrape cannot simply be deleted: `configuredValue`
     * entries carry {id, identifier, description, thumbnailUrl, value, cid} and
     * NOT priceAdjustment, so a selection that has been paged off the list would
     * lose its price label (see offPageSelections). It stays for that, and for
     * any bundle older than 1.7 — but nothing that renders normally depends on a
     * monkey-patched global any more, which is the point: that patch stops
     * working the day the SDK moves one call to fetch().
     */
    function optionPrice(v) {
        if (v && v.priceAdjustment != null) {
            return v.priceAdjustment;
        }
        return valuePrice(v && v.id);
    }

    function optionLabel(v, fmt, showPrice) {
        var label = valueLabel(v);
        // `showPriceAdjustment: false` is a merchant hiding the adjustment on
        // purpose — a bundled option, a price already in the base. The SDK's
        // optionPriceAdjustment returns null for it and draws nothing. Printing
        // it anyway contradicted the back office on the shopper's screen.
        var price = showPrice === false ? 0 : optionPrice(v);
        return price ? label + ' (+' + fmt.format(price) + ')' : label;
    }

    /**
     * Hide the colour values this shopper's company isn't allowed to see, using
     * the SDK's own value-category filter — a server-side `filter[category.id]`
     * on the attribute-values request, replacing V1's CSS rule that matched
     * swatch image URLs.
     *
     * The company's Expivi category is matched by NAME against the company's
     * external id (same convention V1 used for `categories[].name`).
     *
     * ponytail + open question: the native filter is "value is in these
     * categories", so while a company filter is active, UNTAGGED colours are
     * hidden too. V1 showed untagged colours to everyone. If merchants rely on
     * that, the filter must be replaced by client-side filtering over
     * `allValues` once value categories are readable per value.
     */
    function applyCompanyCategoryFilter(ctrl, cid, companyCode, done, skip) {
        // Colour catalogs, when configured, are the gate. Running the category
        // filter as well would intersect two separately-managed allow-lists and
        // hide colours the merchant granted on the Colors page.
        if (skip) {
            return Promise.resolve();
        }
        if (!companyCode || typeof ctrl.loadAvailableCategories !== 'function') {
            return Promise.resolve();
        }
        // Once per attribute, ever. setCategoryFilter() reloads the value page,
        // which changes the attribute store, which rebuilds the panel — calling
        // this from every rebuild would loop forever.
        if (done[cid]) {
            return Promise.resolve();
        }
        // Two states, not one. `true` means ATTEMPTED and exists only to stop
        // the loop above; `'gated'` means the filter is actually on. The shopper
        // category chips read the second, because suppressing them on a merely
        // attempted gate would hide the control permanently after one failed
        // fetch — and leave the shopper no way to filter 307 colours.
        done[cid] = true;
        return ctrl
            .loadAvailableCategories()
            .then(function (categories) {
                var match = (categories || []).filter(function (c) {
                    return String((c && c.name) || '').trim().toLowerCase() === companyCode;
                })[0];
                if (!match) {
                    return null; // company has no gated palette — leave every value visible
                }
                return Promise.resolve(ctrl.setCategoryFilter([match.id])).then(function (r) {
                    done[cid] = 'gated';
                    return r;
                });
            })
            .catch(function () {
                return null; // gating is cosmetic; never block the configurator on it
            });
    }

    /**
     * Render the option panel from the live attribute store.
     *
     * One control per attribute, in store order. Rebuilt wholesale on any store
     * change because a rule can disallow or reveal *other* attributes — free-input
     * controls therefore commit on 'change' (blur), never 'input', so a rebuild
     * can't eat keystrokes.
     *
     * Laid out as a wizard when the catalogue declares steps — see stepPlan and
     * renderSteps. Products without a layout tree still render flat.
     */
    /**
     * Everything renderOptions' build() draws, flattened to one comparable
     * string: per attribute, its identity and type, the value the store holds,
     * and the labelled options it would render after colour gating.
     *
     * Built from the same inputs build() reads, so anything build would draw
     * differently shows up here — option prices, for instance, arrive
     * asynchronously and reach this through optionLabel. Attributes build()
     * skips are skipped here too, so a change confined to one of them cannot
     * trigger a redraw that would show nothing.
     *
     * Module-level rather than a closure so it can be tested: an
     * under-sensitive signature means the panel silently stops updating, which
     * is the one failure worth pinning.
     */
    function panelSignature(attributes, sdk, configurator, opts) {
        var parts = [];

        ((attributes && attributes.data) || []).forEach(function (view) {
            if (!view || view.hidden) {
                return;
            }
            // Same skip build() makes: an attribute no step owns is not drawn,
            // so its price arriving must not count as a panel change.
            if (opts.placed && !opts.placed[view.cid]) {
                return;
            }
            var ctrl = sdk.useAttribute(configurator, view.cid);
            var values = (ctrl && ctrl.values) || [];
            if (opts.autoRush && rushValue(values)) {
                return;
            }
            var allowedColors = allowedColorIdentifiers(view, opts.colors, opts.colorAttributes);
            var sigCfg = pageConfig(view);
            if (allowedColors && !values.some(function (v) {
                return String((v && v.identifier) || '') in allowedColors;
            }) && !(sigCfg && sigCfg.page < sigCfg.lastPage)) {
                // Mirrors build(): while more pages remain it KEEPS the gate and
                // auto-pages, so only an exhausted attribute is left unfiltered.
                // Nulling on the current page alone flapped the signature.
                allowedColors = null;
            }

            var rendered = [];
            values.forEach(function (v) {
                if (allowedColors && !isAllowedColor(v, allowedColors)) {
                    return;
                }
                // v.disabled too: a rules pass that only flips a value's disabled
                // state (nothing hidden, nothing reordered, no price change)
                // produced an otherwise byte-identical signature, so the repaint
                // that was supposed to grey it out got skipped by the dedup below.
                rendered.push(v.cid + '~' + optionLabel(v, opts.money) + (v.disabled ? '!d' : ''));
            });

            var current = (view.configuredValue || [])[0] || {};
            // Messages are drawn into the field, so one arriving or clearing is a
            // real change — without this the note never appears until something
            // else happens to redraw.
            var notes = opts.messages && view.identifier
                ? opts.messages.getAttributeMessages(view.identifier).map(function (m) {
                    return (m.type || '') + ':' + (m.message || '');
                }).join(';')
                : '';
            parts.push([
                view.cid,
                view.rtti,
                view.name || view.identifier || '',
                // Attribute-level disabled (ctrl.isDisabled, from view.disabled):
                // same reasoning as v.disabled on `rendered` above, for the
                // whole-attribute case. ctrl guarded the same way `values`
                // above already is - useAttribute can return a falsy
                // controller here (that's what that guard is for), and
                // fakeSdk in the test suite always returns a real object, so
                // an unguarded read here would pass every test and still
                // throw live the first time it hit an unregistered cid.
                (ctrl && ctrl.isDisabled) ? '1' : '0',
                current.cid || '',
                current.value != null ? String(current.value) : '',
                current.description || '',
                rendered.join(','),
                notes,
                // The value page's own state, so the panel redraws when a fetch
                // starts and again when it lands. Without the status in here a
                // search or a Load More changed nothing the signature could see
                // until the new values arrived, so the "loading" state had no
                // build to be drawn in and never appeared at all.
                ((view.valuePage || {}).status || '') + ':' + ((view.valuePage || {}).config || {}).page,
                // The query, sort and category filter as well — everything
                // accumulateValues keys on.
                //
                // `rendered` above is built from ctrl.values, the CURRENT page,
                // while build() renders the ACCUMULATED list. Those two can
                // agree while the accumulation underneath them differs: a search
                // whose first page happens to match the unfiltered first page
                // leaves ctrl.values, the page number and the status all
                // identical, so the guard skips the one build that would have
                // reset the accumulator, and the panel keeps showing pages the
                // search has ruled out.
                //
                // Cheaper to measure the inputs than to reason about when the
                // outputs can collide.
                pageSignature(view),
            ].join('|'));
        });

        return parts.join('\n');
    }

    /**
     * `opts` plus the messages controller, so panelSignature can see the notes
     * build() draws without renderOptions' caller having to know about them.
     */
    function withMessages(opts, messages) {
        var merged = {};
        Object.keys(opts || {}).forEach(function (k) {
            merged[k] = opts[k];
        });
        merged.messages = messages;
        return merged;
    }

    function byPosition(a, b) {
        return (a.position || 0) - (b.position || 0);
    }

    /**
     * The visible layout tree indexed by parent, each parent's children sorted.
     *
     * `position` is numbered PER PARENT, not globally: two sections both number
     * their steps from 0, so anything that sorts all steps by position alone
     * interleaves them. Ordering has to come from walking the tree.
     *
     * `visibility` is dropped here rather than at every use — a rule can hide a
     * whole step, and a hidden entity hides its subtree with it.
     *
     * Only an explicit `false` hides. Treating a MISSING flag as hidden meant a
     * bundle that stopped sending it (or a new entity type that never had it)
     * emptied the whole tree, stepPlan returned null, and the panel fell back to
     * flat with nothing said — the loudest possible bug reported as silence.
     */
    function layoutIndex(data) {
        var byParent = {};
        (data || []).forEach(function (e) {
            if (!e || e.visibility === false) {
                return;
            }
            var key = e.parentId || '';
            (byParent[key] = byParent[key] || []).push(e);
        });
        Object.keys(byParent).forEach(function (k) {
            byParent[k].sort(byPosition);
        });
        return byParent;
    }

    /**
     * Every attribute in this subtree, in reading order.
     *
     * Depth-first, because a step's attributes are not always its direct
     * children: `static_grouped_attributes_layout_entity` puts a labelled group
     * in between and Expivi's composer renders what is inside it. A
     * direct-children filter drops those attributes with no error anywhere —
     * which is the exact failure this whole function exists to avoid.
     *
     * Nested steps are left alone: their attributes belong to that step's page.
     */
    /**
     * The widget the merchant chose for this attribute, off the layout entity.
     *
     * `displayType` is per-attribute and lives ONLY here — the attribute store
     * has no idea whether its values were meant to be a dropdown, a radio list
     * or a row of toggle buttons. Reading the store alone is why the panel drew
     * 27 identical <select>s while the reference preview drew five kinds.
     *
     * The WHOLE displaySettings object is kept, not just the type: `searchable`
     * and `sortable` live beside it and valueTools draws them.
     */
    function noteDisplay(display, e) {
        var ds = e.config && e.config.displaySettings;
        if (ds && ds.displayType) {
            display[e.attributeCid] = ds;
        }
    }

    /**
     * Static content the merchant dropped into the layout — a paragraph
     * (`static_text`, `config.text`) or a picture (`static_image`,
     * `config.thumbnailUrl`). Neither has an attributeCid, so the walk used to
     * step straight over them and they rendered as nothing at all.
     *
     * They join the same ordered list as the attributes under a synthetic key,
     * so everything downstream — step ownership, planSignature, renderSteps,
     * the `fields` map — keeps working on one flat list in document order. A
     * paragraph that explains the options above it has to render BETWEEN them,
     * not in a separate pass at the end.
     */
    var STATIC_BLOCKS = {
        static_text_layout_entity: 'text',
        static_image_layout_entity: 'image',
        // A labelled group of attributes. Unlike the other two it still has
        // children to walk, so it emits a heading and the walk carries on
        // through it — see attrCidsUnder.
        static_grouped_attributes_layout_entity: 'group',
    };

    function staticKey(e) {
        return 'static:' + e.id;
    }

    /**
     * Whether this entity will actually draw something. staticBlock returns null
     * for a group with no name, an image with no thumbnailUrl and a text block
     * with no text — so pushing their keys into the step contributes a slot that
     * can never be filled.
     *
     * renderSteps filters on `fields[cid]` and so never breaks on one, but the
     * key still counts towards a step's contents: a step holding nothing but an
     * unnamed group looked occupied to stepPlan and empty to renderSteps, which
     * drew the "No options available for your current selection" placeholder
     * over a step that was never going to show anything.
     */
    function staticBlockDraws(e) {
        var config = e.config || {};
        switch (STATIC_BLOCKS[e.entityType]) {
        case 'group':
            return !!config.name;
        case 'image':
            return !!config.thumbnailUrl;
        case 'text':
            return !!config.text;
        default:
            return false;
        }
    }

    function attrCidsUnder(byParent, id, out, display, seen) {
        (byParent[id] || []).forEach(function (e) {
            if (e.entityType === 'static_step_layout_entity') {
                return;
            }
            if (STATIC_BLOCKS[e.entityType] && !seen.node['block:' + e.id]) {
                seen.node['block:' + e.id] = true;
                if (staticBlockDraws(e)) {
                    out.push(staticKey(e));
                    seen.blocks[staticKey(e)] = e;
                }
                if (STATIC_BLOCKS[e.entityType] !== 'group') {
                    return; // no children, and no attribute to record
                }
                // A group's heading goes in front of its own children, which
                // the descend below still has to collect — whether or not the
                // group itself draws a heading.
            }
            // A cid claimed twice would be APPENDED twice, and appendChild MOVES
            // the node: the first panel silently empties while still counting as
            // a step. First placement wins.
            if (e.attributeCid && !seen.attr[e.attributeCid]) {
                seen.attr[e.attributeCid] = true;
                out.push(e.attributeCid);
                noteDisplay(display, e);
            }
            // parentId comes off the wire; a cycle in it would recurse forever.
            if (!seen.node[e.id]) {
                seen.node[e.id] = true;
                attrCidsUnder(byParent, e.id, out, display, seen);
            }
        });
        return out;
    }

    /**
     * The catalogue's layout tree, flattened to what the panel needs:
     * `{steps: [{name, attrCids}], loose: [cid]}`, or null when the product
     * declares no steps — then the panel stays flat, exactly as it always was.
     *
     * V1 expressed steps as `step` PARENT ATTRIBUTES. V2 moved them out of the
     * attribute store and into the layout tree (`useStoreLayouts`), a flat list
     * of entities linked by parentId:
     *
     *   StaticSection "Customize Wood Hood"  (displayType: horizontal_step)
     *     └ StaticStep "Size Options"        ← one page of the wizard
     *         └ ConfigurableList  attributeCid → the attribute store
     *
     * Sections are walked but not drawn. Expivi gives them five display types
     * (tab / vertical_step / horizontal_step / flat / custom); the first three
     * are all "navigate between steps", which is what the wizard does. `flat`
     * sections hold their attributes directly rather than in steps, so those
     * arrive in `loose` and render above the wizard, always visible. Several
     * sections' steps run together in one sequence — accurate ordering, no
     * per-section chrome. Add a section tab strip here if a merchant needs one.
     *
     * `loose` is the safety net generally: an attribute placed under anything
     * this function does not recognise still renders, in tree order, rather
     * than vanishing.
     *
     * ponytail: attributes the tree does not place AT ALL are not rendered —
     * what Expivi's own composer does, and this catalogue relies on it to keep
     * "Select Hood Color" off the page. build() warns once per attribute so
     * that stays diagnosable rather than silent.
     */
    function stepPlan(layouts) {
        var byParent = layoutIndex((layouts && layouts.data) || []);
        var steps = [];
        var loose = [];
        var display = {}; // attribute cid -> the layout entity's displaySettings
        // Shared across steps AND loose: see attrCidsUnder.
        var seen = { attr: {}, node: {}, blocks: {} };

        // The section a step sits in decides two things the step cannot decide
        // alone: how the steps are laid out (`displayType`), and whether a step
        // may hide its own title. Carried down the walk rather than looked up
        // afterwards, because a catalogue may hold several sections.
        var sections = [];

        (function descend(id, section) {
            (byParent[id] || []).forEach(function (e) {
                if (e.entityType === 'static_section_layout_entity') {
                    var ds = (e.config && e.config.displaySettings) || {};
                    var entry = {
                        name: (e.config && e.config.name) || '',
                        displayType: ds.displayType || 'horizontal_step',
                        // Absent means shown, which is the SDK's default.
                        showTitle: ds.showTitle !== false,
                    };
                    sections.push(entry);
                    if (!seen.node[e.id]) {
                        seen.node[e.id] = true;
                        descend(e.id, entry);
                    }
                    return;
                }
                if (e.entityType === 'static_step_layout_entity') {
                    var sds = (e.config && e.config.displaySettings) || {};
                    steps.push({
                        name: (e.config && e.config.name) || '',
                        // `StaticStepLayoutEntityController.showTitle`: a step
                        // may only hide its title inside a `flat` section. In a
                        // stepped section the title IS the navigation, so a
                        // merchant turning it off would leave pages with no
                        // names on the dots.
                        showTitle: sds.showTitle !== false
                            || !section || section.displayType !== 'flat',
                        // How this step's SECTION wants its steps laid out. On
                        // the step because a catalogue may hold several
                        // sections, and renderSteps draws one sequence.
                        layout: (section && section.displayType) || 'horizontal_step',
                        attrCids: attrCidsUnder(byParent, e.id, [], display, seen),
                    });
                    return; // its subtree belongs to the step, not to `loose`
                }
                if (STATIC_BLOCKS[e.entityType] && !seen.node['block:' + e.id]) {
                    seen.node['block:' + e.id] = true;
                    if (staticBlockDraws(e)) {
                        loose.push(staticKey(e));
                        seen.blocks[staticKey(e)] = e;
                    }
                    if (STATIC_BLOCKS[e.entityType] !== 'group') {
                        return;
                    }
                }
                if (e.attributeCid && !seen.attr[e.attributeCid]) {
                    seen.attr[e.attributeCid] = true;
                    loose.push(e.attributeCid);
                    noteDisplay(display, e);
                }
                if (!seen.node[e.id]) {
                    seen.node[e.id] = true;
                    descend(e.id, section);
                }
            });
        }('', null));

        if (!steps.length) {
            return null;
        }

        return {
            steps: steps.filter(function (s) {
                return s.attrCids.length;
            }),
            loose: loose,
            display: display,
            blocks: seen.blocks,
            sections: sections,
        };
    }

    /**
     * The plan as one comparable string, so a rule that hides a step or moves an
     * attribute between steps forces a rebuild. panelSignature cannot see this:
     * the same attributes in the same state can belong to different pages.
     */
    function planSignature(plan) {
        if (!plan) {
            return '';
        }
        // displayType is in here too: the same attributes in the same state
        // render as a dropdown or a radio list depending on it alone.
        var widgets = Object.keys(plan.display).sort().map(function (cid) {
            return cid + '=' + plan.display[cid].displayType;
        }).join(',');
        return widgets + '|' + plan.loose.join(',') + '|' + plan.steps.map(function (s) {
            return s.name + '>' + s.attrCids.join(',');
        }).join(';');
    }

    /**
     * Draw the built fields as a wizard: a dot per step, one visible panel, and
     * a Back / Next footer — the shape Expivi's composer gives this catalogue.
     *
     * `state.index` is owned by renderOptions' closure, not by this function,
     * because build() wipes the container on every rebuild: without it a rule
     * settling while the shopper is on step 4 would drop them back on step 1.
     *
     * A step whose every attribute is currently disallowed is dropped rather
     * than shown empty, so "Step 2 of 5" counts pages the shopper can reach.
     * Fewer than two left is not a wizard — those render flat, with no chrome.
     */
    /**
     * A pick-one control drawn as one row per value instead of a <select>.
     *
     * `list`, `tiles` and `toggle_buttons` are the same control wearing three
     * skins, so they share this builder and differ by a modifier class. Three
     * separate loops would each have to re-solve colour gating, the price
     * lookup and the change handler, and drift apart the first time one is
     * touched.
     *
     * Radios rather than clickable divs: arrow-key selection, the accessible
     * name, and the group semantics all come free, and Expivi's own markup is a
     * radiogroup underneath its styling too.
     *
     * The price gets its own cell rather than being appended to the label the
     * way optionLabel does it — the composer right-aligns it in a column, which
     * a native <option> cannot do but this can.
     */
    function valueGroup(view, ctrl, kind, allowedColors, selectedCids, money, showPrice, offPage, values, thumbImgs) {
        var group = document.createElement('div');
        var readOnly = !!ctrl.isStatic;
        // `valueInputType: multiple` lets the shopper hold several values at
        // once. The SDK reads it as `controller.isMultiple`, keeps the whole
        // `configuredValue` array in `selectedValues`, and marks the listbox
        // aria-multiselectable. Radios cannot express that — the browser
        // enforces one-per-name — so the control changes with the attribute.
        var many = !!ctrl.isMultiple;
        group.className = 'expivi-options expivi-options--' + kind.replace(/_/g, '-')
            + (readOnly ? ' expivi-options--static' : '');
        group.setAttribute('role', many ? 'group' : 'radiogroup');
        if (many) {
            group.setAttribute('aria-multiselectable', 'true');
        }
        if (readOnly || ctrl.isDisabled) {
            group.setAttribute('aria-disabled', 'true');
        }
        var focusable = null;

        // The shopper's own selection first when paging has taken it off the
        // list, so the group never shows nothing checked for a value the store
        // is still holding — and still charging for.
        (offPage || []).concat(values || []).forEach(function (v) {
            // Same gating rule as the <select> path — see build().
            if (allowedColors && !isAllowedColor(v, allowedColors)) {
                return;
            }

            var option = document.createElement('label');
            option.className = 'expivi-options__option';

            var radio = document.createElement('input');
            radio.type = many ? 'checkbox' : 'radio';
            radio.className = 'expivi-options__radio';
            // Per attribute, or every radio in the panel joins one group and
            // picking a wood species would clear the colour above it.
            radio.name = 'expivi-' + view.cid;
            radio.value = v.cid;
            radio.checked = selectedCids.indexOf(v.cid) !== -1;
            radio.dataset.expiviAttrCid = view.cid;
            // `canClickOption` returns false for every option of a static
            // attribute, so the SDK's rows are inert. Disabling the radio is
            // the same thing said in markup: no click, no arrow key, no focus.
            // Same for a rule-disabled attribute (view.disabled -> ctrl.isDisabled)
            // or a rule-disabled individual value (v.disabled, stamped by the SDK
            // from the attribute's disabledValueIds) - distinct from view.hidden,
            // which build() drops from the layout entirely before this ever runs.
            radio.disabled = readOnly || !!ctrl.isDisabled || !!v.disabled;
            // The same call either way: ConfigurableListAttributeController
            // TOGGLES membership for a multiple attribute and replaces for a
            // single one, which is why the SDK's selectOption has no branch.
            radio.addEventListener('change', function () {
                // Belt and suspenders: a real native `disabled` radio already
                // can't fire `change` from user interaction, unlike the
                // dropdown's <li> or the slider's single range, which both
                // need this re-check for real (no native enforcement there).
                // The one gap this closes is the settle window between a
                // rules pass flipping v.disabled and the next rebuild
                // reflecting it on this still-live DOM node - panelSignature
                // folding v.disabled in (two rounds ago) already keeps that
                // window near zero, not exactly zero.
                if (radio.disabled) {
                    return;
                }
                ctrl.markAsDirty();
                ctrl.setValue(radio.value);
            });
            option.appendChild(radio);
            // Never a disabled radio: .focus() on one is a silent no-op, and
            // the rebuild-focus-restore below (focusCid === view.cid) would
            // otherwise call it on a value a rule disabled out from under a
            // shopper who had it focused, dropping their keyboard position
            // instead of landing on the next real tab stop in this group.
            if (!radio.disabled && (radio.checked || !focusable)) {
                focusable = radio; // the checked one wins once it turns up
            }

            // Only when there is an image to show. Every value in this
            // catalogue has thumbnailUrl null, and the vendor's tile draws a
            // grey placeholder box — a column of those is worse than none.
            var thumb = v.thumbnailUrl || v.thumbnail_url || (kind === 'list' && /wood/i.test((view && view.name) || '') ? woodPhoto(v) : '');
            // Branded controls: a plain list draws the preview too when the
            // value has one, so wood species render as material cards.
            if ((kind === 'tiles' || kind === 'list') && thumb) {
                var preview = document.createElement('span');
                preview.className = 'expivi-options__preview';
                // The wood photos all show vertical grain. A "Horizontal Grain"
                // value gets a modifier so the CSS turns its photo 90deg.
                if (/horizontal\s*grain/i.test(valueLabel(v))) {
                    preview.classList.add('expivi-options__preview--horizontal-grain');
                }
                // Reused across rebuilds, keyed by the value's own cid, rather
                // than created fresh every time: the real cause of the blink
                // was not slow loading (neither loading="lazy" nor forcing
                // decoding="sync" stopped it — the image CDN is already
                // cache-control'd for 31 days and this one is 9.4 KB) but
                // that the SDK's rules engine can settle the store a second
                // time well after the click (measured live: a second full
                // rebuild, same panel, ~1.2s after the first — see
                // panelSignature's own comment on "~34 rebuilds" from one
                // selection). build() wipes and recreates the whole panel on
                // every one of those, so even an UNCHANGED thumbnail got torn
                // down and reinserted each time. Moving the existing node is
                // a no-op visually; appendChild on a node already in the
                // document just relocates it.
                //
                // v.cid alone, not view.cid + URL: a cid is scoped to
                // (attributeCid, value id) and the store reuses it on every
                // refetch of the same value (attribute-value-data.slice.js
                // `add()` - "update existing value, keeping its cid"), so
                // it's both stable across rebuilds and unique per value. A
                // URL-based key does neither: "Custom Color Match" reuses the
                // identical thumbnail across every colour attribute on this
                // product (chimney, apron, ...), which collided on URL alone,
                // and two different values in the SAME attribute sharing one
                // thumbnail would have collided on view.cid + URL the same
                // way - either collision steals the one cached node for
                // whichever tile builds first, leaving the other's preview
                // empty. src is re-read every render rather than only on
                // creation, so a URL change for the same cid can't go stale.
                var thumbKey = v.cid;
                var img = thumbImgs[thumbKey];
                if (!img) {
                    img = document.createElement('img');
                    img.alt = '';
                    // Safe now that the node is reused rather than recreated
                    // every rebuild (the original removal, 5a23bb1's own
                    // history: a FRESH node on every rebuild + lazy sat blank
                    // mid-evaluation). This only ever runs once per value, on
                    // its first appearance - later rebuilds relocate the same,
                    // already-loaded node, which lazy can't re-blank. Matches
                    // xpv-chip's own thumbnail markup, and this catalogue can
                    // run to ~133 swatches once more of them carry real
                    // thumbnails than just "Custom Color Match" does today.
                    img.loading = 'lazy';
                    thumbImgs[thumbKey] = img;
                }
                img.src = thumb;
                preview.appendChild(img);
                option.appendChild(preview);
            }

            var text = document.createElement('span');
            text.className = 'expivi-options__label';
            text.textContent = valueLabel(v);
            text.title = text.textContent; // the CSS ellipses it; hover shows the rest
            option.appendChild(text);

            var price = showPrice === false ? 0 : optionPrice(v);
            if (price) {
                var amount = document.createElement('span');
                amount.className = 'expivi-options__price';
                amount.textContent = '+ ' + money.format(price);
                option.appendChild(amount);
            }

            // The merchant's note on this VALUE. Expivi hangs it off an
            // `xpv-popover` on desktop and an info button + modal on mobile
            // (`xpv-list`), a `title` on `xpv-toggle-buttons`, an inline span on
            // `xpv-tiles`. Inline for all three here: a popover is the one
            // presentation that shows nothing on a phone unless you also build
            // the modal, and the text is the point, not the chrome.
            //
            // `title` as well, because the row ellipses under narrow panels.
            if (v.description) {
                var info = document.createElement('span');
                info.className = 'expivi-options__info';
                info.appendChild(valuePopover(v.description));
                option.appendChild(info);
            }

            group.appendChild(option);
        });

        return { el: group, focusable: focusable };
    }

    /**
     * A single-select attribute's own dropdown: a trigger button plus a
     * `<ul role="listbox">` panel, positioned with plain CSS (`position:
     * absolute` under the wrapper's `position: relative`) rather than the
     * vendor's JS-computed `position: fixed`.
     *
     * That is the whole reason this exists instead of the vendor's
     * `xpv-dropdown-selector`, which draws the same shape — a closed control
     * with the paginator INSIDE the open panel — but never opened here even in
     * total isolation (a bare instance appended straight to `document.body`,
     * no theme CSS at all, still didn't respond to a click). Whatever it needs
     * lives in the vendor's app shell (`ui-composer`), not in the standalone
     * `ui-kit` loader we can vendor. See docs/expivi-colour-dropdown-
     * paging.md's 2026-09-17 note. `position: absolute` in normal flow has no
     * such dependency: it moves with whatever scrolls it, by construction.
     *
     * MULTIPLE stays on the native `<select multiple>` — see that branch's own
     * comment for why a single `selectedId` can't carry several picks.
     */
    function valueDropdown(view, ctrl, opts) {
        var wrap = document.createElement('div');
        wrap.className = 'expivi-dropdown';

        var listId = 'expivi-dropdown-list-' + view.cid;
        var selected = opts.values.filter(function (v) {
            return opts.selectedCids.indexOf(v.cid) !== -1;
        })[0];

        var trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'expivi-dropdown__trigger';
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', listId);
        trigger.disabled = !!opts.disabled;
        var valueText = document.createElement('span');
        valueText.className = 'expivi-dropdown__value';
        // Branded controls: label and price in separate spans so the price
        // can sit in its own right-hand column. Same text as optionLabel().
        valueText.textContent = selected ? valueLabel(selected) : 'Select an option';
        trigger.appendChild(valueText);
        var selectedPrice = selected && opts.showPriceAdjustment !== false ? optionPrice(selected) : 0;
        if (selectedPrice) {
            var triggerPrice = document.createElement('span');
            triggerPrice.className = 'expivi-dropdown__price';
            triggerPrice.textContent = '+ ' + opts.money.format(selectedPrice);
            trigger.appendChild(triggerPrice);
        }

        var panel = document.createElement('ul');
        panel.id = listId;
        panel.className = 'expivi-dropdown__panel';
        panel.setAttribute('role', 'listbox');
        // Reopened across a rebuild that happens while it's open (a background
        // page load elsewhere calls build() same as everything else does) —
        // otherwise a shopper mid-scroll on "Load More" watches the panel
        // snap shut under them. pageState is the same object valueLoadMore
        // already keys by attribute and that survives across rebuilds.
        var isOpen = !!opts.state.open;
        panel.hidden = !isOpen;
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        // How far down the panel the shopper has scrolled, on the same state
        // object as `open` and for the same reason: a rebuild destroys this
        // panel and builds a new one, and a new element's scrollTop is 0.
        //
        // That is the "Load More sends me back to the first item" report. The
        // panel is the scroller — max-height 280px, overflow-y auto — so the
        // shopper scrolls 40 colours down INSIDE it, clicks Load More at the
        // bottom, and the replacement panel opens at the top. Nothing scrolled
        // them; the box they were scrolling no longer exists.
        //
        // Recorded on every scroll rather than read at teardown, because by the
        // time build() empties the container this element is already gone.
        panel.addEventListener('scroll', function () {
            opts.state.scrollTop = panel.scrollTop;
        });

        function onDocClick(e) {
            // `e.isTrusted` rather than a click straight from the shopper:
            // `hs-product-details.liquid` delegates a 'click' listener on the
            // WHOLE options container that calls `thumb3d.click()` on every
            // click inside it (see its own comment — "touching the panel
            // shows the render"). That fires a SECOND, synthetic click that
            // bubbles to `document` mid-dispatch of the first — clicking this
            // trigger, or the Load More button inside the panel, both count —
            // and its target (a gallery thumbnail) is never inside `wrap`, so
            // an untrusted-blind check reads it as "clicked outside" and
            // closes the panel the shopper is still using. A programmatic
            // `.click()` is always untrusted; a shopper's never is.
            if (e.isTrusted && !wrap.contains(e.target)) {
                close();
            }
        }
        function onDocKeydown(e) {
            if (e.key === 'Escape') {
                close();
                trigger.focus();
            }
        }
        // A rebuild while open (Load More, any background page load) replaces
        // this entire panel — container.textContent = '' detaches `wrap`, but
        // nothing ever called close() on THIS instance to remove its document
        // listeners first. Left alone, `onDocClick` keeps watching every future
        // click forever: `wrap.contains(e.target)` on a detached wrap is
        // always false, so it reads the NEXT real click anywhere as "outside"
        // and calls its OWN close() — which sets the SHARED `opts.state.open`
        // (the same pageState object every rebuild reads) to false. Click
        // "Load More" and that stale listener can flip `state.open` to false
        // during the very click that triggers the reload, before the reload's
        // own rebuild ever reads it — so the freshly rebuilt panel opens
        // already marked closed. `state.detachDocListeners` is the one thing
        // that survives the rebuild, so each new instance tears down
        // whatever the previous one left attached before adding its own.
        function attachDocListeners() {
            if (opts.state.detachDocListeners) {
                opts.state.detachDocListeners();
            }
            document.addEventListener('click', onDocClick, true);
            document.addEventListener('keydown', onDocKeydown, true);
            opts.state.detachDocListeners = function () {
                document.removeEventListener('click', onDocClick, true);
                document.removeEventListener('keydown', onDocKeydown, true);
                opts.state.detachDocListeners = null;
            };
        }
        function open() {
            isOpen = true;
            opts.state.open = true;
            panel.hidden = false;
            trigger.setAttribute('aria-expanded', 'true');
            attachDocListeners();
        }
        function close() {
            isOpen = false;
            opts.state.open = false;
            panel.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
            if (opts.state.detachDocListeners) {
                opts.state.detachDocListeners();
            }
        }
        if (isOpen) {
            // Rebuilt already open: the listeners a fresh open() would attach
            // never ran for this instance.
            attachDocListeners();
        }
        trigger.addEventListener('click', function () {
            if (opts.disabled) {
                return;
            }
            if (isOpen) {
                close();
            } else {
                open();
            }
        });

        opts.values.forEach(function (v) {
            var li = document.createElement('li');
            li.className = 'expivi-dropdown__option';
            li.setAttribute('role', 'option');
            var isSelected = opts.selectedCids.indexOf(v.cid) !== -1;
            li.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            if (isSelected) {
                li.classList.add('expivi-dropdown__option--selected');
            }
            // Branded controls: check · label · price, each its own span.
            var check = document.createElement('span');
            check.className = 'expivi-dropdown__check';
            check.setAttribute('aria-hidden', 'true');
            li.appendChild(check);
            var label = document.createElement('span');
            label.className = 'expivi-dropdown__label';
            label.textContent = valueLabel(v);
            li.appendChild(label);
            var optPrice = opts.showPriceAdjustment === false ? 0 : optionPrice(v);
            if (optPrice) {
                var optPriceEl = document.createElement('span');
                optPriceEl.className = 'expivi-dropdown__price';
                optPriceEl.textContent = '+ ' + opts.money.format(optPrice);
                li.appendChild(optPriceEl);
            }
            if (v.description) {
                li.title = v.description;
            }
            // A rule can disable one value without disabling the whole
            // attribute (the trigger's own `opts.disabled` only covers the
            // latter) - stays listed, keeps its selection, just not pickable.
            if (v.disabled) {
                li.classList.add('expivi-dropdown__option--disabled');
                li.setAttribute('aria-disabled', 'true');
            }
            li.addEventListener('click', function () {
                if (v.disabled) {
                    return;
                }
                // Closed BEFORE setValue, not after: setValue can rebuild
                // this whole panel synchronously (attributes.subscribe(build)),
                // and a rebuild reads opts.state.open to decide whether the
                // FRESH instance starts open. Closing first makes that read
                // false; closing after leaves the new instance open with its
                // own listeners attached, and this stale one closing behind
                // it never reaches them.
                close();
                ctrl.markAsDirty();
                ctrl.setValue(v.cid);
            });
            panel.appendChild(li);
        });

        // Same call, same one-request-per-page guard as the native <select>'s
        // Load More — the difference is only WHERE it can be appended. A
        // <select> has no inside; this panel is a real scrolling <ul>, so the
        // paginator sits in it exactly like the admin preview's does.
        //
        // Colour gating changes what "complete" means: `opts.values` here is
        // already filtered down to the allowed SKUs, so once every one of
        // them has turned up in a loaded page, later pages can only ever
        // hold colours this shopper may not see. Fetching them anyway to
        // announce "Showing 40 of 307" under a 12-swatch panel is not a
        // paginator earning its keep, it's noise — so gating that has found
        // everything it is looking for skips valueLoadMore entirely, same as
        // a genuinely single-page attribute would.
        // + the two always-offered values (Raw, Custom Color Match), which are
        // not in the allow-list but may sit on any page. If an attribute lacks
        // one, this never completes and the paginator stays - the safe side.
        var allowedCount = opts.allowedColors ? Object.keys(opts.allowedColors).length + H.ALWAYS_SHOWN_COUNT : null;
        // A small catalog (<= AUTO_PAGE_MAX_COLORS colours) is walked page by
        // page automatically rather than behind a Load More button.
        var autoPage = !!opts.allowedColors
            && Object.keys(opts.allowedColors).length <= AUTO_PAGE_MAX_COLORS;
        var more = (allowedCount !== null && uniqueByCid(opts.values).length >= allowedCount)
            ? null
            // hideTotal (last arg): "Showing 40 of 307" counts against
            // Expivi's whole catalogue, which this gated shopper can never
            // reach - see valueLoadMore's own comment on hideTotal.
            : valueLoadMore(view, ctrl, opts.state, panel, autoPage, !!opts.allowedColors);
        if (more) {
            var moreItem = document.createElement('li');
            moreItem.className = 'expivi-dropdown__more';
            moreItem.appendChild(more);
            panel.appendChild(moreItem);
        }

        wrap.appendChild(trigger);
        wrap.appendChild(panel);
        return { el: wrap, trigger: trigger };
    }

    /**
     * The value page the store currently holds for an attribute: which page,
     * how many, what search and sort produced it. Everything paging, searching
     * and sorting reads its state from here rather than keeping its own copy,
     * because the store is what `loadValuePage` actually changes.
     */
    function pageConfig(view) {
        return (view && view.valuePage && view.valuePage.config) || null;
    }

    /**
     * What makes the loaded pages a DIFFERENT list: the query, the sort and the
     * category filter. accumulateValues resets on it, and panelSignature
     * includes it so that reset always gets a build to happen in.
     *
     * One definition rather than two, because the two drifting apart is exactly
     * how the accumulator ends up rendering a list the store has moved on from.
     */
    function pageSignature(view, ctrl) {
        var cfg = pageConfig(view) || {};
        var sort = cfg.sort || {};
        return [
            pageQuery(view),
            sort.field + ':' + sort.order,
            ((ctrl && ctrl.selectedCategoryIds) || cfg.categoryIds || []).join(','),
        ].join('|');
    }

    /** The search term the store's current page was built with, or ''. */
    function pageQuery(view) {
        var cfg = pageConfig(view);
        var search = (cfg && cfg.search) || [];
        for (var i = 0; i < search.length; i++) {
            if (search[i] && search[i].field === 'name') {
                return String(search[i].value || '');
            }
        }
        return '';
    }

    /**
     * The search box and A–Z / Z–A sort Expivi's own panel draws above a value
     * list when the merchant ticks `searchable` / `sortable` — both per
     * attribute in `displaySettings`, see noteDisplay.
     *
     * SERVER-SIDE, exactly as the SDK does it: `xpv-list` wires its search and
     * sort to `pageHelpers`, which calls
     * `controller.loadValuePage({search: [{field: 'name', value}]})` and
     * `({sort: {field: 'name', order}})`. That matters because `ctrl.values` is
     * ONE PAGE of values, not all of them — a client-side filter over the
     * rendered radios silently searches only what page 1 happened to contain,
     * and a search box that quietly misses matches is worse than none.
     *
     * The sort select binds to `pageConfig.sort.order` and nothing else, which
     * is why Expivi's own panel can show "A–Z" over a list in catalogue order:
     * the page is sorted by `position` until the shopper picks, and the control
     * reports the ORDER, not the field. Matched here rather than second-guessed
     * — re-sorting a merchant's option order on load is not ours to do.
     *
     * 300ms debounce on input, the same as `xpv-search`.
     */
    function valueTools(view, ctrl, ds, state) {
        var bar = document.createElement('div');
        bar.className = 'expivi-options__tools';
        var cfg = pageConfig(view) || {};

        function load(patch) {
            if (typeof ctrl.loadValuePage !== 'function') {
                return; // older bundle: no paged value API to call
            }
            Promise.resolve(ctrl.loadValuePage(patch)).catch(function (error) {
                // eslint-disable-next-line no-console
                console.warn('[expivi] value page request failed for "'
                    + (view.name || view.identifier) + '": ' + (error && error.message));
            });
        }

        if (ds.searchable) {
            var search = document.createElement('input');
            search.type = 'search';
            search.className = 'expivi-options__search';
            search.placeholder = 'Search…';
            search.maxLength = 999;
            search.value = pageQuery(view);
            search.setAttribute('aria-label', 'Search options');
            // Marked so build() can put the cursor back: every accepted
            // keystroke reloads the page, which rebuilds the whole panel.
            search.dataset.expiviSearchCid = view.cid;
            search.addEventListener('input', function () {
                var q = search.value;
                clearTimeout(state.timer);
                state.timer = setTimeout(function () {
                    load({ search: q ? [{ field: 'name', value: q }] : [] });
                }, 300);
            });
            bar.appendChild(search);
        }

        if (ds.sortable) {
            var order = (cfg.sort && cfg.sort.order) || 'asc';
            var sort = document.createElement('select');
            sort.className = 'expivi-options__sort';
            sort.setAttribute('aria-label', 'Sort options');
            [['asc', 'A–Z'], ['desc', 'Z–A']].forEach(function (o) {
                var opt = document.createElement('option');
                opt.value = o[0];
                opt.textContent = o[1];
                opt.selected = o[0] === order;
                sort.appendChild(opt);
            });
            sort.addEventListener('change', function () {
                load({ sort: { field: 'name', order: sort.value } });
            });
            bar.appendChild(sort);
        }

        return bar;
    }

    /**
     * Every value loaded so far for one attribute, in the order the pages
     * arrived, deduped by cid.
     *
     * Kept here rather than read off `controller.allValues`, which looks like
     * exactly this and is not. Measured against the live catalogue:
     *
     *   paging 1 -> 2 -> 3      allValues 40 -> 80 -> 120   (accumulates, dedupes)
     *   re-loading page 1       allValues 120               (no duplicates)
     *   searching "clover"      values 1, allValues 120     (does NOT reset)
     *   sorting by name         allValues 120 -> 153        (appends a re-ordered page)
     *
     * So after a search it still holds everything fetched before it, and after
     * a sort it is a union of two orderings. Rendering it directly would answer
     * a search for "clover" with 120 unrelated colours.
     *
     * This resets whenever the query, the sort or the category filter changes —
     * anything that makes the already-fetched pages a different list.
     */
    function accumulateValues(view, ctrl, state) {
        var cfg = pageConfig(view) || {};

        // Only a genuinely multi-page attribute (colour catalogues) has
        // anything to accumulate ACROSS builds. Everything else is a single
        // page that the store can swap out wholesale — e.g. a rule engine
        // narrowing "Height"/"Width" to match a newly-picked "Size" — and
        // pageSignature only changes on search/sort/category, none of which
        // fired here. Accumulating anyway left the values a rule just
        // removed sitting in the dropdown alongside the new ones instead of
        // being replaced by them.
        if (!(cfg.lastPage > 1)) {
            state.signature = null;
            state.seen = {};
            state.values = (ctrl.values || []).slice();
            state.requested = cfg.page || 1;
            return state.values;
        }

        var signature = pageSignature(view, ctrl);

        if (state.signature !== signature) {
            state.signature = signature;
            state.seen = {};
            state.values = [];
            // The in-flight page cursor belongs to the accumulation, so it has
            // to reset with it. Left behind, valueLoadMore's `requested >= next`
            // guard refuses the new list's page 2: page to 3, search, clear, and
            // Load More is a dead button because `requested` is still 3.
            state.requested = (cfg.page || 1);
        }
        (ctrl.values || []).forEach(function (v) {
            if (v && v.cid && !state.seen[v.cid]) {
                state.seen[v.cid] = true;
                state.values.push(v);
            }
        });
        return state.values;
    }

    /**
     * "Load More", then auto-load on scroll — `xpv-infinite-scroll`, which is
     * what the vendor draws under a paged list. This replaced a row of numbered
     * page buttons, which was the wrong shape twice over: it REPLACED the
     * rendered page instead of extending it, so scrolling back up through a
     * list that had been paged showed nothing.
     *
     * The vendor's shape, worth stating because it is not obvious: the button
     * is shown ONCE. Clicking it sets `isAutoLoadingActive`, after which the
     * button is replaced by a sentinel watched by an IntersectionObserver and
     * every further page loads as the shopper scrolls it into view. The shopper
     * opts into infinite scroll rather than getting it uninvited.
     *
     * Nothing fires scrolling back up: the observer only watches the sentinel
     * at the bottom, and the earlier pages are still on screen because
     * accumulateValues kept them.
     *
     * `autoDrain` (native `<select>` only — see docs/expivi-colour-dropdown-
     * paging.md, Approach 3): a button can only sit under the CLOSED select,
     * open/close/click/reopen to reach page 2. So instead of waiting for that
     * click, walk every remaining page on its own with the same
     * `loadValuePage` call, same one-request-per-page guard. A failed page
     * sets `state.failed` and drops to the manual button below — a stalled
     * auto-load must not turn into a wall the shopper can't get past.
     *
     * `hideTotal`: drops the "Showing 40 of 307" / "Loading 40 of 307…" text,
     * keeping the button/sentinel (paging still works, colours past page 1
     * still load) but not the number. Requested for a colour-gated dropdown:
     * the 307 is Expivi's WHOLE catalogue, not what this shopper's allow-list
     * lets them reach, so the count is just wrong for them - commented out
     * this way rather than deleted so it is a one-flag flip back on if the
     * client asks for a count later (one that means something, presumably
     * against the allow-list size instead of cfg.total).
     */
    function valueLoadMore(view, ctrl, state, scrollBox, autoDrain, hideTotal) {
        var cfg = pageConfig(view);
        if (!cfg || !(cfg.page < cfg.lastPage) || typeof ctrl.loadValuePage !== 'function') {
            return null;
        }

        var host = document.createElement('div');
        host.className = 'expivi-more';

        function load() {
            var next = cfg.page + 1;
            // One request per page however many times the observer fires while
            // the response is in flight — the sentinel stays on screen until the
            // new rows push it down, so it keeps re-triggering on its own.
            if (state.requested >= next) {
                return;
            }
            state.requested = next;
            Promise.resolve(ctrl.loadValuePage({ page: next })).catch(function (error) {
                state.requested = cfg.page;
                state.failed = true;
                // eslint-disable-next-line no-console
                console.warn('[expivi] could not load page ' + next + ' of "'
                    + (view.name || view.identifier) + '": ' + (error && error.message));
            });
        }

        if (autoDrain && !state.failed) {
            load();
            if (!hideTotal) {
                var status = document.createElement('span');
                status.className = 'expivi-more__count';
                status.textContent = 'Loading ' + Math.min(cfg.page * cfg.perPage, cfg.total)
                    + ' of ' + cfg.total + '…';
                host.appendChild(status);
            }
            return host;
        }

        if (!state.auto) {
            var button = document.createElement('button');
            button.type = 'button';
            button.className = 'expivi-more__button';
            button.textContent = 'Load More';
            button.dataset.expiviMoreCid = view.cid;
            button.addEventListener('click', function () {
                state.auto = true;
                load();
            });
            host.appendChild(button);
        } else {
            var sentinel = document.createElement('div');
            sentinel.className = 'expivi-more__sentinel';
            // Always "Loading" (dots animated in CSS): this node only exists
            // while pages remain, and an empty string between two pages made
            // it blink on every rebuild.
            sentinel.textContent = 'Loading';
            host.appendChild(sentinel);

            // Without IntersectionObserver (old Safari, any headless check) the
            // sentinel becomes a button again rather than a dead div the shopper
            // can never trigger.
            if (typeof IntersectionObserver !== 'function') {
                sentinel.className = 'expivi-more__button';
                sentinel.textContent = 'Load More';
                sentinel.addEventListener('click', load);
            } else {
                var observer = new IntersectionObserver(function (entries) {
                    if (entries[0] && entries[0].isIntersecting) {
                        load();
                    }
                }, {
                    // The option list scrolls inside its own box when it has a
                    // max-height, and against the page when it does not.
                    // The caller says what scrolls; do NOT re-decide it by
                    // measuring. The dropdown panel is `hidden` when build()
                    // runs, so its scrollHeight and clientHeight are both 0 —
                    // the old `scrollHeight > clientHeight` test read that as
                    // "not a scroller" and fell back to the viewport. The panel
                    // then sits below the fold on most screens, so the sentinel
                    // never intersected and auto-loading never fired at all:
                    // the shopper scrolled to the bottom of the list and nothing
                    // happened. `xpv-infinite-scroll` passes its rootId
                    // unconditionally for exactly this reason.
                    root: scrollBox || null,
                    rootMargin: '10px',
                    threshold: 0.1,
                });
                observer.observe(sentinel);
                // build() throws the container away on the next rebuild, and an
                // observer still holding the detached sentinel keeps it alive.
                state.observer = observer;
            }
        }

        // The count is what tells a shopper the list is not the whole list -
        // suppressed by hideTotal, see this function's own comment.
        if (!hideTotal) {
            var count = document.createElement('span');
            count.className = 'expivi-more__count';
            count.textContent = 'Showing ' + Math.min(cfg.page * cfg.perPage, cfg.total)
                + ' of ' + cfg.total;
            host.appendChild(count);
        }

        return host;
    }

    /**
     * The bounds a ConfigurableNumber was configured with, resolved exactly the
     * way `xpv-number-input.updateState` resolves them:
     *
     *   min  = settings.min            ?? configuredValue[0].min
     *   max  = settings.max            ?? configuredValue[0].max
     *   step = settings.step           ?? configuredValue[0].step
     *   dp   = settings.decimal_places ?? configuredValue[0].decimalPlaces ?? 0
     *
     * Attribute settings win over the configured value because settings is
     * where a merchant edits them; the value carries the catalogue's own
     * defaults. Both were ignored here, so a number field a merchant capped at
     * 48 accepted 4800 and the store rejected it a round-trip later.
     *
     * Missing bounds come back null rather than the SDK's {0, Infinity}: this
     * feeds HTML attributes, and `min="0"` on a field the merchant left open is
     * a constraint nobody asked for.
     */
    function numberConfig(view) {
        var settings = (view && view.settings) || {};
        var value = ((view && view.configuredValue) || [])[0] || {};

        function pick(a, b) {
            return a != null ? a : (b != null ? b : null);
        }

        var decimals = pick(settings.decimal_places, value.decimalPlaces);
        var step = pick(settings.step, value.step);
        // No explicit step but decimals allowed: the native input would default
        // to step=1 and call "12.5" a step mismatch on a field built to take it.
        if (step == null && decimals > 0) {
            step = Number(Math.pow(10, -decimals).toFixed(decimals));
        }
        return {
            min: pick(settings.min, value.min),
            max: pick(settings.max, value.max),
            step: step,
            decimals: decimals == null ? 0 : decimals,
        };
    }

    /** Clamp to whichever bounds exist; a null bound is not a bound. */
    function clampToConfig(n, config) {
        if (config.min != null && n < config.min) {
            return config.min;
        }
        if (config.max != null && n > config.max) {
            return config.max;
        }
        return n;
    }

    /**
     * The "0 / 60" counter and clear button `xpv-text-field` draws for a string
     * attribute (`showCounter` and `showClearButton`, both true in
     * `xpv-string-input`). Without the counter a maxlength is a wall the
     * shopper walks into mid-word with no warning that it was coming.
     */
    function textCounter(input, max, ctrl) {
        var row = document.createElement('div');
        row.className = 'expivi-configurator__counter';

        var count = document.createElement('span');
        function paint() {
            count.textContent = input.value.length + ' / ' + max;
        }
        paint();
        input.addEventListener('input', paint);
        row.appendChild(count);

        var clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'expivi-configurator__clear';
        clear.textContent = 'Clear';
        clear.addEventListener('click', function () {
            input.value = '';
            paint();
            ctrl.markAsDirty();
            ctrl.setValue('');
            input.focus();
        });
        row.appendChild(clear);

        return row;
    }

    /**
     * Shopper-facing category chips above a value list, drawn when the merchant
     * ticks `enableCategoryFilter`. Ported from `xpv-category-filter`: chips
     * from `controller.loadAvailableCategories()`, selection from
     * `controller.selectedCategoryIds`, click straight into
     * `controller.setCategoryFilter(id)`.
     *
     * A bare id TOGGLES — `resolveCategoryFilterInput` adds it when absent and
     * drops it when present — so one call is both select and deselect. An array
     * replaces and null clears, which is what the company gate below uses.
     *
     * `TOP_LEVEL_CHIP_LIMIT` is 5 in the vendor component, with the overflow
     * behind a "View all" that opens an `xpv-category-hierarchy` panel. Same
     * limit here, but "View all" just reveals the rest inline.
     * ponytail: flat list, no hierarchy panel — these categories are one level
     * deep in this catalogue. Build the tree when a nested one turns up.
     *
     * `state` is the per-attribute cache in renderOptions: the fetch is async
     * and build() is not, so the first pass draws nothing, the response is
     * stored, and `onLoad` asks for one more build.
     */
    var TOP_LEVEL_CHIP_LIMIT = 5;

    function categoryFilter(view, ctrl, state, onLoad) {
        if (typeof ctrl.loadAvailableCategories !== 'function') {
            return null; // older bundle: no categories to offer
        }
        if (!state.status) {
            state.status = 'loading';
            ctrl.loadAvailableCategories().then(function (list) {
                state.status = 'ready';
                // `/view/value-categories` is catalogue-wide, not scoped to this
                // attribute: it also returns categories built for other steps
                // (height groupings, product-line groupings). A category only
                // belongs here when its own attributeIds names this attribute —
                // an empty attributeIds is "applies to nothing", not "applies to
                // everything", so it is dropped too.
                state.list = (list || []).filter(function (category) {
                    return (category.attributeIds || []).indexOf(view.id) !== -1;
                });
                // The catalogue returned categories, but none named this attribute -
                // indistinguishable, from here, between "genuinely none apply" and a
                // silent type/space mismatch (attributeIds holding a different id
                // shape than view.id, say). Only this one load per attribute
                // (gated by state.status above), so a plain warn costs nothing.
                if ((list || []).length && !state.list.length) {
                    // eslint-disable-next-line no-console
                    console.warn('[expivi] "' + (view.name || view.identifier) + '" has '
                        + 'enableCategoryFilter set, but none of the ' + list.length + ' '
                        + 'categories the catalogue returned name it in their attributeIds - '
                        + 'check the Categories mapping, or whether attributeIds and view.id '
                        + 'disagree on id vs cid.');
                }
                onLoad();
            }, function (error) {
                state.status = 'ready';
                state.list = [];
                // eslint-disable-next-line no-console
                console.warn('[expivi] could not load categories for "'
                    + (view.name || view.identifier) + '": ' + (error && error.message));
                onLoad();
            });
        }
        if (state.status !== 'ready' || !state.list.length) {
            return null;
        }

        var selected = ctrl.selectedCategoryIds || [];
        var bar = document.createElement('div');
        bar.className = 'expivi-categories';
        bar.setAttribute('role', 'group');
        bar.setAttribute('aria-label', 'Filter options by category');

        var hidden = [];
        state.list.forEach(function (category, i) {
            var on = selected.indexOf(category.id) !== -1;
            var label = category.name || category.identifier || '';
            var chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'expivi-categories__chip'
                + (on ? ' expivi-categories__chip--on' : '');
            chip.setAttribute('aria-pressed', on ? 'true' : 'false');

            // A palette thumbnail, which is most of what makes a colour category
            // recognisable. `xpv-chip` renders one whenever the category has a
            // thumbnailUrl; ours were text-only, so "Artisan" and "Heritage"
            // looked like two words rather than two palettes.
            if (category.thumbnailUrl) {
                var thumb = document.createElement('img');
                thumb.className = 'expivi-categories__thumb';
                thumb.src = category.thumbnailUrl;
                thumb.alt = '';
                thumb.loading = 'lazy';
                chip.appendChild(thumb);
            }

            // Truncated at 30 with the full text on the element, the same as
            // `xpv-chip`'s MAX_LABEL_LENGTH. A chip is a fixed-width thing in a
            // wrapping row; one long category name otherwise pushes the rest of
            // the row off the panel.
            var text = document.createElement('span');
            text.className = 'expivi-categories__label';
            if (label.length > 30) {
                text.textContent = label.slice(0, 30) + '\u2026';
                chip.title = label;
                chip.setAttribute('aria-label', label);
            } else {
                text.textContent = label;
            }
            chip.appendChild(text);

            // `xpv-chip` puts the description in an xpv-popover, INSIDE the
            // <button>, between the label and the close icon \u2014 matched here.
            // The trigger has no click handler of its own (valuePopover
            // reveals on :hover/:focus-within, not click), so nesting it is
            // not really "a control inside a control"; stopPropagation still
            // guards a tap on mobile from also toggling the chip's filter.
            if (category.description) {
                var note = valuePopover(category.description);
                note.className += ' expivi-categories__info';
                note.addEventListener('click', function (event) {
                    event.stopPropagation();
                });
                chip.appendChild(note);
            }

            // The selected chip carries a clear affordance, so "click it again"
            // is visible rather than something a shopper has to guess.
            if (on) {
                var close = document.createElement('span');
                close.className = 'expivi-categories__close';
                close.setAttribute('aria-hidden', 'true');
                close.textContent = '\u00d7';
                chip.appendChild(close);
            }

            chip.addEventListener('click', function () {
                Promise.resolve(ctrl.setCategoryFilter(category.id)).catch(function (error) {
                    // eslint-disable-next-line no-console
                    console.warn('[expivi] category filter failed: ' + (error && error.message));
                });
            });
            // An overflow chip that is already ON must stay visible, or the
            // shopper cannot see (or clear) the filter they are looking at.
            if (i >= TOP_LEVEL_CHIP_LIMIT && !on) {
                chip.hidden = true;
                hidden.push(chip);
            }
            bar.appendChild(chip);
        });

        if (hidden.length) {
            var more = document.createElement('button');
            more.type = 'button';
            more.className = 'expivi-categories__more';
            more.textContent = 'View all';
            more.addEventListener('click', function () {
                hidden.forEach(function (c) {
                    c.hidden = false;
                });
                more.remove();
            });
            bar.appendChild(more);
        }

        return bar;
    }

    /**
     * `displayType: list_slider` — one option at a time, picked with a slider.
     * Ported from `xpv-list-slider`, which drives `xpv-option-slider` over the
     * option INDICES and shows the active option's label, price adjustment and
     * description beneath.
     *
     * A native <input type="range"> rather than a custom track: it already has
     * the drag, the arrow keys, Home/End, the touch target and the focus ring,
     * and `aria-valuetext` is what makes a screen reader read "Walnut" instead
     * of "3". The vendor renders a parallel <ul> listbox for the same reason;
     * valuetext is the shorter way to the same place.
     *
     * Was unhandled before: build() fell through to a <select>, so a merchant
     * who chose the slider got a dropdown and the warning said so. That is a
     * fair degradation, but it is not what they configured.
     */
    function valueSlider(view, ctrl, allowedColors, selectedCids, money, showPrice, loaded, offPage) {
        var wrap = document.createElement('div');
        wrap.className = 'expivi-slider';

        // Same concat as valueGroup. Without it a selection that paging has
        // taken off the list is not among the slider's stops at all, so
        // activeOptionIndex falls back to 0 and the readout names the FIRST
        // value while the store holds - and the price charges for - another one.
        // A list shows nothing checked in that state, which reads as a mistake;
        // a slider shows a confident wrong answer, which does not.
        var values = (offPage || []).concat(loaded || []).filter(function (v) {
            return !allowedColors || isAllowedColor(v, allowedColors);
        });
        if (!values.length) {
            return { el: wrap, focusable: null };
        }

        var index = 0;
        values.forEach(function (v, i) {
            if (selectedCids.indexOf(v.cid) !== -1) {
                index = i;
            }
        });

        var range = document.createElement('input');
        range.type = 'range';
        range.className = 'expivi-slider__range';
        range.min = '0';
        range.max = String(values.length - 1);
        range.step = '1';
        range.value = String(index);
        // ponytail: attribute-level only (isStatic / isDisabled). A single
        // <input type=range> has one disabled state, not one per stop, so a
        // rule disabling an individual value can't be expressed here - add a
        // stepped/segmented control if that combination turns up for real.
        range.disabled = !!ctrl.isStatic || !!ctrl.isDisabled;
        range.dataset.expiviAttrCid = view.cid;

        var readout = document.createElement('div');
        readout.className = 'expivi-slider__readout';
        var name = document.createElement('span');
        name.className = 'expivi-slider__value';
        var price = document.createElement('span');
        price.className = 'expivi-options__price';
        var note = document.createElement('span');
        note.className = 'expivi-options__description';
        readout.appendChild(name);
        readout.appendChild(price);
        readout.appendChild(note);

        function paint(i) {
            var v = values[i];
            name.textContent = valueLabel(v);
            var amount = showPrice === false ? 0 : optionPrice(v);
            price.textContent = amount ? '+ ' + money.format(amount) : '';
            note.textContent = v.description || '';
            // Without this the slider announces its index. "3 of 6" tells a
            // shopper who cannot see the readout nothing about what they picked.
            range.setAttribute('aria-valuetext', name.textContent);
        }
        paint(index);

        // `input` paints while dragging, `change` commits: setValue rebuilds the
        // whole panel, and doing that on every pixel of a drag would fight the
        // shopper's thumb for the control.
        range.addEventListener('input', function () {
            paint(Number(range.value));
        });
        range.addEventListener('change', function () {
            var v = values[Number(range.value)];
            // The whole-slider disable above (range.disabled) doesn't cover a
            // rule disabling just this one stop - same guard the other control
            // kinds apply per value, so a disabled stop can be dragged to and
            // read but never actually committed. Without the else, `input`'s
            // own paint() left the readout/price/thumb showing that disabled
            // stop's label as if it were selected while the store silently
            // kept the OLD value - a shopper reading "+ $1,670.00" for a
            // configuration they are not actually being charged for. Snap the
            // visible state back to what's actually committed (`index`, as of
            // this render) instead of leaving it on the rejected stop.
            if (v && !v.disabled) {
                ctrl.markAsDirty();
                ctrl.setValue(v.cid);
            } else {
                range.value = String(index);
                paint(index);
            }
        });

        wrap.appendChild(range);
        wrap.appendChild(readout);
        return { el: wrap, focusable: range };
    }

    /**
     * Tags and attributes a merchant's static text may keep. `xpv-static-text`
     * assigns `config.text` straight to innerHTML; this does not.
     *
     * The text comes from the Expivi back office, which is not the same trust
     * boundary as this theme's own source: it is edited by whoever holds a
     * catalogue login, it is served to every shopper, and a storefront is where
     * card details get typed. So the formatting a merchant actually writes is
     * kept and everything that executes is dropped — no <script>, no on*
     * handler, no javascript: URL, no <iframe>/<object>/<style>.
     *
     * ponytail: allow-list, not a full sanitiser. A tag not on this list loses
     * its markup and keeps its text, which is the safe direction to fail.
     */
    var STATIC_TEXT_TAGS = {
        A: true, B: true, BR: true, DIV: true, EM: true, I: true, LI: true,
        OL: true, P: true, SMALL: true, SPAN: true, STRONG: true, U: true, UL: true,
        H1: true, H2: true, H3: true, H4: true, H5: true, H6: true,
        // Merchants put pictures in rich text. The vendor renders them because
        // it renders everything; dropping them was a side effect of the
        // allow-list, not a decision. `src` is scheme-checked like `href`.
        IMG: true, FIGURE: true, FIGCAPTION: true,
    };
    var STATIC_TEXT_ATTRS = { href: true, src: true, alt: true, title: true, target: true, rel: true };
    // The two that fetch. Everything else on the list is inert text, and running
    // the scheme test over `title` only rejected a merchant writing the word
    // "javascript:" in a tooltip.
    var STATIC_TEXT_URL_ATTRS = { href: true, src: true };
    var STATIC_TEXT_DROP = {
        SCRIPT: true, STYLE: true, IFRAME: true, OBJECT: true, EMBED: true,
        LINK: true, META: true, FORM: true, INPUT: true, BUTTON: true,
    };

    /**
     * What to do with one tag: drop it and everything inside, unwrap it and
     * keep the words, or keep it. Split out from the DOM walk because this is
     * where the security decision lives and node has no DOM to test it in —
     * the walk below is plumbing, this is the rule.
     */
    function staticTagAction(tagName) {
        var tag = String(tagName || '').toUpperCase();
        if (STATIC_TEXT_DROP[tag]) {
            return 'drop';
        }
        return STATIC_TEXT_TAGS[tag] ? 'keep' : 'unwrap';
    }

    /**
     * Whether one attribute survives. Allow-listed name, and for the two that
     * fetch (`href`, `src`), a scheme that cannot execute.
     *
     * Whitespace and control characters are stripped before the scheme test: a
     * tab inside "java<tab>script:" is dropped by the URL parser, so the scheme
     * is still javascript: by the time it matters.
     *
     * ALL `data:` URLs are refused, deliberately and including images. It is
     * tempting to allow `data:image/...` since a merchant might paste one, but
     * `data:image/svg+xml` is an image by MIME type and a script host in
     * practice — an SVG can carry <script> and inline event handlers, and it
     * executes on the artifact's own origin. Distinguishing the safe image
     * types from that one means parsing the MIME out of an attacker-controlled
     * string, which is the kind of test that is wrong once and then wrong
     * forever. A merchant who wants an image has a URL for it: Expivi already
     * hosts every thumbnail in this catalogue on data.expivi.net.
     */
    function keepStaticAttr(name, value) {
        var key = String(name || '').toLowerCase();
        if (!STATIC_TEXT_ATTRS[key]) {
            return false;
        }
        if (!STATIC_TEXT_URL_ATTRS[key]) {
            return true;
        }
        var raw = String(value || '');
        var scheme = '';
        for (var i = 0; i < raw.length; i++) {
            if (raw.charCodeAt(i) > 32) {
                scheme += raw.charAt(i);
            }
        }
        scheme = scheme.toLowerCase();
        return scheme.indexOf('javascript:') !== 0
            && scheme.indexOf('data:') !== 0
            && scheme.indexOf('vbscript:') !== 0;
    }

    function sanitizeStaticHtml(html) {
        var host = document.createElement('div');
        host.innerHTML = String(html || '');

        // Snapshot first: the walk reparents nodes, and a live NodeList would
        // shift under it.
        [].slice.call(host.querySelectorAll('*')).forEach(function (el) {
            if (!el.parentNode) {
                return; // already removed with an ancestor
            }
            var action = staticTagAction(el.tagName);
            if (action === 'drop') {
                el.parentNode.removeChild(el);
                return;
            }
            if (action === 'unwrap') {
                // Keep the words, drop the element. `replaceWith(...childNodes)`
                // is not in the ES5 dialect the rest of this file uses.
                while (el.firstChild) {
                    el.parentNode.insertBefore(el.firstChild, el);
                }
                el.parentNode.removeChild(el);
                return;
            }
            [].slice.call(el.attributes).forEach(function (attr) {
                if (!keepStaticAttr(attr.name, attr.value)) {
                    el.removeAttribute(attr.name);
                }
            });
            if (el.tagName === 'A' && el.getAttribute('target') === '_blank') {
                el.setAttribute('rel', 'noopener noreferrer');
            }
        });
        return host.innerHTML;
    }

    /**
     * A `static_text` or `static_image` layout entity, rendered as the element
     * the merchant placed there. Both were on the not-ported list as "renders
     * nothing", which is the worst of the three options: the merchant sees
     * their paragraph in the back office preview and not on the storefront.
     */
    function staticBlock(entity) {
        var config = entity.config || {};
        var kind = STATIC_BLOCKS[entity.entityType];

        if (kind === 'group') {
            if (!config.name) {
                return null;
            }
            var heading = document.createElement('h3');
            heading.className = 'expivi-group-heading';
            heading.textContent = config.name;
            // ponytail: the heading, not the box. `xpv-static-grouped-attributes`
            // also wraps its children in a __content div, but the children are
            // already siblings in step order here and the label is the part a
            // shopper was missing — "Ventilation" over three vent options.
            // Nest them if a merchant ever needs the group to be visibly boxed.
            return heading;
        }

        if (kind === 'image') {
            if (!config.thumbnailUrl) {
                return null;
            }
            var settings = config.displaySettings || {};
            var img = document.createElement('img');
            img.className = 'expivi-static-image';
            img.src = config.thumbnailUrl;
            img.alt = config.name || '';
            img.loading = 'lazy';
            // The vendor component resolves percentage sizes against an
            // ancestor with a ResizeObserver. CSS does that on its own here —
            // the value is handed over untouched and the browser resolves it.
            if (settings.width) {
                img.style.width = String(settings.width);
            }
            if (settings.height) {
                img.style.height = String(settings.height);
            }
            if (settings.imageFit) {
                img.style.objectFit = String(settings.imageFit);
            }
            return img;
        }

        if (!config.text) {
            return null;
        }
        var block = document.createElement('div');
        block.className = 'expivi-static-text';
        block.innerHTML = sanitizeStaticHtml(config.text);
        return block;
    }

    /**
     * Configured values that are not on the page currently loaded.
     *
     * `loadValuePage` REPLACES `ctrl.values`, so the moment a shopper pages
     * past their own selection it vanishes from the options — the <select>
     * falls back to showing "Select an option" and a radiogroup shows nothing
     * checked, while the store still holds the value and the price still
     * charges for it. On this catalogue "Select Hood Color" has 307 values at
     * 40 a page, so page 2 was enough to trigger it.
     *
     * The SDK covers the same hole from two directions: `activeOption` falls
     * back to `allOptions` when the value is off-page, and
     * `getSelectableValueById` accepts a value that is "on current page or
     * already in configuredValue (off-page selection)".
     *
     * The two shapes differ and that is fine, checked against the live store: a
     * page value carries its label at `config.value`, a configuredValue entry
     * carries it at `value`, and valueLabel reads both — it already had the
     * `value` fallback for Static* attributes. `id` (valuePrice), `identifier`
     * (colour gating) and `cid` are present on both.
     *
     * One thing genuinely degrades: `thumbnailUrl` is "" on a configuredValue
     * entry where the page value has a real URL, so an off-page selection in a
     * `tiles` group renders without its picture. `allValues` does not reliably
     * hold it either (120 of 307 on this catalogue). A missing thumbnail on one
     * row beats a selection that looks unmade.
     */
    function offPageSelections(view, ctrl) {
        // Only a genuinely multi-page attribute has an "off-page" to fall
        // off of. On a single page, `ctrl.values` IS the whole list, so a
        // configured cid missing from it isn't sitting on some other page —
        // it is gone, most often because a rule engine swapped the list
        // wholesale (e.g. "Height" narrowed after "Size" changed). Pinning
        // it back in here is what made a value the shopper can no longer
        // pick still render as selected. staleSelectionReset is what
        // corrects the underlying selection for this same case.
        var cfg = pageConfig(view) || {};
        if (!(cfg.lastPage > 1)) {
            return [];
        }
        var onPage = {};
        (ctrl.values || []).forEach(function (v) {
            onPage[v.cid] = true;
        });
        return (view.configuredValue || []).filter(function (cv) {
            return cv && cv.cid && !onPage[cv.cid];
        });
    }

    /**
     * `values` with every cid kept once, order preserved. offPageSelections
     * above is already written to exclude anything its `shownValues`
     * argument holds, so an `offPage.concat(shownValues)` pair should never
     * actually have a duplicate - this is cheap insurance for the two gated
     * colour-catalogue completion checks built on exactly that concat
     * (valueGroup's Load More suppression and valueDropdown's own), not a
     * response to a confirmed live duplicate. Both used to dedupe this
     * independently (or not at all); shared here so they can't drift again.
     */
    function uniqueByCid(values) {
        var seen = {};
        return (values || []).filter(function (v) {
            if (!v || !v.cid || seen[v.cid]) {
                return false;
            }
            seen[v.cid] = true;
            return true;
        });
    }

    /**
     * '' when a single-page attribute's current selection has fallen out of
     * `ctrl.values` entirely — not paged past, genuinely gone (a rule engine
     * swapped the whole list, e.g. "Height" narrowed after a "Size" change)
     * — or null when there's nothing to correct. '' is not "no value", it's
     * the SAME thing setValue gets when a shopper manually picks the
     * "Select an option" placeholder (that option's native `<option value="">`
     * — see the dropdown branch below), because that placeholder is exactly
     * what a reset attribute must show: the same blank state as the very
     * first load, not some auto-picked value the shopper never chose.
     *
     * Mirrors correctedSelection's shape (used for colour-gating), which
     * moves to the gated list's first value instead - that stays a genuine
     * one-of-few narrowing among choices the shopper could still reasonably
     * want, not a whole list swapped out from under them.
     *
     * Multi-page attributes are left alone: offPageSelections already
     * covers a selection sitting on a page that just is not loaded yet,
     * and `ctrl.values` there is one page, not the whole list, so "missing"
     * doesn't mean "invalid".
     *
     * An empty `ctrl.values` is left alone too - indistinguishable here
     * from a value fetch still in flight, and clearing on a guess would
     * blank a selection that turns out to still be valid once it loads.
     *
     * `multiple` attributes are left alone too - setValue TOGGLES for
     * those rather than replacing, so "clear to ''" doesn't mean the same
     * thing. No case of a multi-select child list going stale has been
     * reported; if one turns up, it needs its own fix, not this one
     * stretched to fit.
     */
    function staleSelectionReset(view, ctrl) {
        if (ctrl.isMultiple) {
            return null;
        }
        var cfg = pageConfig(view) || {};
        if (cfg.lastPage > 1) {
            return null;
        }
        var current = (view.configuredValue || [])[0];
        if (!current || !current.cid) {
            return null;
        }
        var values = ctrl.values || [];
        if (!values.length) {
            return null;
        }
        var stillValid = values.some(function (v) {
            return v && v.cid === current.cid;
        });
        return stillValid ? null : '';
    }

    /**
     * Expivi's own info glyph, from `COMMON_ICONS.info` in the ui-kit bundle —
     * the same one the admin preview shows beside an option, so a merchant
     * comparing the two screens sees one control rather than two.
     */
    var INFO_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"'
        + ' viewBox="0 0 16 16" aria-hidden="true" focusable="false">'
        + '<path fill="currentColor" d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>'
        + '<path fill="currentColor" d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469'
        + 'l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416'
        + 'c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533z"/>'
        + '<path fill="currentColor" d="M9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/></svg>';

    /**
     * A value's description as the hover card Expivi draws for it, rather than
     * the line of grey text this used to append under every option.
     *
     * Ported from `xpv-popover`: an info icon revealing a white card with a 1px
     * border, 4px radius, a shadow and an arrow. Sizes taken from
     * theme.config.css `.xpv-popover__content` / `__arrow`, not eyeballed.
     *
     * CSS-only — revealed by `:hover` and `:focus-within`, positioned by the
     * stylesheet. The vendor ships a positioning engine because its popover can
     * flip sides; ours is always above the row, which is where the room is in a
     * ~320px storefront column.
     *
     * `:focus-within` on a tabbable trigger is what makes it work on a phone,
     * where there is no hover. That was the stated objection to a popover when
     * this was first written as inline text, and it costs two lines of CSS
     * rather than the info-button-plus-modal the vendor builds for the same case.
     */
    var popoverId = 0;

    function valuePopover(text) {
        var wrap = document.createElement('span');
        wrap.className = 'expivi-popover';

        var id = 'expivi-tip-' + (popoverId += 1);

        var trigger = document.createElement('span');
        trigger.className = 'expivi-popover__trigger';
        // Focusable so `:focus-within` can reveal the card — that is what makes
        // this work without hover, on a phone or a keyboard.
        trigger.tabIndex = 0;
        // NOT role="button". It was, and that was a promise the element did not
        // keep: a button says "press Enter or Space and something happens", and
        // nothing did. The description is revealed by focus itself, so the
        // honest markup is a described-by relationship — a screen reader then
        // reads the text on focus, which is the same moment a sighted keyboard
        // user sees the card.
        trigger.setAttribute('aria-describedby', id);
        trigger.innerHTML = INFO_ICON;
        wrap.appendChild(trigger);

        var content = document.createElement('span');
        content.className = 'expivi-popover__content';
        content.id = id;
        content.setAttribute('role', 'tooltip');
        // textContent, not innerHTML: this is merchant-authored, and
        // sanitizeStaticHtml is the only place in this file that puts merchant
        // markup on the page.
        content.textContent = text;
        var arrow = document.createElement('span');
        arrow.className = 'expivi-popover__arrow';
        content.appendChild(arrow);
        wrap.appendChild(content);

        return wrap;
    }

    /**
     * The fallback for when the browser has no validationMessage of its own —
     * which happens when the value is out of range but the input is not, e.g. a
     * merchant bound the field tighter than the DOM attributes express.
     *
     * Only names the bounds that exist. Interpolating both unconditionally
     * printed "Value must be between 12 and null" on a field with a floor and
     * no ceiling.
     */
    function boundsMessage(config) {
        if (config.min != null && config.max != null) {
            return 'Value must be between ' + config.min + ' and ' + config.max + '.';
        }
        if (config.min != null) {
            return 'Value must be ' + config.min + ' or more.';
        }
        if (config.max != null) {
            return 'Value must be ' + config.max + ' or less.';
        }
        // Unreachable from the blur handler — with no bounds, clampToConfig is
        // the identity and it returns before asking for a message — but a
        // default beats a sentence with a hole in it if that ever changes.
        return 'That value is not allowed here.';
    }

    /**
     * One live error line per field, replaced rather than appended — a shopper
     * correcting a value three times should not collect three messages.
     *
     * Separate from the SDK-message rendering in build(), which draws whatever
     * `useMessages` reports for the attribute: this is the browser's own
     * validation talking, and it arrives and clears between rebuilds.
     */
    function showFieldError(field, message) {
        var existing = field.querySelector('.expivi-configurator__message--invalid');
        if (!message) {
            if (existing) {
                existing.remove();
            }
            return;
        }
        var line = existing || document.createElement('p');
        line.className = 'expivi-configurator__message expivi-configurator__message--error'
            + ' expivi-configurator__message--invalid';
        line.setAttribute('role', 'alert');
        line.textContent = message;
        if (!existing) {
            field.appendChild(line);
        }
    }

    /**
     * The section title, when the merchant asked for one. Shared by all three
     * layouts - `xpv-static-section` draws it above whatever it contains.
     */
    function renderSectionTitles(container, sections) {
        (sections || []).forEach(function (section) {
            if (!section.showTitle || !section.name) {
                return;
            }
            var heading = document.createElement('h2');
            heading.className = 'expivi-section-title';
            heading.textContent = section.name;
            container.appendChild(heading);
        });
    }

    /**
     * `displayType: vertical_step` - every step stacked, each behind its own
     * heading, one open at a time. That is `xpv-static-step`'s own shape: its
     * title carries `role="button"` and `aria-expanded` with a +/- toggle, and
     * the step's content sits below it.
     *
     * A real <button> rather than the vendor's tabindex'd <h1>: it IS a button,
     * and saying so brings the keyboard handling and the screen-reader role
     * without writing either.
     *
     * One open at a time, matching the vendor's single `active` flag. Six steps
     * open at once is the flat render, which is the thing steps exist to avoid.
     */
    function renderAccordion(container, panels, state, sections) {
        renderSectionTitles(container, sections);
        state.index = Math.min(Math.max(state.index, 0), panels.length - 1);

        var headings = [];
        function show(i) {
            state.index = i;
            panels.forEach(function (p, n) {
                p.el.hidden = n !== i;
                headings[n].setAttribute('aria-expanded', n === i ? 'true' : 'false');
                headings[n].classList.toggle('is-open', n === i);
            });
        }

        panels.forEach(function (p, i) {
            var item = document.createElement('div');
            item.className = 'expivi-accordion__item';

            var heading = document.createElement('button');
            heading.type = 'button';
            heading.className = 'expivi-accordion__title';
            heading.textContent = p.showTitle ? p.name : 'Step ' + (i + 1);
            heading.id = 'expivi-acc-title-' + i;
            p.el.id = p.el.id || 'expivi-acc-panel-' + i;
            p.el.setAttribute('aria-labelledby', heading.id);
            heading.setAttribute('aria-controls', p.el.id);
            heading.addEventListener('click', function () {
                show(i);
                container.scrollIntoView({ block: 'nearest' });
            });
            headings.push(heading);

            item.appendChild(heading);
            item.appendChild(p.el);
            container.appendChild(item);
        });

        show(state.index);
    }

    /**
     * `displayType: tab` - one row of tabs, one panel. `xpv-tabs` is the
     * vendor's component; the roles and arrow-key behaviour here are the
     * standard tablist pattern it implements.
     */
    function renderTabs(container, panels, state, sections) {
        renderSectionTitles(container, sections);
        state.index = Math.min(Math.max(state.index, 0), panels.length - 1);

        var list = document.createElement('div');
        list.className = 'expivi-tabs';
        list.setAttribute('role', 'tablist');

        var tabs = [];
        function show(i) {
            state.index = i;
            panels.forEach(function (p, n) {
                p.el.hidden = n !== i;
                tabs[n].setAttribute('aria-selected', n === i ? 'true' : 'false');
                // Roving tabindex: one tab stop for the whole strip, arrows move
                // within it. A tablist you have to Tab through tab by tab is the
                // thing this pattern exists to avoid.
                tabs[n].tabIndex = n === i ? 0 : -1;
                tabs[n].classList.toggle('is-active', n === i);
            });
        }

        panels.forEach(function (p, i) {
            var tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'expivi-tabs__tab';
            tab.setAttribute('role', 'tab');
            tab.textContent = p.showTitle ? p.name : 'Step ' + (i + 1);
            tab.id = 'expivi-tab-' + i;
            p.el.setAttribute('role', 'tabpanel');
            p.el.setAttribute('aria-labelledby', tab.id);
            tab.addEventListener('click', function () {
                show(i);
            });
            tab.addEventListener('keydown', function (event) {
                var to = null;
                if (event.key === 'ArrowRight') {
                    to = i + 1;
                } else if (event.key === 'ArrowLeft') {
                    to = i - 1;
                } else if (event.key === 'Home') {
                    to = 0;
                } else if (event.key === 'End') {
                    to = panels.length - 1;
                }
                if (to === null) {
                    return;
                }
                event.preventDefault();
                var next = (to + panels.length) % panels.length;
                show(next);
                tabs[next].focus();
            });
            tabs.push(tab);
            list.appendChild(tab);
        });

        container.appendChild(list);
        panels.forEach(function (p) {
            container.appendChild(p.el);
        });
        show(state.index);
    }

    /**
     * The nearest ancestor that actually scrolls, or null when the page itself
     * is the scroller.
     *
     * Both need restoring after a rebuild: a theme may put the configurator in
     * its own scrolling column, and then `window.pageYOffset` never moves while
     * the shopper's view still jumps.
     */
    function scrollableAncestor(el) {
        var node = el && el.parentElement;
        while (node && node !== document.body) {
            var overflow = window.getComputedStyle(node).overflowY;
            if ((overflow === 'auto' || overflow === 'scroll')
                && node.scrollHeight > node.clientHeight) {
                return node;
            }
            node = node.parentElement;
        }
        return null;
    }

    function renderSteps(container, plan, fields, state, sections, rushHiddenCids) {
        var panels = [];

        plan.forEach(function (step) {
            var owned = step.attrCids.filter(function (cid) {
                return fields[cid];
            });
            if (!owned.length && step.attrCids.length
                && step.attrCids.every(function (cid) { return (rushHiddenCids || {})[cid]; })) {
                // Every attribute this step owns is the rush attribute, hidden
                // for an auto-rush buyer — not a catalogue rule disallowing it,
                // but a permanent fact of who this buyer is. Unlike the rule
                // case below, there is nothing to explain: the step is simply
                // not part of this buyer's wizard, so drop it rather than show
                // a placeholder for a choice they were never going to make.
                return;
            }
            var panel = document.createElement('div');
            panel.className = 'expivi-configurator__step';
            if (owned.length) {
                owned.forEach(function (cid) {
                    panel.appendChild(fields[cid]);
                });
            } else {
                // Every attribute this step owns was disallowed by a rule. The
                // step is KEPT and says so, which is what `xpv-static-step`
                // does (`showEmptyPlaceholder` -> `xpv-empty-state`, and
                // `showsPlaceholderWhenEmpty` is true whenever the entity has
                // attribute placements — stepPlan has already dropped the ones
                // that have none).
                //
                // Dropping it instead renumbered the wizard under the shopper:
                // a rule firing on step 2 turned "Step 4 of 6" into "Step 3 of
                // 5" with no explanation for either number.
                var empty = document.createElement('p');
                empty.className = 'expivi-configurator__empty';
                empty.setAttribute('role', 'status');
                empty.textContent = 'No options available for your current selection';
                panel.appendChild(empty);
            }
            panels.push({ name: step.name, el: panel, showTitle: step.showTitle !== false });
        });

        // How the merchant asked for these steps to be laid out. `tab` and
        // `vertical_step` were both rendered as the horizontal wizard, which is
        // a reasonable degradation and still not what they chose: a vertical
        // section is an accordion in the vendor's UI, and a tabbed one puts
        // every page one click away.
        var layout = (plan[0] && plan[0].layout) || 'horizontal_step';

        if (panels.length >= 2 && layout === 'vertical_step') {
            renderAccordion(container, panels, state, sections);
            return;
        }
        if (panels.length >= 2 && layout === 'tab') {
            renderTabs(container, panels, state, sections);
            return;
        }

        if (panels.length < 2) {
            panels.forEach(function (p) {
                container.appendChild(p.el);
            });
            return;
        }

        // A step can disappear between rebuilds, so the remembered index is only
        // a hint.
        state.index = Math.min(Math.max(state.index, 0), panels.length - 1);

        var nav = document.createElement('ol');
        nav.className = 'expivi-steps__nav';
        // "Step 2 of 6" above the step name (branded controls). Its own node
        // so the title element keeps carrying just the name.
        var kicker = document.createElement('p');
        kicker.className = 'expivi-steps__kicker';
        var title = document.createElement('p');
        title.className = 'expivi-steps__title';
        var footer = document.createElement('div');
        footer.className = 'expivi-steps__footer';
        var back = document.createElement('button');
        back.type = 'button'; // the panel can sit inside the theme's product form
        back.className = 'expivi-steps__btn expivi-steps__btn--back';
        back.textContent = '← Back';
        var count = document.createElement('span');
        count.className = 'expivi-steps__count';
        var next = document.createElement('button');
        next.type = 'button';
        next.className = 'expivi-steps__btn expivi-steps__btn--next';
        next.textContent = 'Next step ›';

        var items = []; // the <li>s, so the rail can fill up to the active step
        var dots = panels.map(function (p, i) {
            var li = document.createElement('li');
            items.push(li);
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'expivi-steps__dot';
            // The dot is a bare circle, so the step name has to be its label or
            // the whole nav reads as "button, button, button".
            dot.setAttribute('aria-label', 'Step ' + (i + 1) + ': ' + p.name);
            dot.addEventListener('click', function () {
                show(i, true);
            });
            // Branded stepper: the step number inside the circle and a short
            // step name under it. Both aria-hidden - the dot's aria-label
            // already says "Step 2: ...", so a screen reader must not hear it twice.
            var num = document.createElement('span');
            num.className = 'expivi-steps__num';
            num.setAttribute('aria-hidden', 'true');
            num.textContent = String(i + 1);
            dot.appendChild(num);
            li.appendChild(dot);
            var stepLabel = document.createElement('span');
            stepLabel.className = 'expivi-steps__label';
            stepLabel.setAttribute('aria-hidden', 'true');
            stepLabel.textContent = shortStepName(p.name);
            // Same target as the dot, so the whole step (circle + name) is
            // clickable; the dot stays the one focusable control.
            stepLabel.addEventListener('click', function () {
                show(i, true);
            });
            li.appendChild(stepLabel);
            nav.appendChild(li);
            return dot;
        });

        function show(i, moved) {
            state.index = i;
            panels.forEach(function (p, n) {
                p.el.hidden = n !== i;
                // The branded stylesheet's hxIn slide-in is scoped to this
                // class (expivi-branded-controls.css), not the bare element,
                // for exactly this reason: `moved` is true only for an actual
                // Back/Next/dot click, never for the unconditional
                // show(state.index, false) every rebuild ends with (see
                // below) — which runs whether or not anything the shopper is
                // looking at actually changed, and recreates this step's
                // panel from scratch either way. Without the guard, that
                // silent rebuild replayed the same slide-in as a real step
                // change, repeatedly, while the shopper stayed on one step.
                p.el.classList.toggle('is-entering', moved && n === i);
            });
            items.forEach(function (li, n) {
                li.classList.toggle('is-filled', n < i);
            });
            dots.forEach(function (d, n) {
                d.classList.toggle('is-active', n === i);
                d.classList.toggle('is-done', n < i);
                if (n === i) {
                    d.setAttribute('aria-current', 'step');
                } else {
                    d.removeAttribute('aria-current');
                }
            });
            // `StaticStepLayoutEntityController.showTitle` — a step may only
            // hide its title inside a `flat` section, which stepPlan has already
            // resolved. Hidden, the heading line collapses rather than leaving a
            // gap where a name used to be.
            title.textContent = panels[i].showTitle ? panels[i].name : '';
            title.hidden = !panels[i].showTitle;
            count.textContent = 'Step ' + (i + 1) + ' of ' + panels.length;
            kicker.textContent = count.textContent;
            back.disabled = i === 0;
            next.disabled = i === panels.length - 1;
            // Next names where it goes ("Next: Wood →"). On the last step it
            // is disabled; Add to cart sits right below.
            next.textContent = i < panels.length - 1
                ? 'Next: ' + shortStepName(panels[i + 1].name) + ' →'
                : 'Next step ›';
            // Only when the shopper moved. Every rebuild re-runs show(), and
            // scrolling on those would yank the page around under someone who is
            // just reading further down.
            if (moved) {
                container.scrollIntoView({ block: 'nearest' });
            }
        }

        back.addEventListener('click', function () {
            show(state.index - 1, true);
        });
        next.addEventListener('click', function () {
            show(state.index + 1, true);
        });

        renderSectionTitles(container, sections);
        container.appendChild(nav);
        container.appendChild(kicker);
        container.appendChild(title);
        panels.forEach(function (p) {
            container.appendChild(p.el);
        });
        footer.appendChild(back);
        footer.appendChild(count);
        footer.appendChild(next);
        container.appendChild(footer);

        show(state.index, false);
    }

    /**
     * Every `displaySettings` key this file does NOT act on, with the value it
     * assumes instead. Anything a merchant sets away from these is silently not
     * honoured — the panel still renders, just not what they configured, which
     * is exactly how displayType went unnoticed for the whole V2 migration.
     *
     * Down to one. `searchable` and `sortable` are drawn by valueTools,
     * `pageSize` by valueLoadMore, `enableCategoryFilter` by categoryFilter,
     * `showPriceAdjustment` by optionLabel/valueGroup/valueSlider. `displayType`
     * was never in here: it is handled per attribute in build(), and
     * warnUnhandledDisplaySettings reports unknown VALUES of it rather than its
     * presence.
     *
     * Search and sort need radios to act on, so build() still warns on its own
     * when either is set on a `dropdown`.
     */
    var HONOURED_DISPLAY_SETTINGS = {
        customField: null,           // `custom` components need the Stencil ui-kit
    };

    /**
     * The displayType values build() knows how to draw, PER ATTRIBUTE KIND —
     * because that is how build() draws them. Only the list-shaped kinds read
     * `display[cid]` at all; a ConfigurableNumber gets a number input whatever
     * the merchant chose, so `dropdown` on one is unhandled even though it is
     * perfectly handled on a list. One flat set called that a match and said
     * nothing.
     *
     * Taken from the SDK's own enums (AttributeListDisplayType and friends) so
     * this list is checkable against a bundle upgrade rather than folklore:
     *
     *   list      list | list_slider | tiles | dropdown | toggle_buttons | custom
     *   boolean   toggle_buttons | custom
     *   string    input_field | static_value | custom
     *   number    input_field | static_value | custom
     */
    var HANDLED_DISPLAY_TYPES = {
        ConfigurableList: { list: true, list_slider: true, tiles: true, dropdown: true, toggle_buttons: true },
        ConfigurableBoolean: { dropdown: true, toggle_buttons: true },
        ConfigurableString: { input_field: true, static_value: true },
        ConfigurableNumber: { input_field: true, static_value: true },
    };

    // The union, used when the caller cannot say which kind an attribute is (no
    // attribute store to ask — the node check calls it that way). Warning only
    // on a type no kind handles is the old, looser behaviour: fewer true
    // positives, but never a false one.
    var ANY_HANDLED_DISPLAY_TYPE = {};
    Object.keys(HANDLED_DISPLAY_TYPES).forEach(function (rtti) {
        Object.keys(HANDLED_DISPLAY_TYPES[rtti]).forEach(function (type) {
            ANY_HANDLED_DISPLAY_TYPE[type] = true;
        });
    });

    /**
     * One console warning per problem, listing what the merchant configured
     * that this panel does not act on.
     *
     * A warning, not a throw: an unhandled setting degrades to a working
     * control, and a storefront must not go blank because someone ticked
     * "searchable". The point is only that it stops being invisible.
     */
    function warnUnhandledDisplaySettings(layouts, warned, valueCount, rttiOf) {
        // `valueCount` is unused since valueLoadMore started honouring
        // pageSize — it existed only to keep that one warning quiet on a list
        // shorter than its own page. Kept in the signature because the caller
        // pays nothing for it and the next deferred setting may well need the
        // same "is this actually a gap here?" test.
        void valueCount;

        var ignored = {};
        var unknown = {};

        ((layouts && layouts.data) || []).forEach(function (e) {
            if (!e || !e.attributeCid) {
                return;
            }
            var ds = (e.config && e.config.displaySettings) || {};
            var name = (e.config && e.config.name) || e.attributeCid;

            var rtti = rttiOf ? rttiOf(e.attributeCid) : null;
            var handled = HANDLED_DISPLAY_TYPES[rtti] || ANY_HANDLED_DISPLAY_TYPE;
            if (ds.displayType && !handled[ds.displayType]) {
                // The kind is part of the complaint when we know it: "dropdown"
                // alone reads as a typo, "dropdown on ConfigurableNumber" reads
                // as the gap it is.
                var what = '"' + ds.displayType + '"'
                    + (HANDLED_DISPLAY_TYPES[rtti] ? ' on ' + rtti : '');
                (unknown[what] = unknown[what] || []).push(name);
            }

            Object.keys(HONOURED_DISPLAY_SETTINGS).forEach(function (key) {
                if (!Object.prototype.hasOwnProperty.call(ds, key)) {
                    return;
                }
                var assumed = HONOURED_DISPLAY_SETTINGS[key];
                // customField has no single "off" value; anything non-null is
                // something we are not acting on.
                var honoured = assumed === null ? ds[key] == null : ds[key] === assumed;

                if (!honoured) {
                    (ignored[key] = ignored[key] || []).push(name + '=' + JSON.stringify(ds[key]));
                }
            });
        });

        Object.keys(unknown).forEach(function (type) {
            if (warned['dtype:' + type]) {
                return;
            }
            warned['dtype:' + type] = true;
            // eslint-disable-next-line no-console
            console.warn('[expivi] displayType ' + type + ' is not implemented, so these '
                + 'render as their default control instead: ' + unknown[type].join(', ')
                + '. See docs/expivi-v2-migration.md.');
        });

        Object.keys(ignored).forEach(function (key) {
            if (warned['dsetting:' + key]) {
                return;
            }
            warned['dsetting:' + key] = true;
            // eslint-disable-next-line no-console
            console.warn('[expivi] displaySettings.' + key + ' is set but this theme does not '
                + 'act on it (assumes ' + JSON.stringify(HONOURED_DISPLAY_SETTINGS[key]) + '): '
                + ignored[key].join(', ') + '. See docs/expivi-v2-migration.md.');
        });
    }

    /**
     * Log the SDK's GLOBAL messages — the ones carrying no attribute identifier,
     * so getAttributeMessages() never returns them and the panel never draws
     * them. "Failed to validate product" arrives here, and a failed validation
     * is why no rule is evaluated: every attribute then reports disallowed
     * false, so conditional options all render and the price is built without
     * the rule adjustments. Silence made that look like a theme bug for a week.
     *
     * Console only: a shopper can do nothing about a catalogue fault, and the
     * panel already renders. This exists so the NEXT one is visible in a
     * screenshot instead of being inferred from a wrong total.
     *
     * ponytail: dedupe by message id, since the store re-notifies on every
     * settled rule/price response and would otherwise log the same fault dozens
     * of times per page.
     */
    function reportGlobalMessages(messages, container) {
        if (typeof messages.subscribeGlobal !== 'function') {
            return; // older bundle: no global channel to read
        }
        var seen = {};
        var centre = null;
        var dismissed = {};

        function panel() {
            if (centre || !container || !container.parentNode) {
                return centre;
            }
            centre = document.createElement('div');
            centre.className = 'expivi-messages';
            centre.setAttribute('role', 'status');
            centre.setAttribute('aria-live', 'polite');
            container.parentNode.insertBefore(centre, container);
            return centre;
        }

        function paint(list) {
            var host = panel();
            if (!host) {
                return;
            }
            host.textContent = '';
            var shown = (list || []).filter(function (m) {
                return m && m.message && !dismissed[m.id];
            });
            host.hidden = !shown.length;
            shown.forEach(function (m) {
                var row = document.createElement('p');
                row.className = 'expivi-messages__item expivi-messages__item--'
                    + (m.type || 'info');
                var text = document.createElement('span');
                text.textContent = m.message;
                row.appendChild(text);

                var close = document.createElement('button');
                close.type = 'button';
                close.className = 'expivi-messages__clear';
                close.setAttribute('aria-label', 'Clear message');
                close.textContent = '×';
                close.addEventListener('click', function () {
                    dismissed[m.id] = true;
                    paint(list);
                });
                row.appendChild(close);
                host.appendChild(row);
            });
        }

        messages.subscribeGlobal(function (list) {
            // The console line stays, and stays deduped: a global fault is a
            // catalogue problem, and the id and product offer are what a
            // developer needs out of a screenshot.
            (list || []).forEach(function (m) {
                if (!m || seen[m.id]) {
                    return;
                }
                seen[m.id] = true;
                var where = m.productOffer ? ' (product offer ' + m.productOffer + ')' : '';
                console[m.type === 'error' ? 'error' : 'warn'](
                    '[expivi] ' + String(m.type || 'message').toUpperCase() + ': '
                    + m.message + where
                );
            });
            paint(list);
        });
    }

    function renderOptions(container, sdk, configurator, opts) {
        var attributes = sdk.useStoreAttributes(configurator);
        // Absent on older bundles; without it the panel renders flat, which is
        // what it did before steps existed.
        var layouts = typeof sdk.useStoreLayouts === 'function'
            ? sdk.useStoreLayouts(configurator)
            : null;
        var step = { index: 0 }; // survives the rebuilds that wipe the container
        // The SDK reports a failed value fetch, and any rule message, ONLY here.
        // Unread, an attribute that failed to load renders as an empty dropdown
        // with nothing to explain it.
        var messages = typeof sdk.useMessages === 'function' ? sdk.useMessages(configurator) : null;
        var warned = {};
        var filtered = {}; // attribute cid -> company category filter already applied
        var corrected = {}; // attribute cid -> disallowed selection already replaced
        var clearedHidden = {}; // attribute cid -> hidden attribute's old value already cleared
        var tools = {};     // attribute cid -> {timer} for valueTools' debounce, kept across rebuilds
        var categories = {}; // attribute cid -> {status, list} for categoryFilter's async fetch
        var pages = {};     // attribute cid -> {signature, seen, values, auto, requested, observer}
                            // for accumulateValues and valueLoadMore, kept across rebuilds
        var thumbImgs = {}; // value cid -> <img>, reused across rebuilds (see
                            // valueGroup's tile branch) so an unchanged swatch
                            // thumbnail is never destroyed and recreated, which is
                            // what actually blinks, not slow loading. Never evicted,
                            // bounded by this catalogue's size either way (hundreds
                            // of cids at most) - checked that renderOptions itself
                            // is never re-entered for the same source/configurator
                            // (the one call site latches on `started`, loadProduct()
                            // fires exactly once), so there is no live "second boot,
                            // same closure, stale product" path for this to go
                            // actually stale against. ponytail: revisit if a
                            // product-switch-without-reload feature ever adds one.
        var painted = false;
        var money = opts.money;
        var lastSignature = null;

        var buildOnce = function () {
            // Everything the panel actually draws, as one comparable string. The
            // store notifies on every settled rule, price and BOM response, not
            // only on the ones that change an option — measured live, a single
            // selection fired ~34 rebuilds that produced 2 distinct panels. Each
            // one wipes the container, so any <select> the shopper has open is
            // destroyed and its dropdown shuts.
            //
            // Skipping the identical ones is the whole fix: the rebuild still
            // happens whenever the panel genuinely changes, so nothing about how
            // it renders is different — there is just no longer a burst of
            // redraws tearing the panel down between them.
            var plan = stepPlan(layouts);
            var placed = null;
            if (plan) {
                placed = {};
                plan.loose.forEach(function (cid) {
                    placed[cid] = true;
                });
                plan.steps.forEach(function (s) {
                    s.attrCids.forEach(function (cid) {
                        placed[cid] = true;
                    });
                });
            }
            var display = (plan && plan.display) || {};
            var sigOpts = withMessages(opts, messages);
            sigOpts.placed = placed;

            var signature = planSignature(plan) + '\n'
                + panelSignature(attributes, sdk, configurator, sigOpts);
            if (signature === lastSignature) {
                return;
            }
            lastSignature = signature;

            // Data that arrives on its own clock — the category list is fetched
            // per attribute and lands after the build that asked for it. The
            // store never changed, so the signature is identical and the guard
            // above would swallow the rebuild; clearing it is what lets the one
            // extra pass through.
            var rebuildForAsync = function () {
                lastSignature = null;
                build();
            };

            // AFTER the early return, not before it: this walks every layout
            // entity and reads the attribute store per entity, and build() runs
            // ~34 times per selection to produce 2 distinct panels. Its output
            // is one-shot anyway (keyed into the same `warned` map as every
            // other notice), so the other 32 passes were pure cost.
            var rttiByCid = {};
            attributes.data.forEach(function (v) {
                if (v) {
                    rttiByCid[v.cid] = v.rtti;
                }
            });
            warnUnhandledDisplaySettings(layouts, warned, function (cid) {
                var c = sdk.useAttribute(configurator, cid);
                return c && c.values ? c.values.length : null;
            }, function (cid) {
                return rttiByCid[cid] || null;
            });

            var active = document.activeElement;
            var dataset = (active && active.dataset) || {};
            var focusCid = dataset.expiviAttrCid || null;
            var focusInput = null;
            // The search box needs its own restore path, and the caret with it:
            // every accepted keystroke reloads the value page, which rebuilds
            // this whole container out from under the shopper mid-word.
            var searchCid = dataset.expiviSearchCid || null;
            var searchCaret = searchCid ? active.selectionStart : null;
            // Load More gets the same treatment: the button that was clicked is
            // about to be destroyed with everything else, and focus falling back
            // to <body> is how a keyboard shopper loses their place entirely.
            var moreCid = dataset.expiviMoreCid || null;

            // Where the shopper is looking, and whatever box they are looking
            // through. Emptying the container collapses the document — with 300
            // option rows rendered that is thousands of pixels — and the browser
            // clamps scrollTop to the new, much shorter height. Re-adding the
            // content does not undo the clamp, so the shopper ends up at the top
            // of the list. That is the "Load More sent me back to the first
            // item" report: nothing scrolled them, the page shrank underneath
            // them and the scroll position had nowhere to be.
            //
            // Captured and restored inside the same synchronous task, so the
            // browser never paints the collapsed state and the list reads as
            // having simply grown.
            var scrolled = scrollableAncestor(container);
            var scrollTop = scrolled ? scrolled.scrollTop : 0;
            var pageX = window.pageXOffset;
            var pageY = window.pageYOffset;
            container.textContent = '';

            // Built detached and keyed by attribute, then appended in layout
            // order: the attribute store's own order is arbitrary, and a step
            // has to show its fields in the order the merchant arranged them.
            var fields = {};
            var order = [];
            // cids the auto-rush hide swallowed this build, so renderSteps can
            // tell "empty because entitled buyers never see this" from "empty
            // because a catalogue rule disallowed it" and drop the former
            // instead of showing a placeholder for it.
            var rushHiddenCids = {};

            attributes.data.forEach(function (view) {
                if (view && view.hidden) {
                    // A rule hides this attribute but leaves its old value in
                    // the configuration, so Expivi keeps pricing it and the
                    // cart lists it: Ventilation Brand back to None hid the
                    // Model yet the $700 stayed in the total. Cleared once per
                    // hidden episode; setValue('') is the same blank a reset
                    // attribute shows (see staleSelectionReset).
                    var hiddenCtrl = !clearedHidden[view.cid] && (view.configuredValue || []).length
                        ? sdk.useAttribute(configurator, view.cid)
                        : null;
                    // Never the auto-rush attribute: its value is set on the
                    // buyer's behalf and healed by the tick below, not by them.
                    if (hiddenCtrl && !hiddenCtrl.isMultiple
                        && !(opts.autoRush && rushValue(hiddenCtrl.values))) {
                        clearedHidden[view.cid] = true;
                        hiddenCtrl.markAsDirty();
                        hiddenCtrl.setValue('');
                    }
                    return;
                }
                if (view) {
                    delete clearedHidden[view.cid];
                }
                // Nothing in the layout tree owns it, so Expivi's composer does
                // not draw it either. Warned rather than dropped in silence:
                // this is the one way a merchant can lose an option from the
                // page without touching the theme.
                if (placed && !placed[view.cid]) {
                    if (!warned['unplaced:' + view.cid]) {
                        warned['unplaced:' + view.cid] = true;
                        // eslint-disable-next-line no-console
                        console.warn('[expivi] "' + (view.name || view.identifier) + '" sits in no '
                            + 'section or step of the layout tree, so it is not rendered — the same '
                            + 'as Expivi\'s own composer. Place it in a step to show it.');
                    }
                    return;
                }
                var ctrl = sdk.useAttribute(configurator, view.cid);

                // Auto-rush buyers get rush selected for them and the option
                // hidden. NOT guarded to run once: the SDK applies a snapshot
                // AFTER it emits READY, and its parse() CLEARS every attribute
                // the snapshot does not mention — including this one, since
                // whoever saved the snapshot was not an auto-rush buyer. A
                // once-guard made that clobber permanent. autoRushValue returns
                // null the moment rush is correctly set, and setValue is a no-op
                // on an unchanged value, so re-checking every build is free and
                // the tick heals whatever cleared it.
                if (opts.autoRush && rushValue(ctrl.values)) {
                    var rushOn = autoRushValue(view, ctrl.values);
                    if (rushOn) {
                        ctrl.markAsDirty();
                        ctrl.setValue(rushOn);
                    }
                    rushHiddenCids[view.cid] = true;
                    return;
                }

                var allowedColors = allowedColorIdentifiers(view, opts.colors, opts.colorAttributes);

                // Computed here, ahead of the "does this mapping match anything"
                // check right below, because that check must look at every page
                // loaded so far — not just whichever page the SDK currently holds
                // in ctrl.values. See the check's own comment for why that
                // distinction is the whole bug.
                var pageState = pages[view.cid] || (pages[view.cid] = { requested: 0 });
                // The previous build's observer still holds its sentinel, which
                // the container is about to discard.
                if (pageState.observer) {
                    pageState.observer.disconnect();
                    pageState.observer = null;
                }
                // Every page loaded so far, not just the current one — paging is
                // additive now. See accumulateValues for why this cannot simply
                // read ctrl.allValues.
                var shownValues = accumulateValues(view, ctrl, pageState);

                // A mapping that matches none of an attribute's values would render
                // an empty dropdown - a silently broken option, worse than an
                // ungated one. Seen live: "Select Hood Color" carries Expivi's
                // generated 32-hex ids rather than SKUs, so mapping it emptied it.
                //
                // Checked against shownValues (every page accumulated so far), NOT
                // ctrl.values (whichever single page the SDK currently holds).
                // ctrl.values IS the whole list for a single-page attribute, but a
                // paged colour attribute replaces it wholesale on every "Load
                // More" — so a 21-colour catalogue whose matches all happened to
                // land on page 1 would see page 2 arrive with zero overlap,
                // read that as "this mapping matches nothing at all", and turn
                // gating off entirely for the rest of the session. That is the
                // exact bug: clicking Load More once made every later rebuild
                // show the full unfiltered catalogue.
                var gateCfg = pageConfig(view);
                var gateMorePages = !!(gateCfg && gateCfg.page < gateCfg.lastPage
                    && !pageState.failed && typeof ctrl.loadValuePage === 'function');
                // Deliberately the STRICT identifier test, not isAllowedColor:
                // Raw / Custom Color Match are always shown, so counting them
                // here would end the auto-paging as soon as one sits on page 1,
                // before any catalog colour has been found.
                if (allowedColors && !shownValues.some(function (v) {
                    return String((v && v.identifier) || '') in allowedColors;
                })) {
                    if (gateMorePages) {
                        // Nothing allowed on the pages loaded so far, but more
                        // pages exist: the allowed colours may simply be later in
                        // the 307. Dropping the gate here showed every catalog's
                        // colours until a scroll happened to reach one. Keep the
                        // gate and walk the remaining pages (one request per
                        // page, guarded by pageState.requested).
                        var gateNext = gateCfg.page + 1;
                        if (pageState.requested < gateNext) {
                            pageState.requested = gateNext;
                            // .then, not Promise.resolve(call): a synchronous
                            // throw from loadValuePage must reach the catch.
                            Promise.resolve().then(function () {
                                return ctrl.loadValuePage({ page: gateNext });
                            }).catch(function () {
                                // Re-read: the cursor may have moved since.
                                pageState.requested = (pageConfig(view) || {}).page || 1;
                                pageState.failed = true;
                            });
                        }
                    } else {
                    if (!warned['empty:' + view.cid]) {
                        warned['empty:' + view.cid] = true;
                        // Two very different causes, and blaming the merchant for
                        // the wrong one costs an afternoon: an attribute whose
                        // value page failed to load matches nothing either, and
                        // the SDK reports THAT only through useMessages.
                        // eslint-disable-next-line no-console
                        console.warn(shownValues.length
                            ? '[expivi] "' + (view.name || view.identifier) + '" is mapped as a '
                                + 'colour option but none of its values match an allowed SKU - '
                                + 'leaving it unfiltered. Check the mapping on the Colors page.'
                            : '[expivi] "' + (view.name || view.identifier) + '" has no values loaded '
                                + 'at all, so colour gating cannot apply - this is a failed value '
                                + 'fetch, not a mapping problem.');
                    }
                    allowedColors = null;
                    }
                }
                var field = document.createElement('div');
                field.className = 'expivi-configurator__field';

                var label = document.createElement('label');
                label.className = 'expivi-configurator__label';
                label.textContent = view.name || view.identifier || '';
                field.appendChild(label);

                // The merchant's help text, drawn under the label exactly where
                // Expivi's own `.xpv-component__description` sits. It lives on
                // the ATTRIBUTE, not on the layout entity, which is the whole
                // reason it went unnoticed: it is not a displaySettings key, so
                // warnUnhandledDisplaySettings never had it to report and the
                // text was simply dropped in silence.
                var desc = null;
                if (view.description) {
                    desc = document.createElement('span');
                    desc.className = 'expivi-configurator__description';
                    desc.id = 'expivi-desc-' + view.cid;
                    desc.textContent = view.description;
                    field.appendChild(desc);
                }

                // Once per attribute: setValue rebuilds the panel, and the
                // replacement IS allowed, so a second pass finds nothing to do -
                // the flag makes that a fact rather than a hope.
                //
                // Not `||`: staleSelectionReset can return '' (clear to the
                // placeholder), which is falsy and `||` would skip straight
                // past it to look for yet another correction.
                var switchTo = correctedSelection(view.configuredValue, ctrl.values, allowedColors,
                    (ctrl.allValues || []).concat(shownValues));
                if (switchTo === null) {
                    switchTo = staleSelectionReset(view, ctrl);
                }
                if (switchTo !== null && !corrected[view.cid]) {
                    corrected[view.cid] = true;
                    ctrl.markAsDirty();
                    ctrl.setValue(switchTo);
                }

                // Every configured value, not just the first: a `multiple`
                // attribute holds several and the SDK keeps them all in
                // `selectedValues`. Reading [0] alone drew one tick on an
                // attribute the shopper had picked four things in.
                var selectedCids = (view.configuredValue || []).map(function (cv) {
                    return (cv || {}).cid;
                }).filter(Boolean);

                // The current choice, shown at the right of the field label
                // (branded controls). A SIBLING of the <label>, never inside it:
                // esd-xboost-toggle.js reads the label's own text to find the
                // Rush field. Display only - nothing reads it back.
                if (view.rtti === 'ConfigurableList' || view.rtti === 'ConfigurableBoolean') {
                    var chosenText = (view.configuredValue || []).filter(Boolean).map(valueLabel)
                        .filter(function (t) { return t && t !== '(unnamed)'; }).join(', ');
                    if (chosenText) {
                        var chosen = document.createElement('span');
                        chosen.className = 'expivi-configurator__value';
                        chosen.textContent = chosenText;
                        chosen.title = chosenText;
                        field.insertBefore(chosen, label.nextSibling);
                    }
                }
                // pageState / shownValues were already computed above, ahead of
                // the colour-gating "matches nothing" check that needs them.
                //
                // Checked against shownValues (every page accumulated so far),
                // NOT ctrl - the SDK's single current page. offPageSelections'
                // whole job is "is this configured selection missing from what
                // we have loaded", and shownValues is what "loaded" means once
                // paging is additive. Passing ctrl here made a SELECTED value
                // reappear duplicated the moment Load More moved past its page:
                // it was already sitting in shownValues from the page it was
                // originally chosen on, but ctrl.values had moved on to a later
                // page that doesn't contain it, so this read it as newly
                // off-page and concatenated a second copy in front of the one
                // accumulateValues already kept.
                var offPage = offPageSelections(view, { values: shownValues });
                var input = null;
                var grouped = false; // set when `input` is a radiogroup, not a control
                var usesCustomDropdown = false; // set when `input` is valueDropdown's own wrapper
                var focusable = null;
                var toolbar = null;
                var maxChars = null; // ConfigurableString only; see that branch

                // The merchant's own choice of widget, off the layout entity.
                // Absent (no entity for this attribute) falls back to the
                // dropdown the panel has always drawn.
                var ds = display[view.cid] || {};
                var kind = ds.displayType || 'dropdown';

                // Branded controls: a single-choice dropdown with only 2-4
                // values (Modify Hood Height, Chimney Extension...) is drawn
                // as segmented buttons, so every choice and its price is
                // visible without opening anything. Only when every value is
                // already loaded (no Load More) and nothing needs search/sort.
                if (kind === 'dropdown' && !ctrl.isMultiple && !ds.searchable && !ds.sortable
                    && (view.rtti === 'ConfigurableList' || view.rtti === 'ConfigurableBoolean')) {
                    var segCfg = pageConfig(view);
                    var segValues = offPage.concat(shownValues).filter(function (v) {
                        return !allowedColors || isAllowedColor(v, allowedColors);
                    });
                    if (!(segCfg && segCfg.page < segCfg.lastPage)
                        && segValues.length >= 2 && segValues.length <= 4) {
                        kind = 'toggle_buttons';
                    }
                }

                // valueTools needs radios to filter and reorder. A native
                // <select> has none — its own type-ahead is the search, and
                // there is nothing to hang a sort control off — so a merchant
                // who ticks either on a dropdown gets nothing. Say so, rather
                // than leave the setting looking honoured.
                if ((ds.searchable || ds.sortable) && kind === 'dropdown' && !warned['tools:' + view.cid]) {
                    warned['tools:' + view.cid] = true;
                    // eslint-disable-next-line no-console
                    console.warn('[expivi] "' + (view.name || view.identifier) + '" is set searchable '
                        + 'or sortable, but its displayType is "dropdown" — those controls are only '
                        + 'drawn on list/tiles/toggle_buttons. Change the display type to show them.');
                }

                // ConfigurableBoolean renders exactly like a list because that is
                // what it is here: a two-value pick with its own labels ("Standard
                // Lead Time" / "Rush My Lead Time"), which is why Expivi's own
                // panel draws two radios for it. The checkbox this replaced was
                // wrong three ways — it set values[0] as "on" (the rush option is
                // values[1], and the order differs per attribute), it read as
                // ticked whenever EITHER value was selected, and it threw both
                // labels away.
                if (view.rtti === 'ConfigurableList' || view.rtti === 'ConfigurableBoolean') {
                    if (kind !== 'dropdown') {
                        var built = kind === 'list_slider'
                            ? valueSlider(view, ctrl, allowedColors, selectedCids, money,
                                ds.showPriceAdjustment, shownValues, offPage)
                            : valueGroup(view, ctrl, kind, allowedColors, selectedCids, money,
                                ds.showPriceAdjustment, offPage, shownValues, thumbImgs);
                        input = built.el;
                        focusable = built.focusable;
                        grouped = true;
                        if (ds.searchable || ds.sortable) {
                            var toolState = tools[view.cid] || (tools[view.cid] = { timer: null });
                            toolbar = valueTools(view, ctrl, ds, toolState);
                        }
                    } else if (!ctrl.isMultiple) {
                        // `multiple` stays on the native <select multiple>
                        // below — see that branch's own comment.
                        var builtDropdown = valueDropdown(view, ctrl, {
                            values: offPage.concat(shownValues).filter(function (v) {
                                return !allowedColors || isAllowedColor(v, allowedColors);
                            }),
                            allowedColors: allowedColors,
                            selectedCids: selectedCids,
                            money: money,
                            showPriceAdjustment: ds.showPriceAdjustment,
                            state: pageState,
                            disabled: ctrl.isStatic || ctrl.isDisabled,
                        });
                        input = builtDropdown.el;
                        focusable = builtDropdown.trigger;
                        usesCustomDropdown = true;
                    }
                }

                if (grouped || usesCustomDropdown) {
                    // already built
                } else if (view.rtti === 'ConfigurableList' || view.rtti === 'ConfigurableBoolean') {
                    input = document.createElement('select');
                    // A deliberate divergence: `xpv-dropdown` binds a single
                    // `selectedId` even for a multiple attribute, so the vendor
                    // panel shows one of several picks and hides the rest. A
                    // native <select multiple> costs nothing and does not lose
                    // the shopper's other selections.
                    input.multiple = !!ctrl.isMultiple;
                    if (!input.multiple) {
                        var none = document.createElement('option');
                        none.value = '';
                        // Expivi's own wording for the same empty state. Ours
                        // read "— select —", which is the only string on the
                        // panel a shopper could tell apart from the vendor
                        // preview.
                        none.textContent = 'Select an option';
                        input.appendChild(none);
                    }
                    offPage.concat(shownValues).forEach(function (v) {
                        // Colour gating: the value's `identifier` is the SKU, and
                        // which SKU depends on the line — short for Choose Color,
                        // the apron/chimney one for those. A selected-but-now-
                        // disallowed value is dropped too; the store rejects it on
                        // the next change and the shopper reselects.
                        if (allowedColors && !isAllowedColor(v, allowedColors)) {
                            return;
                        }
                        var option = document.createElement('option');
                        option.value = v.cid;
                        option.disabled = !!v.disabled;
                        option.textContent = optionLabel(v, money, ds.showPriceAdjustment);
                        // A native <option> has nowhere to put a second line,
                        // but it does honour `title` — which is what
                        // xpv-dropdown-selector shows for the same field.
                        if (v.description) {
                            option.title = v.description;
                        }
                        if (selectedCids.indexOf(v.cid) !== -1) {
                            option.selected = true;
                        }
                        input.appendChild(option);
                    });
                    input.addEventListener('change', function () {
                        ctrl.markAsDirty();
                        if (!input.multiple) {
                            ctrl.setValue(input.value);
                            return;
                        }
                        // setValue TOGGLES on a multiple attribute, so send it
                        // the difference rather than the new set — replaying
                        // the whole selection would switch off everything the
                        // shopper kept.
                        var wanted = {};
                        [].forEach.call(input.selectedOptions, function (o) {
                            wanted[o.value] = true;
                        });
                        selectedCids.forEach(function (cid) {
                            if (!wanted[cid]) {
                                ctrl.setValue(cid);
                            }
                        });
                        Object.keys(wanted).forEach(function (cid) {
                            if (selectedCids.indexOf(cid) === -1) {
                                ctrl.setValue(cid);
                            }
                        });
                    });
                } else if (kind === 'static_value'
                    && (view.rtti === 'ConfigurableNumber' || view.rtti === 'ConfigurableString')) {
                    // The merchant marked this field display-only. The SDK maps
                    // `static_value` to `xpv-static-string` for both kinds
                    // (staticNumberComponent / staticStringComponent) and draws
                    // no input at all. We drew an editable box, which is the one
                    // remaining displayType gap with a correctness edge rather
                    // than a cosmetic one: the shopper could type into a field
                    // the catalogue does not read back.
                    //
                    // Left as `input = null` so the shared static branch below
                    // renders it, value description and all.
                    input = null;
                } else if (view.rtti === 'ConfigurableNumber') {
                    input = document.createElement('input');
                    input.type = 'number';
                    var current = (view.configuredValue || [])[0];
                    input.value = current && current.value != null ? String(current.value) : '';
                    // The merchant's bounds, handed to the browser so it
                    // validates and steps for us — the SDK passes the same four
                    // to `xpv-number-field`.
                    var numberCfg = numberConfig(view);
                    if (numberCfg.min != null) {
                        input.min = String(numberCfg.min);
                    }
                    if (numberCfg.max != null) {
                        input.max = String(numberCfg.max);
                    }
                    if (numberCfg.step != null) {
                        input.step = String(numberCfg.step);
                    }
                    input.addEventListener('change', function () {
                        // Number('') is 0. Clearing the field is not an order for
                        // zero of anything — leave the store on its last value.
                        if (!input.value.trim()) {
                            return;
                        }
                        ctrl.markAsDirty();
                        ctrl.setValue(Number(input.value));
                    });
                    // Clamped on blur, like `xpv-number-input.handleBlur`: the
                    // browser refuses to fire `change` for an out-of-range value
                    // in some engines and happily fires it in others, so without
                    // this a shopper could leave 4800 sitting in a field capped
                    // at 48 — visible, plausible, and not what gets ordered.
                    input.addEventListener('blur', function () {
                        var raw = input.value.trim();
                        if (!raw) {
                            return;
                        }
                        var typed = Number(raw);
                        // isFinite, not !isNaN: Number('Infinity') is a number
                        // and clamping it against an absent max returns it.
                        if (!isFinite(typed)) {
                            return;
                        }
                        var fixed = clampToConfig(typed, numberCfg);
                        // Compared as NUMBERS. String(fixed) === raw called
                        // "12.0" a change from 12 and rewrote the field, marked
                        // the attribute dirty and pushed a setValue nobody
                        // asked for — on every blur, for a value the shopper
                        // had merely typed with a trailing zero.
                        if (fixed === typed) {
                            return;
                        }
                        // Read BEFORE the clamp, because clamping makes the
                        // field valid and empties validationMessage. `xpv-number-input`
                        // shows the same string off `showValidationError`
                        // (rangeOverflow / rangeUnderflow / stepMismatch / badInput).
                        //
                        // Worth saying rather than silently snapping: a shopper
                        // who typed 4800 into a field capped at 48 watched it
                        // become 48 with no explanation, and the natural reading
                        // of that is that the configurator is broken.
                        //
                        // The browser writes this string, so it arrives in the
                        // shopper's own language — which is more than the rest of
                        // this panel manages.
                        showFieldError(field, input.validationMessage
                            || boundsMessage(numberCfg));
                        input.value = String(fixed);
                        ctrl.markAsDirty();
                        ctrl.setValue(fixed);
                    });
                    // Cleared as soon as they start correcting it themselves.
                    input.addEventListener('input', function () {
                        showFieldError(field, '');
                    });
                } else if (view.rtti === 'ConfigurableString') {
                    input = document.createElement('input');
                    input.type = 'text';
                    input.placeholder = 'Enter text...';
                    var currentStr = (view.configuredValue || [])[0];
                    var strValue = currentStr && currentStr.value != null ? String(currentStr.value) : '';
                    // The SDK's own createConfiguredValueFromSettings seeds a
                    // ConfigurableString's configuredValue from
                    // settings.default_value when the catalogue sets one -
                    // genuinely "No configured default -> keep null so the
                    // input renders blank" is the SDK's own comment for the
                    // other case, confirming this one is deliberate: a real
                    // default is meant to arrive pre-filled AND already
                    // committed. Seen live: a catalogue author used it for
                    // instructional placeholder copy ("Color Code Here")
                    // instead, which this can't tell apart from a real
                    // default, so render it as an actual placeholder
                    // (greyed, not submitted) rather than editable text
                    // indistinguishable from the shopper's own answer -
                    // configurationProperties applies the same check so it
                    // isn't saved to the order either. Deliberately TEXT
                    // only: ConfigurableNumber's own default_value is left
                    // alone (pre-filled, editable, and saved as-is) - a
                    // number field's default is a real default a merchant
                    // wants accepted unedited, not catalogue-authored copy.
                    var defaultStrValue = (view.settings && view.settings.default_value != null)
                        ? String(view.settings.default_value) : null;
                    if (defaultStrValue != null && strValue === defaultStrValue) {
                        input.placeholder = defaultStrValue;
                        strValue = '';
                    }
                    input.value = strValue;
                    // `xpv-string-input` reads this off the controller
                    // (DEFAULT_MAX_CHARS is 60) and hands it to xpv-text-field
                    // as maxLength. Unset, the field took any length and the
                    // store truncated it silently on the way to the order.
                    maxChars = typeof ctrl.getMaxCharacters === 'function'
                        ? ctrl.getMaxCharacters()
                        : null;
                    if (maxChars > 0) {
                        input.maxLength = maxChars;
                    }
                    input.addEventListener('change', function () {
                        ctrl.markAsDirty();
                        ctrl.setValue(input.value);
                    });
                } else if (view.rtti !== 'StaticString' && view.rtti !== 'StaticNumber') {
                    // Static* are display-only, so silence is correct for those.
                    if (!warned[view.rtti]) {
                        warned[view.rtti] = true;
                        // eslint-disable-next-line no-console
                        console.warn('[expivi] unhandled attribute type: ' + view.rtti + ' (' + view.name + ')');
                    }
                    return;
                }

                if (input) {
                    // `xpv-loadable-content` wraps the option list and shows a
                    // loader while `pageHelpers.page.status === "loading"`.
                    // Searching 307 colours is a visible round-trip, and without
                    // this the panel sat perfectly still showing the OLD options
                    // until the new ones swapped in — indistinguishable from a
                    // search that had done nothing.
                    if ((view.valuePage || {}).status === 'loading') {
                        field.classList.add('expivi-configurator__field--loading');
                        input.setAttribute('aria-busy', 'true');
                    }
                    // A static attribute is display-only: the SDK marks its
                    // listbox aria-disabled, drops it out of the tab order and
                    // refuses every option click. valueGroup disables its own
                    // radios; a single control disables directly. Same for a
                    // rule-disabled attribute (ctrl.isDisabled) - per-value
                    // disabling for this path is the per-<option> .disabled
                    // set above, which the browser already blocks natively.
                    if ((ctrl.isStatic || ctrl.isDisabled) && !grouped && !usesCustomDropdown) {
                        input.disabled = true;
                    }
                    // A radiogroup carries its own classes and puts the cid on
                    // each radio, so it must not be overwritten with the
                    // single-control styling. valueDropdown's wrapper is the
                    // same story — its own trigger button is the labelable,
                    // disableable control, not the wrapper `input` here.
                    if (!grouped && !usesCustomDropdown) {
                        input.className = 'expivi-configurator__input';
                        input.dataset.expiviAttrCid = view.cid;
                        // Clicking the attribute name focuses its control, and
                        // the control gets an accessible name.
                        input.id = 'expivi-input-' + view.cid;
                        label.htmlFor = input.id;
                    } else {
                        // Neither a radiogroup nor valueDropdown's wrapper has
                        // a single control for `for` to point at, so the group
                        // borrows the same text by reference — without it a
                        // screen reader reads the options with no idea which
                        // attribute they belong to.
                        label.id = 'expivi-label-' + view.cid;
                        input.setAttribute('aria-labelledby', label.id);
                    }
                    if (desc) {
                        input.setAttribute('aria-describedby', desc.id);
                    }
                    if (toolbar) {
                        field.appendChild(toolbar);
                    }
                    // Only when the shopper is the one filtering. A company
                    // palette gate already owns this attribute's categoryIds
                    // (applyCompanyCategoryFilter replaces the whole array);
                    // chips would toggle entries in and out of that same list
                    // and quietly widen a B2B allow-list.
                    if (ds.enableCategoryFilter && filtered[view.cid] !== 'gated') {
                        var chips = categoryFilter(
                            view,
                            ctrl,
                            categories[view.cid] || (categories[view.cid] = {}),
                            rebuildForAsync
                        );
                        if (chips) {
                            field.insertBefore(chips, toolbar || input);
                        }
                    } else if (ds.enableCategoryFilter && !warned['catgate:' + view.cid]) {
                        // Reached only on 'gated' — an attempted-but-failed gate
                        // falls into the branch above and still gets its chips.
                        warned['catgate:' + view.cid] = true;
                        // eslint-disable-next-line no-console
                        console.warn('[expivi] "' + (view.name || view.identifier) + '" has '
                            + 'enableCategoryFilter set, but a company palette gate already owns '
                            + 'its category filter — the shopper-facing chips are suppressed so '
                            + 'the two allow-lists cannot fight.');
                    }
                    field.appendChild(input);
                    if (maxChars > 0) {
                        field.appendChild(textCounter(input, maxChars, ctrl));
                    }
                    // usesCustomDropdown already called valueLoadMore itself,
                    // with its own panel as the scroll box — nothing left to
                    // append out here.
                    //
                    // Colour gating already found a small, final-looking set:
                    // Load More would only invite clicking through pages 2-4
                    // of the RAW catalogue (up to 133) for colours this
                    // shopper's allow-list is very unlikely to admit. Mirrors
                    // valueDropdown's own completion test a few hundred lines
                    // up (opts.values.length >= allowedCount) rather than an
                    // arbitrary small-count threshold: suppress ONLY once
                    // every allow-listed colour (plus the two always-shown
                    // ones) has actually been found, so no later page could
                    // possibly add anything.
                    //
                    // A <15-style threshold instead conflated three different
                    // reasons the count can look small - genuinely few
                    // matches, gateMorePages' own auto-walk above having
                    // stopped at its first match with pages still unexplored,
                    // and a page fetch having failed - and hid the shopper's
                    // only retry control in that last case: pageState.failed
                    // never clears itself, and valueLoadMore (unlike a
                    // !gateMorePages-style guard) deliberately still renders
                    // through a failure so its sentinel/button can retry it.
                    var allowedCount = allowedColors
                        ? Object.keys(allowedColors).length + H.ALWAYS_SHOWN_COUNT
                        : null;
                    // Deduped by cid (uniqueByCid - shared with valueDropdown's
                    // own completion check below, see its own comment) rather
                    // than trusted from offPage/shownValues' invariants alone -
                    // cheap insurance because the failure direction is
                    // asymmetric: an inflated count only pushes suppression,
                    // the same stranding this threshold already caused once
                    // from a different cause (pageState.failed, above).
                    // Undercounting just leaves Load More visible a click
                    // longer than strictly needed, which is noise, not a bug.
                    var gatedMatchCount = allowedColors
                        ? uniqueByCid((offPage || []).concat(shownValues).filter(function (v) {
                            return isAllowedColor(v, allowedColors);
                        })).length
                        : null;
                    var suppressGatedLoadMore = allowedCount !== null
                        && gatedMatchCount >= allowedCount;
                    var more = (usesCustomDropdown || suppressGatedLoadMore)
                        ? null
                        // No scroll box: a radio list has no max-height, so
                        // the page is what scrolls and the viewport is the right
                        // root. Passing `input` here would be worse than
                        // useless — the sentinel is appended to `field`, NOT
                        // inside the group, so an element root it never sits in
                        // can never intersect and auto-loading would stop dead.
                        // (It used to pass `input` and get away with it only
                        // because the old measurement test quietly discarded it.)
                        //
                        // hideTotal (last arg): same reasoning as the dropdown
                        // path above — "Showing 5 of 133" counts the WHOLE
                        // catalogue, not what this shopper's colour allow-list
                        // lets them reach.
                        : valueLoadMore(view, ctrl, pageState, null, !grouped, !!allowedColors);
                    if (more) {
                        field.appendChild(more);
                    }
                    // Refocused after the append below — focus() on a node that
                    // is still detached does nothing.
                    if (focusCid && focusCid === view.cid) {
                        focusInput = (grouped || usesCustomDropdown) ? focusable : input;
                    }
                } else {
                    var staticValue = document.createElement('span');
                    staticValue.className = 'expivi-configurator__static';
                    var shown = (view.configuredValue || [])[0];
                    staticValue.textContent = shown ? valueLabel(shown) : '';
                    field.appendChild(staticValue);
                    // `xpv-static-string` shows the configured value's own
                    // description next to it; ours dropped it with everything
                    // else value-level.
                    if (shown && shown.description) {
                        // `xpv-static-string` hangs the same popover off the
                        // value it displays.
                        staticValue.appendChild(valuePopover(shown.description));
                    }
                }

                // Keyed by IDENTIFIER, not cid — see MessagesController.
                var attrMessages = messages && view.identifier
                    ? messages.getAttributeMessages(view.identifier)
                    : [];
                attrMessages.forEach(function (m) {
                    var note = document.createElement('p');
                    note.className = 'expivi-configurator__message expivi-configurator__message--' + (m.type || 'info');
                    note.textContent = m.message || '';
                    field.appendChild(note);
                    if (!warned['msg:' + view.cid + ':' + m.message]) {
                        warned['msg:' + view.cid + ':' + m.message] = true;
                        // eslint-disable-next-line no-console
                        console.warn('[expivi] ' + (m.type || 'message') + ' on "'
                            + (view.name || view.identifier) + '": ' + m.message);
                    }
                });

                fields[view.cid] = field;
                order.push(view.cid);

                if (view.rtti === 'ConfigurableList') {
                    applyCompanyCategoryFilter(ctrl, view.cid, opts.companyCode, filtered, !!allowedColors);
                }
            });

            // The merchant's own paragraphs and pictures, keyed into the same
            // `fields` map under their synthetic id. stepPlan already put them
            // in `attrCids`/`loose` at the position the merchant placed them,
            // so they land between the options they were written to explain
            // rather than in a block of their own.
            Object.keys((plan && plan.blocks) || {}).forEach(function (key) {
                var el = staticBlock(plan.blocks[key]);
                if (el) {
                    fields[key] = el;
                    order.push(key);
                }
            });

            if (plan) {
                // Above the stepper and always visible: a `flat` section's
                // attributes belong to no page, so hiding them behind step 1
                // would put them somewhere the shopper has to go looking.
                plan.loose.forEach(function (cid) {
                    if (fields[cid]) {
                        container.appendChild(fields[cid]);
                    }
                });
                renderSteps(container, plan.steps, fields, step, plan.sections, rushHiddenCids);
            } else {
                order.forEach(function (cid) {
                    container.appendChild(fields[cid]);
                });
            }

            // Before focus: focus() scrolls the focused node into view, so a
            // restore afterwards would fight it. Order matters more than it
            // looks here.
            if (scrolled && scrolled.scrollTop !== scrollTop) {
                scrolled.scrollTop = scrollTop;
            }
            if (window.pageYOffset !== pageY || window.pageXOffset !== pageX) {
                window.scrollTo(pageX, pageY);
            }

            if (focusInput) {
                // preventScroll where it exists: the control is already where
                // the shopper left it, and focus() would otherwise re-centre it.
                try {
                    focusInput.focus({ preventScroll: true });
                } catch (e) {
                    focusInput.focus();
                }
            }
            // Panels are rebuilt as new elements, so this has to happen after
            // they are attached — scrollTop on a detached node does not stick.
            Object.keys(pages).forEach(function (cid) {
                var pageState = pages[cid];
                if (!pageState.open || !pageState.scrollTop) {
                    return;
                }
                var panel = container.querySelector('#expivi-dropdown-list-' + cid);
                if (panel && !panel.hidden) {
                    panel.scrollTop = pageState.scrollTop;
                }
            });

            if (moreCid) {
                var moreButton = null;
                [].slice.call(container.querySelectorAll('.expivi-more__button'))
                    .forEach(function (el) {
                        if (!moreButton && el.dataset.expiviMoreCid === moreCid) {
                            moreButton = el;
                        }
                    });
                if (moreButton) {
                    try {
                        moreButton.focus({ preventScroll: true });
                    } catch (e) {
                        moreButton.focus();
                    }
                }
            }
            if (searchCid) {
                // Scanned by class and matched on the dataset rather than
                // interpolated into a selector: a cid is a store-generated uuid
                // today, but building a selector out of a value from the wire is
                // a habit that breaks the day one contains a quote or a colon,
                // and CSS.escape is not in this file's ES5 dialect.
                var box = null;
                [].slice.call(container.querySelectorAll('.expivi-options__search'))
                    .forEach(function (el) {
                        if (!box && el.dataset.expiviSearchCid === searchCid) {
                            box = el;
                        }
                    });
                if (box) {
                    box.focus();
                    // setSelectionRange throws on an <input type=search> in some
                    // browsers; the focus is the part that matters.
                    try {
                        box.setSelectionRange(searchCaret, searchCaret);
                    } catch (e) { /* caret is a nicety, focus is not */ }
                }
            }

            // The FIRST build that actually paints a field is when the panel
            // stops being a skeleton. Not "when renderOptions returns": the
            // synchronous first build below usually runs against an empty store
            // and renders nothing, with the real attributes arriving on a later
            // subscribe callback. Guarded so a rebuild on every option change
            // does not re-fire it.
            if (!painted && container.children.length) {
                painted = true;
                if (typeof opts.onReady === 'function') {
                    opts.onReady();
                }
            }
        };

        // The store notifies synchronously, and a draw can itself start a value
        // page request (auto-paging). That notification re-entered the build
        // mid-draw: the inner pass painted the steps, then the outer pass
        // appended its own copy after them, so the configuration showed twice
        // until the next rebuild hid one. A notification during a draw is
        // queued and replayed once the draw finishes instead.
        var building = false;
        var rebuildQueued = false;
        var build = function () {
            if (building) {
                rebuildQueued = true;
                return;
            }
            building = true;
            try {
                buildOnce();
                // Bounded: a draw that always asks for another would spin.
                for (var n = 0; rebuildQueued && n < 5; n++) {
                    rebuildQueued = false;
                    buildOnce();
                }
            } finally {
                building = false;
                rebuildQueued = false;
            }
        };

        // The store (and layouts/messages) notify repeatedly while the rules
        // engine settles a selection — measured live on two different
        // shapes: the initial page load (5 notifications over ~6s) and a
        // Size change that cascades other attributes (3 notifications over
        // ~3s). Each notification tears the whole panel down and rebuilds
        // it (nothing wrong with any individual one — buildOnce() already
        // skips a rebuild whose signature did not change — these are
        // genuinely different intermediate states), so a shopper watching
        // sees the panel repeatedly wiped and redrawn; any per-row entrance
        // styling replays every time, reading as the panel "sliding in"
        // over and over.
        //
        // A flat debounce (wait N ms after every notification, always)
        // was tried first and rejected: measured live, a SINGLE isolated
        // option change fires exactly one notification, so delaying it
        // bought nothing to coalesce and only made that - the common case -
        // feel slower. This is leading-edge + trailing instead: the FIRST
        // notification after a quiet period renders immediately (a lone
        // edit is exactly as fast as before), and only a FURTHER
        // notification arriving before the panel has gone quiet again is
        // folded into one trailing rebuild instead of redrawing again
        // right away. 600ms is a deliberate middle ground, not the value
        // that bridges every gap measured live (those ran up to ~2s, so
        // the widest ones still redraw more than once) — holding the panel
        // on a stale intermediate state for a full 1-2s after the
        // shopper's last input was rejected as too slow. 600ms catches the
        // tighter, more common bursts without that hold.
        var REBUILD_SETTLE_MS = 600;
        var rebuildTimer = null;
        var debouncedBuild = function () {
            if (!painted) {
                build(); // first paint: no debounce, nothing to coalesce yet
                return;
            }
            if (rebuildTimer === null) {
                build(); // leading edge: a lone edit redraws immediately
            }
            clearTimeout(rebuildTimer);
            rebuildTimer = setTimeout(function () {
                rebuildTimer = null;
                build(); // trailing edge: catches whatever the burst settled on
            }, REBUILD_SETTLE_MS);
        };

        build();
        attributes.subscribe(debouncedBuild);
        // A rule can hide a step, which changes nothing in the attribute store.
        if (layouts && typeof layouts.subscribe === 'function') {
            layouts.subscribe(debouncedBuild);
        }
        if (messages) {
            messages.subscribe(debouncedBuild);
            reportGlobalMessages(messages, container);
        }
        return attributes;
    }

    /**
     * Price element + wholesale breakdown, driven by the price-breakdown
     * subscription. Rows, when a wholesale % applies:
     *   Price     — retail hood (total minus the add-ons)
     *   Discount  — the discounted hood + the % (e.g. "$1,080.00 (10%)")
     *   <group>   — one row per add-on group with amount > 0, at full price
     *   Total     — what they pay
     * At percent 0 only the price element is written and the breakdown stays hidden.
     */
    function renderPrice(root, priceCtrl, percent, groups, freeRush) {
        var priceEl = root.querySelector('[data-expivi-price]');
        var breakdown = root.querySelector('[data-expivi-breakdown]');
        var fmt = moneyFormatter(root);

        var render = function (data) {
            if (!data) {
                if (priceEl) {
                    priceEl.textContent = '';
                }
                if (breakdown) {
                    breakdown.hidden = true;
                }
                return;
            }
            var charge = chargeableBreakdown(data, groups, freeRush());
            var a = breakdownAmounts(charge.total, charge.groups, percent);
            if (priceEl) {
                priceEl.textContent = fmt.format(a.total);
            }
            if (!breakdown || !(percent > 0)) {
                if (breakdown) {
                    breakdown.hidden = true;
                }
                return;
            }
            breakdown.textContent = '';
            breakdown.appendChild(breakdownRow('Price', fmt.format(a.hood)));
            breakdown.appendChild(breakdownRow('Discounted Price', fmt.format(a.discounted) + ' (' + percent + '%)'));
            Object.keys(a.groups).forEach(function (label) {
                breakdown.appendChild(breakdownRow(label, fmt.format(a.groups[label])));
            });
            breakdown.appendChild(breakdownRow('Total', fmt.format(a.total)));
            breakdown.hidden = false;
        };

        render(priceCtrl.data);
        priceCtrl.subscribe(render);
    }

    /**
     * Resolves once the attribute store has gone QUIET_MS without a
     * notification, or after QUIET_CAP_MS regardless.
     *
     * The SDK exposes no "settled" signal — usePriceBreakdown refreshes on
     * every rule evaluation whether or not anyone listens, and there is no
     * count of work in flight — so silence is the only thing left to measure.
     *
     * Used by the initial snapshot load only (loadSavedConfiguration below):
     * waiting for several attributes' rule cascade to finish before parsing
     * a multi-attribute snapshot. addToCart() used to share this (waiting for
     * the store to go quiet before reading the price), but measured live the
     * store goes quiet almost immediately on a selection while the price
     * round-trip itself can take several seconds — so that wait elapsed
     * before the price had actually moved, and reintroduced the exact
     * undercharge it was meant to fix. addToCart() now waits on
     * trackPriceFreshness() below instead, which tracks the price directly.
     */
    function storeQuiet(sdk, configurator) {
        var QUIET_MS = 900;
        var QUIET_CAP_MS = 12000;
        return new Promise(function (resolve) {
            var store = sdk.useStoreAttributes(configurator);
            var idle = null;
            var cap = null;
            var unsubscribe = null;
            var done = function () {
                clearTimeout(idle);
                clearTimeout(cap);
                if (typeof unsubscribe === 'function') {
                    unsubscribe();
                }
                resolve();
            };
            var poke = function () {
                clearTimeout(idle);
                idle = setTimeout(done, QUIET_MS);
            };
            cap = setTimeout(function () {
                // eslint-disable-next-line no-console
                console.warn('[expivi] the configurator never went quiet; proceeding anyway.');
                done();
            }, QUIET_CAP_MS);
            unsubscribe = store.subscribe(poke);
            poke();
        });
    }

    /**
     * Tracks whether the price breakdown has caught up to the LATEST
     * attribute state, so addToCart() can wait on the actual thing it
     * depends on instead of guessing at a delay.
     *
     * First attempt here was storeQuiet() (above) on the theory that the
     * price catches up shortly after the last selection. Measured live: the
     * attribute store updates immediately when a value is picked, but the
     * price round-trip itself can take several seconds — well past
     * storeQuiet's 900ms — so that wait elapsed before the price had
     * actually moved, and the exact same undercharge it was meant to fix
     * (captured total misses the shopper's last change) still happened.
     *
     * attrGen bumps on every attribute-store notification; priceGen records
     * which attrGen the LAST price update was for. wait() returns
     * immediately when they already match (price is current), otherwise
     * waits for the next price push — however long that takes, capped at
     * PRICE_CAP_MS — rather than a fixed guess. Resolves null on the cap, so
     * a stalled recompute blocks the charge (existing `!price` handling)
     * instead of silently using a possibly-stale one.
     *
     * onStaleChange (optional) fires every time the fresh/stale state
     * flips, so the button can be disabled with a visible reason for the
     * WHOLE window a click would otherwise silently wait through, not just
     * disabled reactively after the click lands. This is layered on top of
     * the same check `wait()` uses — it does not change when a charge is
     * allowed to proceed, only when the button looks clickable.
     *
     * Two things measured live ruled out comparing PRICE DATA (an earlier
     * version of this function did: "has priceCtrl emitted something
     * different from before the change") as the freshness signal:
     *
     *   - Tapered Without Trim, Select Wood Species: a single selection
     *     sometimes fires TWO priceCtrl notifications before the real
     *     prices/generate call is even sent — one for free (re-emitting the
     *     cached breakdown for an unrelated rule pass), then the real one.
     *     Comparing data treated the free one as "caught up" and cleared
     *     stale a full round-trip early.
     *   - Select Hood Color, "Raw (Unfinished)" -> "Custom Color Match":
     *     both price at $0, so the real, fully-recomputed response is
     *     BYTE-IDENTICAL to the one from before the change. Comparing data
     *     would read that as still-stale forever (capped at PRICE_CAP_MS),
     *     blocking a perfectly normal free option swap.
     *
     * The SDK's price controller (Nl in the vendored bundle) turns out to
     * expose exactly the right signal for this, despite storeQuiet's
     * comment above claiming otherwise: `isBusy` / `subscribeBusy()`,
     * true for exactly the window its own refresh() wraps the actual
     * `priceService.generate()` call in (setBusy(true) before, setBusy(false)
     * in a finally). That is authoritative regardless of whether the
     * resulting total moved, so it is immune to both failures above — no
     * data to compare, only "did a real request start and finish since the
     * last change."
     *
     * pendingRefresh is reset on every attribute-store notification, so a
     * busy->idle transition only counts if it started AFTER the shopper's
     * latest change: a refresh already in flight for an older attrGen
     * finishing after a newer edit lands must not be read as "caught up".
     * `refresh()` has no such guard itself — a change landing mid-flight
     * can leave two overlapping refreshes, and the vendor's own setBusy(false)
     * on the earlier one would otherwise look identical to the later one
     * finishing. Gating on pendingRefresh is what tells them apart.
     *
     * Falls back to "any price notification counts" (the pre-isBusy
     * behaviour, imperfect on both cases above) only if a future SDK bundle
     * drops subscribeBusy — never silently does nothing.
     */
    function trackPriceFreshness(attributes, priceCtrl, onStaleChange) {
        var PRICE_CAP_MS = 20000;
        var hasBusySignal = typeof priceCtrl.subscribeBusy === 'function';
        var attrGen = 0;
        var priceGen = -1;
        var pendingRefresh = false;
        var notify = function () {
            if (typeof onStaleChange === 'function') {
                onStaleChange(attrGen !== priceGen);
            }
        };
        // The store also notifies for things that never reprice: a colour page
        // lazy-loading, a rule settling. Counting those as edits left the price
        // "stale" forever, because no refresh follows them — Add to Cart then
        // sat on "Updating price…" until the shopper picked something. Only a
        // changed selection is an edit. No readable data -> every notify counts.
        var selectionKey = function () {
            var data = attributes.data;
            if (!Array.isArray(data)) {
                return null;
            }
            return data.map(function (v) {
                return v ? v.cid + '=' + ((v.configuredValue || []).map(function (c) {
                    return c && (c.cid + ':' + c.value);
                }).join(',')) : '';
            }).join('|');
        };
        var lastSelection = selectionKey();
        attributes.subscribe(function () {
            var key = selectionKey();
            if (key !== null && key === lastSelection) {
                return;
            }
            lastSelection = key;
            attrGen++;
            pendingRefresh = false;
            notify();
        });
        if (hasBusySignal) {
            priceCtrl.subscribeBusy(function (busy) {
                if (busy) {
                    pendingRefresh = true;
                    return;
                }
                if (!pendingRefresh) {
                    return; // a stale finish for a refresh started before our last edit
                }
                pendingRefresh = false;
                priceGen = attrGen;
                notify();
            });
        } else {
            priceCtrl.subscribe(function () { priceGen = attrGen; notify(); });
        }

        // Report the CONSTRUCTED state (stale: attrGen 0 has no matching
        // priceGen yet) immediately, not just on the next transition. Without
        // this, the button only ever heard "stale" reactively — and
        // renderOptions' onReady fires as soon as the option panel has
        // something to show, independently of whether the FIRST price load
        // has finished, so it could re-enable Add to Cart before the initial
        // refresh's busy cycle ever ran. Measured live: the button was
        // clickable on first paint, before the configurator had a price at
        // all. This call runs before onReady can (both happen inside the
        // same synchronous build(), and onReady's own enable is asynchronous),
        // so it wins that race rather than leaving it to timing.
        notify();

        return {
            wait: function () {
                if (priceGen === attrGen) {
                    return Promise.resolve(priceCtrl.data);
                }
                return new Promise(function (resolve) {
                    var done = false;
                    var finish = function () {
                        if (done) {
                            return;
                        }
                        done = true;
                        if (typeof unsubscribe === 'function') {
                            unsubscribe();
                        }
                        resolve(priceCtrl.data);
                    };
                    // Registered after the subscribeBusy() above, so priceGen has
                    // already been updated (or not) for a given transition by the
                    // time this runs — checking attrGen === priceGen is what keeps
                    // wait() from resolving on a stale-refresh finish notify() is
                    // taught to ignore.
                    var unsubscribe = hasBusySignal
                        ? priceCtrl.subscribeBusy(function (busy) {
                            if (!busy && attrGen === priceGen) {
                                finish();
                            }
                        })
                        : priceCtrl.subscribe(function () {
                            finish();
                        });
                    setTimeout(function () {
                        if (done) {
                            return;
                        }
                        done = true;
                        if (typeof unsubscribe === 'function') {
                            unsubscribe();
                        }
                        // eslint-disable-next-line no-console
                        console.warn('[expivi] price never caught up to the latest selection within '
                            + (PRICE_CAP_MS / 1000) + 's; blocking the charge rather than using a stale price.');
                        resolve(null);
                    }, PRICE_CAP_MS);
                });
            },
        };
    }

    function addToCart(root, button, roots, ctx) {
        button.disabled = true;
        button.classList.add('expivi-viewer__add--loading');

        // The click can land while the store is still catching up from the
        // shopper's LAST change: an attribute value updates immediately, but
        // the price recompute it triggers settles a beat later, and
        // ctx.configurator.serialize() below captures whatever is CURRENT at
        // that later moment. Reading the price breakdown before it has
        // settled can capture a total from before that last change while
        // serialize() captures the change itself — the two silently diverge.
        //
        // Measured live on order HW1184: the shopper's last pick before
        // clicking Add to Cart was the Ventilation Model. The captured price
        // total did not yet include it ($4720), but the saved
        // `_expivi_configuration` did, and Expivi's own price/generate priced
        // that exact configuration at $5420 — the buyer was undercharged the
        // full $700 Ventilation line, caught only later by VerifyOrderPricing.
        //
        // ctx.priceReady() (trackPriceFreshness above) waits for the price
        // controller itself to catch up to the latest selection — not a
        // fixed delay — so both price and serialize() below come from the
        // identical settled state regardless of how long that recompute takes.
        ctx.priceReady().then(function (price) {
            // Fail CLOSED, same rule as V1: a wholesale % applies and add-on groups
            // are configured, but we have no price breakdown to split. Charging anyway
            // would either discount the add-ons (undercharge) or mismatch the server
            // verifier and trip a false price-review hold.
            if (ctx.percent > 0 && ctx.groups.length && !price) {
                showError(roots, 'Pricing is temporarily unavailable — please refresh and try again.');
                return null;
            }
            if (!price) {
                showError(roots, 'Pricing is still loading — please try again in a moment.');
                return null;
            }
            // A settled breakdown with a $0 (or negative) total is not a free
            // product - every SKU this configurator sells is made-to-order and
            // priced well above zero. Seen live: Curved Metal Hood's own
            // /prices/generate returned total_amount: 0, items: [] for a valid
            // size selection - a catalogue pricing gap, not a legitimate price.
            // Same fail-closed reasoning as the two checks above: charging $0
            // ships an unpriced order instead of surfacing the real problem.
            if (!(price.total > 0)) {
                showError(roots, 'Pricing is unavailable for this configuration — please contact us before ordering.');
                return null;
            }

            // The line must carry rush before it is serialized, even if the shopper
            // clicked before build() could tick it. A product with no rush attribute
            // at all is a catalogue fact, not an error — but say so, because the
            // alternative is an entitled buyer's order quietly not being built rush.
            if (ctx.autoRush && !ensureRushSelected(ctx.sdk, ctx.configurator, ctx.attributes)) {
                // eslint-disable-next-line no-console
                console.warn('[expivi] auto-rush buyer, but this product has no rush option — '
                    + 'the line will not be marked as a rush job.');
            }

            return Promise.all([
                ctx.configurator.serialize(),
                // Shoot at the model's DEFAULT pose so the cart image does not depend
                // on how the shopper left it rotated, then upload and keep only the
                // url. Bounded and null-tolerant end to end: a cosmetic cart picture
                // must never delay or fail an add to cart, and a line with no
                // _preview_url just keeps the variant photo.
                canonicalScreenshot().then(uploadPreview, function () { return null; }),
            ])
                .then(function (results) {
                var configuration = results[0];
                var previewUrl = results[1];
                var properties = configurationProperties(ctx.attributes.data);
                attachPreview(properties, previewUrl);

                // ONE rush property, not two. configurationProperties keys by
                // ATTRIBUTE NAME and prints the value's own label, so a rushed V2
                // line arrives as "Rush Manufacturing Options": "Rush My Lead
                // Time" — which normalises to rush_manufacturing_options and
                // matches nothing: line-item.liquid, esd-cart-update.js and the
                // hub's PayloadBuilderService all key on "Rush Manufacturing" with
                // the value exactly "Rush My Order". Adding the canonical pair
                // alongside made both visible and the shopper saw rush twice on
                // the line, so the catalogue's own key is renamed instead.
                //
                // Only when RSH is actually selected. Picking "Standard Lead Time"
                // leaves the catalogue property alone: it is already the only rush
                // row on the line, and the hub's exact match on "rush my order"
                // correctly reads it as not rushed.
                var rushAttribute = rushSelectedAttribute(ctx.attributes.data);
                if (rushAttribute) {
                    var rushName = String(rushAttribute.name || '').trim();
                    if (rushName && rushName !== RUSH_PROPERTY_KEY) {
                        delete properties[rushName];
                    }
                    properties[RUSH_PROPERTY_KEY] = RUSH_PROPERTY;
                }

                var masterSku = masterSkuFromBom(ctx.bom.data);
                if (masterSku) {
                    properties['Master SKU'] = masterSku;
                } else {
                    // Warn on a null BOM too, not just an errored one. A BOM that
                    // never resolves reports no error, so this was silent — the
                    // line simply shipped with no Master SKU and nobody knew.
                    // eslint-disable-next-line no-console
                    console.warn('[expivi] BOM unavailable, no Master SKU on this line: '
                        + (ctx.bom.error || 'no data'));
                }

                // The SKU segments, so a line is still identifiable when the BOM
                // is not. Sent alongside the configuration, never inside it: that
                // JSON is POSTed back to Expivi verbatim by the price recompute.
                var skuCodes = configuredSkuCodes(ctx.attributes.data);
                if (skuCodes.length) {
                    properties._expivi_sku_keys = JSON.stringify(skuCodes);
                }

                var charge = chargeableBreakdown(
                    price,
                    ctx.groups,
                    ctx.autoRush ? rushFee(ctx.attributes.data, price) : 0,
                );
                var groupPrices = charge.groups;
                var excludedTotal = excludedSum(groupPrices);
                var unit = unitPriceProperties(charge.total, excludedTotal, ctx.percent);
                properties._unit_price = unit.price;
                if (unit.discount) {
                    properties._unit_discount = unit.discount;
                }
                if (excludedTotal > 0) {
                    // Hidden hint for the hub/HubSpot payload; the verifier
                    // recomputes the groups and never trusts this.
                    properties._addon_prices = JSON.stringify(groupPrices);
                }

                // The serialized configuration is `{cid, version, products,
                // attributes, extensions}` — the same shape as the `context` the
                // V2 price/BOM/rules endpoints take, so the server can rebuild the
                // recompute payload from this alone. V1's catalogue_id lived inside
                // the config; V2's product-offer id does not, so it is sent
                // alongside for the verifier.
                properties._expivi_configuration = JSON.stringify(configuration);
                properties._expivi_product_id = String(ctx.productId);

                return fetch(cartRoot() + 'cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({
                        items: [{ id: ctx.variantId, quantity: 1, properties: properties }],
                    }),
                });
            })
            .then(function (response) {
                if (!response.ok) {
                    return response.json().then(function (data) {
                        throw new Error(data.description || data.message || 'Could not add to cart.');
                    });
                }
                if (ctx.redirect) {
                    window.location.href = cartRoot() + 'cart';
                    return;
                }
                document.dispatchEvent(new CustomEvent('expivi:added-to-cart'));
            });
        })
            .catch(function (error) {
                showError(roots, error.message || 'Could not add the product to the cart.');
            })
            .finally(function () {
                button.disabled = false;
                button.classList.remove('expivi-viewer__add--loading');
            });
    }

    /**
     * Boot one configurator.
     *
     * Called with no argument (the auto-boot below) it behaves exactly as it
     * always has: the FIRST [data-expivi-configurator] in the document, which is
     * the one a product page renders.
     *
     * Called with an element it boots that root instead, which is how a section
     * holding several configurators (hs-av-collection: one per hood tab) starts
     * them one at a time, as each tab is first opened. Such roots carry
     * [data-expivi-manual] so the auto-boot never claims one of them; every root
     * is booted at most once, since nothing here tears a configurator down.
     */
    function boot(sourceRoot) {
        var viewerRoot;
        var configuratorRoot;

        if (sourceRoot && sourceRoot.nodeType === 1) {
            configuratorRoot = sourceRoot.hasAttribute('data-expivi-configurator')
                ? sourceRoot
                : sourceRoot.querySelector('[data-expivi-configurator]');
            viewerRoot = sourceRoot.hasAttribute('data-expivi-viewer')
                ? sourceRoot
                : sourceRoot.querySelector('[data-expivi-viewer]');
        } else {
            viewerRoot = document.querySelector('[data-expivi-viewer]:not([data-expivi-manual])');
            configuratorRoot = document.querySelector('[data-expivi-configurator]:not([data-expivi-manual])');
        }

        var roots = [viewerRoot, configuratorRoot].filter(Boolean);
        if (!roots.length) {
            return;
        }

        var source = configuratorRoot || viewerRoot;

        // One boot per root. A second call would mount a second SDK
        // configurator and a second 3D scene onto the same nodes.
        if (source.__expiviBooted) {
            return;
        }
        source.__expiviBooted = true;
        var d = source.dataset;

        // Held before any of the guards below, so a misconfigured store cannot
        // leave an enabled button in front of a panel that will never load.
        // Every exit from here on either paints the options or calls showError,
        // and both release it.
        setAddToCartLoading(source, true);

        if (!d.token || !d.apiUrl) {
            showError(roots, 'The Expivi app is not configured for this store yet.');
            return;
        }
        if (!d.productId) {
            showError(roots, 'This product has no Expivi product offer id.');
            return;
        }
        // V2 is served from /api/v2; /api is the LEGACY router, where every
        // /view/* path 404s with RouteNotFoundException. loadProduct() swallows
        // that failure and still reaches READY, so a wrong base looks like an
        // empty configurator rather than a bad URL. Say so loudly instead.
        if (!/\/api\/v2\/?$/i.test(d.apiUrl)) {
            // eslint-disable-next-line no-console
            console.warn('[expivi] data-api-url should end in /api/v2 (got "' + d.apiUrl +
                '"). The V2 /view/* routes do not exist under /api.');
        }

        var companyCode = String(d.companyCode || '').toLowerCase();
        var companyPresent = d.companyPresent === 'true';
        var autoRush = d.autoRush === 'true';
        var variantId = parseInt(d.variantId, 10);

        // Discount-excluded add-on GROUPS the merchant configured (shop metafield
        // expivi.discount_excluded_groups): an array of {label, attributes}.
        // Malformed JSON or a non-array falls back to [] (retail behaviour).
        var groups = [];
        try {
            groups = JSON.parse(d.excludedGroups || '[]');
        } catch (e) {
            groups = [];
        }
        if (!Array.isArray(groups)) {
            groups = [];
        }

        // Resolved colour allow-list from the snippet: the default catalog plus
        // whatever this company's own catalogs add. [] means no catalogs are
        // configured, and allowedColorIdentifiers() reads that as "filter nothing".
        var colors = [];
        try {
            colors = JSON.parse(d.colors || '[]');
        } catch (e) {
            colors = [];
        }
        if (!Array.isArray(colors)) {
            colors = [];
        }

        // line -> [configurator attribute label]. Merchant-managed; no mapping
        // means no gating, which is the safe direction.
        var colorAttributes = null;
        try {
            colorAttributes = JSON.parse(d.colorAttributes || 'null');
        } catch (e) {
            colorAttributes = null;
        }

        Promise.all([
            // d.sdkUrl comes straight from asset_url, so it carries the ?v=
            // fingerprint. It was previously assembled from a data-asset-base
            // that split the filename off asset_url and dropped the query
            // string with it — the CDN then cached the SDK under a URL that
            // never changes, and an upgraded bundle stayed invisible in
            // production however many times the theme was redeployed.
            ensureSdk(d.sdkUrl),
            fetchWholesaleDiscount(companyPresent, d.variantId),
        ])
            .then(function (results) {
                var sdk = window.ProductConfiguratorLib;
                if (!sdk || !sdk.ProductConfigurator) {
                    throw new Error('Expivi SDK bundle did not load.');
                }
                // Category discount cap applied once, before the % feeds the
                // breakdown render and the add-to-cart pricing.
                var percent = cappedWholesalePercent(results[1], d.categoryDiscountCap);

                // Shopify gives a bare language code; Expivi needs language-REGION.
                var configurator = new sdk.ProductConfigurator({
                    apiBaseUrl: d.apiUrl,
                    apiToken: d.token,
                    locale: EXPIVI_CONTEXT.locale,
                    country: EXPIVI_CONTEXT.country,
                    currency: EXPIVI_CONTEXT.currency,
                    // NOT snapshotId. Passing it here makes loadProduct parse the
                    // snapshot itself, and for a single-product offer it does so
                    // against a half-loaded value store — see loadSavedConfiguration
                    // below. We load it afterwards instead.
                });
                window.expiviConfigurator = configurator;

                // useProductConfigurator is the ONLY hook that may be created
                // before the product loads: GlobalController does not extend
                // HookController, so it has no status guard. Every other hook
                // throws "Product configurator is not initialized." unless the
                // status is LOADED or READY — hence they are all created inside
                // the READY branch below, not here.
                var lifecycle = sdk.useProductConfigurator(configurator);

                var started = false;
                var failed = false;
                var ready = false;
                var settled = false;     // the load chain settled, or gave up
                // Late-snapshot guard (see the snapshotTimeout block below).
                var restore = { gaveUp: false, touched: false, done: false };
                // A trusted event is a shopper, not our own setValue.
                source.addEventListener('change', function (e) {
                    if (e.isTrusted) {
                        restore.touched = true;
                    }
                }, true);
                var releaseRestore = function () {
                    restore.done = true;
                    setAddToCartRestoring(source, false);
                };
                lifecycle.subscribeStatus(function (status) {
                    if (status === sdk.ProductConfiguratorStatus.ERROR) {
                        // ERROR is terminal as of SDK 1.5.0: loadProduct() returns
                        // here, so READY never arrives and start() never runs. The
                        // latch is still what puts a message on the page — the SDK
                        // swallows the real reason in a bare catch and only files a
                        // generic "Failed to load the Product Configurator." into
                        // useMessages, so nothing else says anything at all.
                        failed = true;
                        // Deliberately does NOT say "see the console": the SDK logs
                        // nothing. Reproduce with productOffer.load()/loadLayout()
                        // by hand to get the real stack.
                        showError(roots, 'Could not load the configurator for product ' + d.productId + '.');
                        return;
                    }
                    if (status === sdk.ProductConfiguratorStatus.READY) {
                        ready = true;
                        // Whichever of READY and the load chain lands SECOND is the
                        // one that builds. Without this retry, a chain that settled
                        // before READY arrived cleared the timeout and left start()
                        // no-opping on !ready with nothing to ever call it again:
                        // a permanently dead panel, and no message either, since
                        // `failed` was never latched.
                        //
                        // Guarded because `start` is declared below this
                        // subscriber: were subscribeStatus to replay the current
                        // status synchronously, this would hit the hoisted
                        // undefined.
                        if (start) {
                            start();
                        }
                    }
                });

                /**
                 * Build the panel, the viewer and the price. Called once.
                 *
                 * Reached FROM the READY status event only as the retry described
                 * there — the ordinary path is the load chain below, and the
                 * `settled` gate is what keeps a bare READY from building early:
                 *
                 * The SDK emits READY and only THEN fetches and parses the
                 * snapshot:
                 *
                 *   setStatus(LOADED)
                 *   await allSettled(systems.init()) -> setStatus(READY)
                 *   snapshotId && await loadSnapshot(snapshotId)
                 *
                 * Building on READY therefore put every write we make — the
                 * auto-rush tick, the colour correction, and setCategoryFilter,
                 * which reloads value pages asynchronously — in a race with the
                 * snapshot. And parse() is destructive: it CLEARS every attribute
                 * the snapshot does not mention, and matches the ones it does
                 * against only the values loaded at that instant. Whichever
                 * settled last won, so a snapshot applied on some loads and was
                 * replaced by defaults on others.
                 *
                 * loadProduct()'s promise resolves after loadSnapshot has parsed,
                 * so waiting on it makes the order deterministic.
                 */
                var start = function () {
                    if (started || failed || !ready || !settled) {
                        return;
                    }
                    started = true;

                    // An empty store makes the SDK's own serialize() throw
                    // "Configuration validation failed!" from inside the price and
                    // BOM controllers — an unhandled rejection with no hint of the
                    // real cause — so stop here instead, with a message that says
                    // which half is missing.
                    var offers = sdk.useStoreProductOffers(configurator);
                    if (!offers.data.length) {
                        showError(roots, 'Product offer ' + d.productId + ' returned nothing. Check the product id and the API base URL.');
                        return;
                    }

                    var viewerContainer = source.querySelector('[data-expivi-viewer-container]');
                    if (viewerContainer) {
                        markViewerKind(viewerContainer, offers.mainProduct);
                        try {
                            mount2dViewer(sdk, configurator, viewerContainer, offers.mainProduct);
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error('[expivi] 2D viewer failed to start:', error);
                        }
                        mount3dViewer(configurator, viewerContainer, d.token, offers.mainProduct, d.apiUrl)
                            .catch(function (error) {
                                // Non-fatal on purpose: options, pricing and
                                // add-to-cart do not need the scene.
                                // eslint-disable-next-line no-console
                                console.error('[expivi] 3D viewer failed to start:', error);
                            });
                    }

                    var priceCtrl = sdk.usePriceBreakdown(configurator);
                    // Not subscribed on purpose: the controller refreshes itself on
                    // every settled rule evaluation whether or not anyone is
                    // listening, so reading .data at add-to-cart is enough.
                    var bomCtrl = sdk.useBomBreakdown(configurator);

                    var optionContainer = source.querySelector('[data-expivi-option-container]');
                    var attributes = renderOptions(optionContainer, sdk, configurator, {
                        autoRush: autoRush,
                        companyCode: companyCode,
                        colors: colors,
                        colorAttributes: colorAttributes,
                        money: moneyFormatter(source),
                        onReady: function () {
                            clearOptionsSkeleton(source);
                            setAddToCartLoading(source, false);
                        },
                    });
                    // The configured SKU, published for the SKU-matched xBoost
                    // estimate (esd-xboost-toggle.js, rules from the app's
                    // hub.esd_sku_override metafield). BOM parts come back null for
                    // this catalogue, so the SKU is the same per-attribute
                    // identifiers cart/add.js sends as _expivi_sku_keys, not
                    // useBomBreakdown(). Published two ways: an attribute on the
                    // configurator root (read by anything booting later) and an
                    // `expivi:sku` event (so a listener reacts at once).
                    //
                    // The on-page debug line (and its console copy) stays hidden
                    // unless the URL carries ?sku_debug=1 — that is how a merchant
                    // finds the SKU to paste into the app. Console output goes
                    // through pristineConsole(): this storefront silences
                    // window.console.
                    var debugSku = source.querySelector('[data-expivi-debug-sku]');
                    // TEMP: true shows the SKU line + console output to everyone
                    // while debugging. Set back to false before launch;
                    // ?sku_debug=1 still works then.
                    var SKU_DEBUG_ALWAYS = true;
                    var showDebugSku = SKU_DEBUG_ALWAYS ||
                        /[?&]sku_debug=1(?:&|$)/.test(window.location.search);
                    if (debugSku && showDebugSku) {
                        debugSku.hidden = false;
                    }
                    // Main SKU list (the app's main_skus) is static for the page,
                    // so parse it once. Used for the published full SKU below,
                    // not only for the debug line.
                    var debugMainSkus = [];
                    try {
                        debugMainSkus = JSON.parse(source.getAttribute('data-main-skus') || '[]');
                    } catch (e) {
                        // Bad/missing list: the line just shows no main SKU.
                    }
                    var lastLoggedSku = null;
                    attributes.subscribe(function () {
                        var codes = configuredSkuCodes(attributes.data);
                        var joined = codes.map(function (c) { return c.sku; }).join('-');
                        // Main SKU in front, as the Hub's sku_gen has it (CCX-3048-...).
                        // Published too, not just shown: only this side knows the
                        // app's main_skus list (data-main-skus), and the toggle
                        // needs it to match an admin entry written with the main
                        // SKU — guessing from the variant SKU turns CCXRAW30 into
                        // CCXRAW, never CCX.
                        var fullSku = typeof buildDebugSku === 'function'
                            ? buildDebugSku(source.getAttribute('data-product-sku'), debugMainSkus, codes)
                            : joined;
                        if (source.getAttribute('data-expivi-sku') !== joined
                            || source.getAttribute('data-expivi-sku-full') !== fullSku) {
                            source.setAttribute('data-expivi-sku', joined);
                            source.setAttribute('data-expivi-sku-full', fullSku || '');
                            try {
                                document.dispatchEvent(new CustomEvent('expivi:sku', {
                                    detail: { sku: joined, fullSku: fullSku, segments: codes },
                                }));
                            } catch (e) {
                                // No CustomEvent constructor: the toggle's poll still sees the attribute.
                            }
                        }
                        if (showDebugSku) {
                            if (debugSku) {
                                // debugSku.textContent = 'SKU (debug): ' + fullSku;
                            }
                            // Once per change: the store notifies ~30 times per
                            // selection, and the same line 30 times buries the rest.
                            if (fullSku !== lastLoggedSku) {
                                lastLoggedSku = fullSku;
                                var segments = codes.map(function (c) { return c.sku; });
                                debugState({
                                    generated: fullSku,
                                    generatedWithoutMainSku: joined,
                                    segments: segments,
                                });
                                // pristineConsole(): window.console is silenced on
                                // this storefront, so a plain console.warn shows nothing.
                                var dbg = pristineConsole();
                                try {
                                    if (dbg && dbg.warn) {
                                        // dbg.warn('[Expivi SKU] generated:', fullSku || '(empty)',
                                        //     '| without main SKU:', joined || '(empty)',
                                        //     '| segments:', segments);
                                    }
                                } catch (e) { /* debugging must not break the configurator */ }
                            }
                        }
                    });

                    if (!attributes.data.length) {
                        // Not fatal for the viewer, but serialize() rejects on a
                        // zero-attribute configuration, so pricing and add-to-cart
                        // will not work — say so rather than showing a blank panel.
                        showError(roots, 'This product offer has no attributes, so it cannot be priced or configured.');
                    }

                    // Disables Add to Cart for exactly the window a click would
                    // otherwise wait through silently (ctx.priceReady() below) —
                    // a fast clicker sees the button go unavailable the moment
                    // they change an option, instead of clicking a live-looking
                    // button that then hangs for however long the recompute takes.
                    //
                    // The FIRST "ready" is debounced (INITIAL_READY_GRACE_MS):
                    // measured live on three different products, the initial
                    // load settles the price TWICE — a real, final-looking total
                    // around 16-18s, then busy again un-prompted for another
                    // 1-4s before a second real settle (same final price both
                    // times, confirmed via genuinely separate rules/evaluate +
                    // prices/generate round-trips, not a stale-data artifact —
                    // see trackPriceFreshness's own comment on a similar
                    // double-notification it already guards against for
                    // post-click busy cycles). Showing "ready" at the first one
                    // only for the button to go back to "Updating price…"
                    // un-prompted a few seconds later reads as broken.
                    //
                    // Only the FIRST ready waits out the grace window - there is
                    // no way to know in advance whether a second pass is coming,
                    // so that one has to be confirmed by waiting. Once a busy
                    // signal actually interrupts it, the pattern this session
                    // measured (always exactly two passes, every product
                    // checked) is confirmed for THIS load, so the next ready is
                    // trusted immediately with no further wait - rather than
                    // defensively re-arming the timer and adding a second,
                    // unconfirmed delay on top of the SDK's own timing. A
                    // genuine third pass (not observed in testing) would show as
                    // a brief flicker rather than being absorbed too; accepted
                    // trade for not tacking extra wait onto the common case.
                    //
                    // This only smooths the DISPLAY - ctx.priceReady()/wait()
                    // below is untouched and still gates a charge on the real
                    // final settle either way, so this cannot let a charge
                    // through early. Once the first ready is shown (whether
                    // immediately after an interruption or after the grace
                    // window elapses uninterrupted), every later busy/idle flip
                    // — from the shopper's own edits — shows instantly, same as
                    // before this change.
                    var INITIAL_READY_GRACE_MS = 2500;
                    var everSettledOnce = false;
                    var hadInterruption = false;
                    var pendingReadyTimer = null;
                    var priceFreshness = trackPriceFreshness(attributes, priceCtrl, function (stale) {
                        if (everSettledOnce) {
                            setAddToCartBusy(source, stale);
                            return;
                        }
                        if (stale) {
                            if (pendingReadyTimer) {
                                clearTimeout(pendingReadyTimer);
                                pendingReadyTimer = null;
                                hadInterruption = true;
                            }
                            setAddToCartBusy(source, true);
                            return;
                        }
                        if (hadInterruption) {
                            everSettledOnce = true;
                            setAddToCartBusy(source, false);
                            return;
                        }
                        pendingReadyTimer = setTimeout(function () {
                            pendingReadyTimer = null;
                            everSettledOnce = true;
                            setAddToCartBusy(source, false);
                        }, INITIAL_READY_GRACE_MS);
                    });

                    // A getter, not a number: rush may not be ticked yet when the
                    // panel first paints, and the value prices land asynchronously.
                    renderPrice(source, priceCtrl, percent, groups, function () {
                        return autoRush ? rushFee(attributes.data, priceCtrl.data) : 0;
                    });

                    var button = source.querySelector('[data-expivi-add-to-cart]');
                    // parseInt of a missing or malformed data-variant-id is NaN,
                    // and JSON.stringify turns {id: NaN} into {"id": null} — so
                    // Shopify 422s and the shopper is told only "Could not add to
                    // cart". Refuse to wire a button that cannot work, and say
                    // which half is missing.
                    if (button && !variantId) {
                        showError(roots, 'This product has no variant to add to the cart.');
                    } else if (button) {
                        button.addEventListener('click', function () {
                            addToCart(source, button, roots, {
                                sdk: sdk,
                                configurator: configurator,
                                attributes: attributes,
                                priceReady: priceFreshness.wait,
                                bom: bomCtrl,
                                percent: percent,
                                groups: groups,
                                autoRush: autoRush,
                                variantId: variantId,
                                productId: d.productId,
                                redirect: d.redirectToCart === 'true',
                            });
                        });
                    }
                };

                // Whichever comes first. loadProduct settling is the correct
                // signal; the timeout only exists so a request that never
                // settles cannot leave a permanent skeleton — it degrades to the
                // old racy behaviour rather than to a dead panel, and the
                // self-healing rush tick plus ensureRushSelected cover the line
                // either way.
                //
                // storeQuiet() itself now lives at module scope, above
                // addToCart() — it needs no per-instance state, and addToCart()
                // needs the same settle detector at click time (see there).

                /**
                 * Apply the saved configuration ourselves, once the store is loaded.
                 *
                 * The SDK's own snapshot path waits for attribute values — but only
                 * on the multi-product branch:
                 *
                 *   const l = e.products.length;
                 *   if (l <= 1) return Promise.resolve(true);   // <- single product
                 *   await this.waitForSubProducts(...);
                 *   await this.waitForAttributeValues(t, d, wa);
                 *   u.parse(e.attributes);
                 *
                 * Our offers are single-product, so that guard returns early and the
                 * snapshot parses against whatever value pages happen to have
                 * arrived. parse() then drops silently what it cannot find
                 * (`.filter(i => i !== null)`), and one lost value cascades: losing
                 * "Select Ventilation Brand" leaves it at None, which disallows the
                 * Model attribute and reprices the hood. Measured on the same URL,
                 * one load restored the snapshot's Natural Stain / Gray /
                 * Vent-A-Hood and the next fell back to Raw / Raw / None.
                 *
                 * loadProduct resolves after systems.init(), which is what loads the
                 * value pages, so parsing here means parse() can find everything the
                 * snapshot names. A failure goes to useMessages, which is now read.
                 */
                var loadSavedConfiguration = function () {
                    if (!d.snapshotId || typeof configurator.loadSnapshot !== 'function') {
                        return Promise.resolve();
                    }
                    // ...and not until the store stops moving. loadProduct resolving
                    // is not the end of the work: READY kicks off a cascade —
                    // rules/evaluate, then an attribute-values reload, then
                    // prices/generate — and a parse dropped into the middle of it
                    // loses values the same way the SDK's own early parse does.
                    // Measured: a parse once everything had settled restored Natural
                    // Stain / Gray / Vent-A-Hood and held them through the cascade
                    // it triggered; the same parse issued right after loadProduct
                    // resolved left every one of them at its default.
                    //
                    // "Stopped moving" is the honest test available to us: the
                    // attribute store going QUIET_MS without a notification. The cap
                    // matters more than the debounce — a catalogue whose rules never
                    // settle must still get a panel.
                    return storeQuiet(sdk, configurator).then(function () {
                        // Gave up and the shopper already chose: parse() is
                        // destructive, so leave their choices alone.
                        if (!shouldApplySnapshot(restore)) {
                            return null;
                        }
                        // loadSnapshot() is fetch + parse in one call, and the fetch
                        // is the slow part: a shopper can give up waiting and start
                        // choosing while it is in flight. So fetch, re-check, THEN
                        // parse. Measured: checking only before the fetch let a late
                        // snapshot overwrite a colour picked in the meantime. Falls
                        // back to loadSnapshot if the SDK internals are not there.
                        var factory = configurator.apiServiceFactory;
                        if (!factory || typeof factory.downloadDesignService !== 'function'
                            || typeof configurator.parse !== 'function') {
                            return configurator.loadSnapshot(d.snapshotId);
                        }
                        return factory.downloadDesignService().getSnapshot(d.snapshotId)
                            .then(function (snapshot) {
                                if (!snapshot) {
                                    throw new Error('Snapshot "' + d.snapshotId + '" returned an empty configuration.');
                                }
                                if (!shouldApplySnapshot(restore)) {
                                    return null;
                                }
                                return configurator.parse(snapshot);
                            });
                    }).then(function () {
                        // parse() copies identifier/label from the frozen snapshot, so
                        // a value Expivi has since renamed or re-coded reads stale to
                        // SKU codes, rush detection and serialize(). Re-select the
                        // live value (same id) once; no-op when nothing differs.
                        try {
                            sdk.useStoreAttributes(configurator).data.forEach(function (view) {
                                if (view.rtti !== 'ConfigurableList' || !(view.configuredValue || []).length) {
                                    return;
                                }
                                var ctrl = sdk.useAttribute(configurator, view.cid);
                                if (!ctrl || ctrl.isMultiple) {
                                    return;
                                }
                                var cid = staleSnapshotValueCid(view.configuredValue, ctrl.allValues);
                                if (cid) {
                                    ctrl.setValueBySystem(cid);
                                }
                            });
                        } catch (e) {
                            // eslint-disable-next-line no-console
                            console.warn('[expivi] could not refresh snapshot values:', e);
                        }
                    });
                };

                var snapshotTimeout = setTimeout(function () {
                    // Giving up counts as settled: the escape hatch exists for a
                    // chain that never resolves, so it has to clear the new gate
                    // too. If READY has not landed even by now there is no
                    // snapshot parse in flight to race — loadSavedConfiguration
                    // runs only after loadProduct resolves, which is after READY.
                    settled = true;
                    restore.gaveUp = true;
                    if (!started && ready) {
                        // eslint-disable-next-line no-console
                        console.warn('[expivi] the configurator did not settle in time; building '
                            + 'without waiting for the saved configuration.');
                        // Defaults are about to be shown for a product that has a
                        // saved configuration: don't let them be bought meanwhile.
                        if (d.snapshotId && !restore.done) {
                            setAddToCartRestoring(source, true);
                            // Hard cap so a dead snapshot cannot block purchase.
                            setTimeout(releaseRestore, 30000);
                        }
                        start();
                    }
                }, 30000);

                return configurator.loadProduct(d.productId)
                    .then(loadSavedConfiguration)
                    .then(function () {
                        clearTimeout(snapshotTimeout);
                        settled = true;
                        releaseRestore();
                        start();
                    }, function (error) {
                        clearTimeout(snapshotTimeout);
                        settled = true;
                        releaseRestore();
                        // loadProduct and loadSnapshot both catch their own failures.
                        // The one rejection the SDK does let through is the account
                        // defaults read (1.5.0+), and that only fires when locale,
                        // country or currency is missing — EXPIVI_CONTEXT hardcodes
                        // all three, so it never runs. Anything reaching here is
                        // unexpected: build anyway rather than show nothing, and
                        // start() re-checks `failed` and `ready` before it does,
                        // and READY retries the build if it lands after this.
                        // eslint-disable-next-line no-console
                        console.error('[expivi] configurator load rejected:', error);
                        start();
                    });
            })
            .catch(function (error) {
                showError(roots, error.message || 'Could not load the configurator.');
            });
    }

    // Export the pure helpers for the node assert check
    // (expivi-viewer.price.test.js); must precede the browser-only boot below so
    // requiring this file never touches window.
    if (typeof module !== 'undefined' && module.exports) {
        // Re-exported so a test can require either file. The definitions
        // live in expivi-hoodsly.js.
        module.exports = Object.assign({}, H, {
            groupPricesFromBreakdown: groupPricesFromBreakdown,
            masterSkuFromBom: masterSkuFromBom,
            configurationProperties: configurationProperties,
            valueLabel: valueLabel,
            optionLabel: optionLabel,
            panelSignature: panelSignature,
            stepPlan: stepPlan,
            pageQuery: pageQuery,
            optionPrice: optionPrice,
            offPageSelections: offPageSelections,
            staleSelectionReset: staleSelectionReset,
            accumulateValues: accumulateValues,
            staticTagAction: staticTagAction,
            keepStaticAttr: keepStaticAttr,
            numberConfig: numberConfig,
            clampToConfig: clampToConfig,
            boundsMessage: boundsMessage,
            warnUnhandledDisplaySettings: warnUnhandledDisplaySettings,
            autoRushValue: autoRushValue,
            rushValue: rushValue,
            rushSelected: rushSelected,
            rushSelectedAttribute: rushSelectedAttribute,
            ensureRushSelected: ensureRushSelected,
            chargeableBreakdown: chargeableBreakdown,
            rushFee: rushFee,
            trackPriceFreshness: trackPriceFreshness,
        });
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    // Public entry point for sections that boot their own roots lazily.
    // Published before the auto-boot guard so it exists even on a page where
    // this file has already run once.
    window.ExpiviViewer = window.ExpiviViewer || {};
    window.ExpiviViewer.boot = boot;

    if (window.__expiviStorefrontBooted) {
        return;
    }
    window.__expiviStorefrontBooted = true;

    if (document.readyState === 'loading') {
        // Wrapped: a listener is called with the Event, and boot() now takes a root.
        document.addEventListener('DOMContentLoaded', function () { boot(); });
    } else {
        boot();
    }
})();
